import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Table,
    Spin,
    Alert,
    Button,
    notification,
    Checkbox,
    Tooltip,
    Space,
    Tag,
    Radio,
    Input,
    Select,
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    Article,
    ArrowsClockwise,
    ArrowsLeftRight,
    ArrowSquareOut,
    CaretDown,
    CaretRight,
    Check,
    Circle,
    CheckCircle,
    CurrencyCircleDollar,
    EnvelopeSimple,
    Eye,
    Hourglass,
    Handshake,
    PencilSimple,
    Prohibit,
    FileText,
    Package,
    RocketLaunch,
    Scroll,
    UserCircle,
    Warning,
    WarningCircle,
    X,
} from '@phosphor-icons/react';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import contractService from '../../../../services/oneButtonServices/contractService';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import {
    contractApiErrorMessage,
    firstSigningLikeUrl,
    formatGoodLeapResyncErrorStorage,
    formatGoodLeapSyncErrorMessage,
    goodLeapSyncErrorNotice,
    loanSyncHasAnyErrors,
    mapQuoteProductTypeLongNameToCode,
    roofingProposalUrlFromApi,
    type LoanSyncResyncErrors,
} from './contractFlowHelpers';
import { LoanSyncErrorsDialogContent } from './LoanSyncErrorsDialogContent';
import {
    agreementGridScrollMinX,
    agreementToolVisibility,
    detectAgreementPartnerKind,
    type AgreementPartnerKind,
    type AgreementToolVisibility,
} from './agreementPartnerTooling';
import { useOneButtonCapability } from '../../../../hooks/useOneButtonCapability';
import { oneButtonCapabilityRawAllows } from '../../../../services/oneButtonServices/capabilityService';
import { ContractSignedProductsModal } from './ContractSignedProductsModal';
import { GoodLeapShareLoanModal } from './GoodLeapShareLoanModal';
import GoodLeapTimelineModalContent from './GoodLeapTimelineModalContent';
import { ViewTrinityContractModal } from './ViewTrinityContractModal';
import { ServiceFinanceSubmitModal } from './ServiceFinanceSubmitModal';
import { SunlightIframeModal } from './SunlightIframeModal';
import { mergeApiShape, pickGoodLeapApplicationUrl } from './goodLeapPickUrl';
import { OBDDropdown } from './form-elements';
import OBDQuoteDetailsModal from './OBDQuoteDetailsModal';
import CreditCheckModal from './CreditCheckModal';
import SignFinancePartnerContractModal from './SignFinancePartnerContractModal';
import DashboardModal from './DashboardModal';
import HoverLogoMark from './HoverLogoMark';
import { LoadingOutlined } from '@ant-design/icons';
import './oneButton-css/FinancePartnerSyncModal.css';
import './oneButton-css/FinancePartnerCreateLeadProgressModal.css';
import './oneButton-css/AgreementsModal.css';

type AgreementNoticePush = (
    type: 'success' | 'error' | 'info' | 'warning',
    description: string,
) => void;

export interface AgreementsModalProps {
    open: boolean;
    onClose: () => void;
    /** Numeric opportunity id (Trinity / Contract API). */
    opportunityId: string | number | null | undefined;
    /** Inline dashboard section (no antd Modal wrapper). */
    variant?: 'modal' | 'embedded';
}

export type AgreementRow = {
    key: string;
    financePartner: string;
    /** Display label (`RO`→`R`, `BO`→`B` per Trinity); use `productTypeCode` for logic. */
    productType: string;
    financeMethod: string;
    /** Raw `FinanceMethod` for loan / cash expand gating (Angular `contains(FinanceMethod, 'Loan')`). */
    financeMethodRaw?: string;
    quoteId: string;
    agreementId: string;
    applicationId: string;
    applicationStatus: string;
    contractStatus: string;
    agreementSignStatus: string;
    /** Raw ids / urls for actions (not shown as table columns). */
    quoteIdRaw: string;
    agreementIdRaw: string;
    partnerFileUrl: string | null;
    primaryContactDisplay: string;
    totalEpcDisplay: string;
    isReadyToSign: boolean;
    quoteGroupId: string;
    contractIdRaw: string;
    leadIdRaw: string;
    externalLink: string | null;
    loanStatus: any;
    isQuoteChangeOrder: boolean;
    sfdcSyncDisplay: string;
    creditStatusRaw: string;
    financePartnerRaw: string;
    /** GoodLeap share-loan form (Trinity `GoodLeapShareLoanModelComponent`). */
    goodLeapOfferId?: string;
    goodLeapCustomers?: unknown[];
    annualProductionRaw?: string;
    systemSizeRaw?: string;
    financedAmountRaw?: string | number | null;
    batteryManufacturerNameRaw?: string;
    batteryModelNameRaw?: string;
    batteryQuantityRaw?: string;
    batteryAdderSubTotalRaw?: string;
    batteryPriceSubTotalRaw?: string;
    /** Roof expand fields (Angular detail renderer). */
    roofManufacturerDisplay?: string;
    roofProductDisplay?: string;
    pricingTierDisplay?: string;
    /** Angular detail renderer uses PurchaseMethod for down payment + loan blocks. */
    purchaseMethodRaw?: string;
    /** Sunnova / Service Finance: term + APR strings. */
    termRaw?: string;
    termAprRaw?: string;
    /** Pricing value shown for Sunnova + cash BO/RO blocks. */
    pricingValueRaw?: unknown;
    pricingMethodRaw?: string;
    /** Angular `getPricingData`: append adder qty on pricing override. */
    adderQuantityRaw?: unknown;
    /** Sunnova: monthly/advance payment. */
    monthlyPaymentRaw?: unknown;
    /** Sunnova solar-only. */
    estimatedProductionRaw?: unknown;
    year1SavingsRaw?: unknown;
    /** IGS energy community extras. */
    nonEcEpcRaw?: unknown;
    nonEcPpwRaw?: unknown;
    /** Down payment shown for non-GL/non-Sunnova some products. */
    downPaymentRaw?: unknown;
    /** Cash expand: invoices/payments/balance remaining. */
    invoicesRaw?: unknown;
    paymentsRaw?: unknown;
    balanceRemainingRaw?: unknown;
    /** Service Finance “Total EPC (Net)” (Angular uses TotalLoanEPC / Dealer_Epc). */
    totalEpcNetDisplay?: string;
    /** Trinity `Finance_Partner_Contract_Id` (Service Finance loan id — differs from opportunity `Contract_Id`). */
    financePartnerContractIdRaw?: string;
    /** Product type after Trinity `setProductTypes` (e.g. RO + RR_Price → RRR). */
    productTypeCode?: string;
    /** Trinity `getLatestQuoteByProduct` — max `Contract_Id` for this `ProductType` across all partner grids. */
    isLatestContractForProductType?: boolean;
    /** Raw `IsContractSigned` — roof proposal / sign flows. */
    isContractSignedBool?: boolean;
    /** Raw API string for `IsContractSigned` (e.g. `in-progress` — `view-roof-proposal.component.html`). */
    isContractSignedRawDisplay?: string;
    /** Trinity `SystemId` — Lightreach sign / refresh. */
    systemIdRaw?: string;
    /** Opportunity contract id (`Contract_Id`) — SF status refresh, distinct from finance-partner loan id. */
    opportunityContractIdRaw?: string;
    /** Detail row — Trinity `Desired_PPW_Net` (Gross PPW). */
    desiredPpwNetDisplay?: string;
    depositAmountDisplay?: string;
    quoteExpiresInDisplay?: string;
    financedAmountDisplay?: string;
    /** GetQuotes / contract row Hover estimate id. `0` / null = not Hover. */
    hoverEstimateID?: string | number | null;
    /** Trinity grid: show Load Calculator in Combined Pack when `LoadCalculatorDataExists`. */
    loadCalculatorDataExists?: boolean;
    /** Trinity `ManualOverride` — roof proposal `GenerateProposal`. */
    manualOverride?: boolean;
    /** Raw `Application_Id` on quote contract (may differ from `LoanStatus.Application_Id`). */
    applicationIdRaw?: string;
    /** Trinity `Application_Reference_No` — GoodLeap reference when loan # not assigned yet. */
    applicationReferenceNoRaw?: string;
    /**
     * Angular `ViewProposal` on contract grid rows ( initializes to `''`;
     * automation may set `in-progress`). Drives “View Finance Partner Agreement” like `view-proposal-renderer.component.html`.
     */
    viewProposalRaw?: string;
    /** Trinity `Quote` — partner quote key / UUID for `FinancePartner/contract` `quoteId` (Angular `data.Quote`, not numeric `QuoteId`). */
    quoteUuidRaw?: string;
    /** Trinity `inPersonSigningUrl` — IGS shortcut in `CreateSunnovaContractComponent.ngOnInit`. */
    inPersonSigningUrlRaw?: string | null;
};

function dash(v: string | undefined) {
    const s = v?.trim();
    return s && s !== 'undefined' && s !== 'null' ? s : '—';
}

function readAgreementHoverEstimateId(raw: Record<string, unknown> | null | undefined): string | number | null {
    if (!raw) return null;
    const v =
        raw.hoverEstimateID ??
        raw.HoverEstimateID ??
        raw.Hover_Opportunity_Estimate_Id ??
        raw.hover_Opportunity_Estimate_Id;
    if (v == null || v === '') return null;
    return v as string | number;
}

function isHoverEstimateId(id: string | number | null | undefined): boolean {
    if (id == null || id === '') return false;
    const n = Number(id);
    if (!Number.isNaN(n)) return n !== 0;
    return String(id).trim().length > 0;
}

function AgreementEstimateSourceKv({ hoverEstimateID }: { hoverEstimateID?: string | number | null }) {
    return (
        <div className="obd-agreements-modal__detail-kv">
            <span className="obd-agreements-modal__detail-k">Estimate Source</span>
            <span className="obd-agreements-modal__detail-v obd-agreements-modal__estimate-source">
                {isHoverEstimateId(hoverEstimateID) ? <HoverLogoMark size="sm" /> : '—'}
            </span>
        </div>
    );
}

/** Trinity `detail-cell-renderer`: `ProductType.includes('R') && ProductType !== 'RR'` — manufacturer, product, pricing. */
function AgreementRoofProductExpandFields({ row }: { row: AgreementRow }) {
    return (
        <>
            <div className="obd-agreements-modal__detail-kv">
                <span className="obd-agreements-modal__detail-k">Roof Manufacturer</span>
                <span className="obd-agreements-modal__detail-v">{dash(row.roofManufacturerDisplay)}</span>
            </div>
            <div className="obd-agreements-modal__detail-kv">
                <span className="obd-agreements-modal__detail-k">Roof Product</span>
                <span className="obd-agreements-modal__detail-v">{dash(row.roofProductDisplay)}</span>
            </div>
            <div className="obd-agreements-modal__detail-kv">
                <span className="obd-agreements-modal__detail-k">Pricing Tier</span>
                <span className="obd-agreements-modal__detail-v">{ (row.pricingTierDisplay == 'PricingOverride')?'Pricing Override':row.pricingTierDisplay}</span>
            </div>
        </>
    );
}

function formatMoney(v: unknown): string {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(n)) return '—';
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatMoneyZero(v: unknown): string {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(n)) return '$0.00';
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumberFixed(v: unknown, digits: number): string {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(n)) return '—';
    return n.toFixed(digits);
}

/** Trinity detail row — `SystemSize` as shown in oneBUTTON Cash expand panel. */
function formatSystemSizeKwDisplay(raw: string | undefined): string {
    if (!raw?.trim()) return '—';
    const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(n)) return raw.trim();
    return `${n.toFixed(3)} Kw`;
}

function formatAnnualKwhDisplay(raw: string | undefined): string {
    if (!raw?.trim()) return '—';
    return `${raw.trim()} Kwh`;
}

function quoteDetailsProductLabel(row: AgreementRow): string {
    const pt = (row.productTypeCode || row.productType || '').toUpperCase();
    /** BO / B = Battery Only (do not use `includes('B')` — SB / SBR would match wrongly). */
    if (pt === 'BO' || pt === 'B') return 'Battery Only';
    /** Solar + Battery (RetrieveContract / GetQuoteDetails `ProductType`). */
    if (pt === 'SB') return 'Solar, Battery';
    if (pt === 'SBR') return 'Solar, Battery, Roof';
    /** `RO` = Roof Only (RetrieveContract); `RR` = R&R line. */
    if (pt === 'RO') return 'Roof Only';
    if (pt === 'RR' || pt === 'RRR') return pt;
    return 'Solar';
}

function termAprDisplay(row: AgreementRow): string {
    const pricingMethod = String(row.financeMethodRaw || row.financeMethod || '');
    const term = String(row.termRaw || '').trim();
    const termApr = String(row.termAprRaw || '').trim();
    const aprNum = parseFloat(String(termApr).replace(/[^0-9.-]/g, ''));
    const apr = Number.isFinite(aprNum) ? aprNum.toFixed(pricingMethod.includes('Loan') ? 2 : 1) : '0.0';
    if (pricingMethod.includes('Loan')) {
        const years = term && term !== 'N/A' ? term : '0';
        return `${years} Years @ ${apr} %`;
    }
    return `${apr} %`;
}

function pricingTierAngularDisplay(row: AgreementRow): string {
    const raw = String(row.pricingTierDisplay ?? '').trim();
    if (!raw || raw === '—') return '—';
    const isOverride = raw.toLowerCase() === 'pricing override' || raw.toLowerCase() === 'pricingoverride';
    if (!isOverride) return raw;
    const aq = row.adderQuantityRaw;
    const n = typeof aq === 'number' ? aq : parseFloat(String(aq ?? '').replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(n) || n === 0) return 'Pricing Override';
    return `Pricing Override -$ ${n}`;
}

function pricingValueAngularDisplay(row: AgreementRow): string {
    const pricingValue = row.pricingValueRaw;
    const n = typeof pricingValue === 'number' ? pricingValue : parseFloat(String(pricingValue ?? '').replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(n)) return '—';
    const pt = String(row.productTypeCode || row.productType || '').toUpperCase();
    const pricingMethod = String(row.pricingMethodRaw || '').toLowerCase();
    const decimals = pt.includes('S') && pricingMethod.includes('rate') ? 3 : 2;
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

/** Pixel size for toolbar + submit-strip icons (matches Ant Spin small footprint). */
const AGREEMENT_TOOL_ICON_PX = 18;
/** Trinity `minWidth: 275` — compact width for submit / service-finance application cells. */
const AGREEMENT_SUBMIT_APPLICATION_COL_WIDTH = 150;
/** Wide enough for the nowrap header "Sign Finance Partner Contract" (Ant table thead `white-space: nowrap`). */
const AGREEMENT_SIGN_FINANCE_PARTNER_CONTRACT_COL_WIDTH = 170;
/** SFDC Sync — nowrap header + padding; minWidth prevents rc-table from shrinking below content. */
const AGREEMENT_SFDC_SYNC_COL_WIDTH = 116;
/** Trinity Documents — nowrap header + eye icon; paired with SFDC so neither column steals space. */
const AGREEMENT_TRINITY_DOCUMENTS_COL_WIDTH = 128;

function AgreementIconSpin() {
    return (
        <span className="obd-agreements-modal__ag-icon-slot">
            <Spin size="small" />
        </span>
    );
}

/**
 * Angular `credit-status-renderer.component.html` — Phosphor `CurrencyCircleDollar` (duotone).
 * Opens `CreditCheckModelComponent` (“Check Credit”); not the Salesforce cloud. Color from `.obd-agreements-modal__ag-icon`.
 */
function CreditStatusGridIcon() {
    return (
        <CurrencyCircleDollar
            size={20}
            weight="duotone"
            className="obd-agreements-modal__ag-icon"
            aria-hidden
        />
    );
}

/** Salesforce cloud — matches Phosphor toolbar icon size / theme color */
function SalesforceCloudIcon({ size = AGREEMENT_TOOL_ICON_PX }: { size?: number }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            height={size}
            width={size}
            viewBox="0 0 640 512"
            aria-hidden
            className="obd-agreements-modal__ag-icon obd-agreements-modal__sf-cloud-icon"
        >
            <path
                fill="currentColor"
                d="M248.9 245.6h-26.4c.7-5.2 3.3-14.1 13.6-14.1 6.8 0 12 3.8 12.7 14.1zm136.7-13.9c-.5 0-14.1-1.8-14.1 20s13.6 20 14.1 20c13 0 14.1-13.5 14.1-20 0-21.8-13.7-20-14.1-20zm-243.2 23.8a8.6 8.6 0 0 0 -3.3 7.3c0 4.8 2.1 6.1 3.3 7.1 4.7 3.7 15.1 2.1 20.9 1v-16.9c-5.3-1.1-16.7-2-20.9 1.7zM640 232c0 87.6-80 154.4-165.4 136.4-18.4 33-70.7 70.8-132.2 41.6-41.2 96.1-177.9 92.2-213.8-5.2C8.9 428.8-50.2 266.5 53.4 205.6 18.6 126.2 76 32 167.7 32a124.2 124.2 0 0 1 98.6 48.7c20.7-21.4 49.4-34.8 81.2-34.8 42.3 0 79 23.5 98.8 58.6C539 63.8 640 132.7 640 232zm-519.6 31.8c0-11.8-11.7-15.2-17.9-17.2-5.3-2.1-13.4-3.5-13.4-8.9 0-9.5 17-6.7 25.2-2.1 0 0 1.2 .7 1.6-.5 .2-.7 2.4-6.6 2.6-7.3a1.1 1.1 0 0 0 -.7-1.4c-12.3-7.6-40.7-8.5-40.7 12.7 0 12.5 11.5 15.4 17.9 17.2 4.7 1.6 13.2 3 13.2 8.7 0 4-3.5 7.1-9.2 7.1a31.8 31.8 0 0 1 -19-6.4c-.5-.2-1.4-.7-1.7 .7l-2.4 7.5c-.5 .9 .2 1.2 .2 1.4 1.8 1.4 10.3 6.6 22.8 6.6 13.2 0 21.4-7.1 21.4-18.1zm32-42.6c-10.1 0-18.7 3.2-21.4 5.2a1 1 0 0 0 -.2 1.4l2.6 7.1a1 1 0 0 0 1.2 .7c.7 0 6.8-4 16.9-4 4 0 7.1 .7 9.2 2.4 3.6 2.8 3.1 8.3 3.1 10.6-4.8-.3-19.1-3.4-29.4 3.8a16.9 16.9 0 0 0 -7.3 14.5c0 5.9 1.5 10.4 6.6 14.4 12.2 8.2 36.3 2 38.1 1.4 1.6-.3 3.5-.7 3.5-1.9v-33.9c0-4.6 .3-21.6-22.8-21.6zM199 200.2a1.1 1.1 0 0 0 -1.2-1.2H188a1.1 1.1 0 0 0 -1.2 1.2v79a1.1 1.1 0 0 0 1.2 1.2h9.9a1.1 1.1 0 0 0 1.2-1.2zm55.8 28.9c-2.1-2.3-6.8-7.5-17.7-7.5-3.5 0-14.2 .2-20.7 8.9-6.4 7.6-6.6 18.1-6.6 21.4 0 3.1 .2 14.3 7.1 21.2 2.6 2.9 9.1 8.2 22.8 8.2 10.8 0 16.5-2.4 18.6-3.8 .5-.2 .7-.7 .2-1.9l-2.4-6.8a1.3 1.3 0 0 0 -1.4-.7c-2.6 .9-6.4 2.8-15.3 2.8-17.4 0-16.9-14.7-16.9-16.7h37.2a1.2 1.2 0 0 0 1.2-.9c-.3 0 2.1-14.7-6.1-24.2zm36.7 52.7c13.2 0 21.4-7.1 21.4-18.1 0-11.8-11.7-15.2-17.9-17.2-4.1-1.7-13.4-3.4-13.4-8.9 0-3.8 3.3-6.4 8.5-6.4a38.1 38.1 0 0 1 16.7 4.2s1.2 .7 1.7-.5c.2-.7 2.4-6.6 2.6-7.3a1.1 1.1 0 0 0 -.7-1.4c-7.9-4.9-16.7-4.9-20.2-4.9-12 0-20.5 7.3-20.5 17.6 0 12.5 11.5 15.4 17.9 17.2 6.1 2 13.2 3.3 13.2 8.7 0 4-3.5 7.1-9.2 7.1a31.8 31.8 0 0 1 -19-6.4 1 1 0 0 0 -1.7 .7l-2.4 7.5c-.5 .9 .2 1.2 .2 1.4 1.7 1.4 10.3 6.6 22.8 6.6zM357.1 224c0-.7-.2-1.2-1.2-1.2h-11.8c0-.1 .9-8.9 4.5-12.5 4.2-4.2 11.8-1.6 12-1.6 1.2 .5 1.4 0 1.6-.5l2.8-7.8c.7-.9 0-1.2-.2-1.4-5.1-2-17.4-2.9-24.5 4.2-5.5 5.5-7 13.9-8 19.5h-8.5a1.3 1.3 0 0 0 -1.2 1.2l-1.4 7.8c0 .7 .2 1.2 1.2 1.2h8.2c-8.5 47.9-8.8 50.2-10.4 55.5-1.1 3.6-3.3 6.9-5.9 7.8-.1 0-3.9 1.7-9.6-.2 0 0-.9-.5-1.4 .7-.2 .7-2.6 6.8-2.8 7.5s0 1.4 .5 1.4c5.1 2 13 1.8 17.9 0 6.3-2.3 9.7-7.9 11.5-12.9 2.8-7.7 2.8-9.8 11.8-59.7h12.2a1.3 1.3 0 0 0 1.2-1.2zm53.4 16c-.6-1.7-5.1-18.1-25.2-18.1-15.3 0-23 10-25.2 18.1-1 3-3.2 14 0 23.5 .1 .3 4.4 18.1 25.2 18.1 15 0 22.9-9.6 25.2-18.1 3.2-9.6 1-20.5 0-23.5zm45.4-16.7c-5-1.7-16.6-1.9-22.1 5.4v-4.5a1.1 1.1 0 0 0 -1.2-1.2h-9.4a1.1 1.1 0 0 0 -1.2 1.2v55.3a1.1 1.1 0 0 0 1.2 1.2h9.6a1.1 1.1 0 0 0 1.2-1.2v-27.8c0-2.9 .1-11.4 4.5-15.1 4.9-4.9 12-3.4 13.4-3.1a1.6 1.6 0 0 0 1.4-.9 74 74 0 0 0 3.1-8 1.2 1.2 0 0 0 -.5-1.4zm46.8 54.1l-2.1-7.3c-.5-1.2-1.4-.7-1.4-.7-4.2 1.8-10.2 1.9-11.3 1.9-4.6 0-17.2-1.1-17.2-19.8 0-6.2 1.9-19.8 16.5-19.8a34.9 34.9 0 0 1 11.5 1.7s.9 .5 1.2-.7c.9-2.6 1.6-4.5 2.6-7.5 .2-.9-.5-1.2-.7-1.2-11.6-3.9-22.3-2.5-27.8 0-1.6 .7-16.2 6.5-16.2 27.5 0 2.9-.6 30.1 28.9 30.1a44.5 44.5 0 0 0 15.5-2.8 1.3 1.3 0 0 0 .5-1.4zm53.9-39.5c-.8-3-5.4-16.2-22.4-16.2-16 0-23.5 10.1-25.6 18.6a38.6 38.6 0 0 0 -1.7 11.8c0 25.9 18.8 29.4 29.9 29.4 10.8 0 16.5-2.4 18.6-3.8 .5-.2 .7-.7 .2-1.9l-2.4-6.8a1.3 1.3 0 0 0 -1.4-.7c-2.6 .9-6.4 2.8-15.3 2.8-17.4 0-16.9-14.7-16.9-16.7h37.2a1.3 1.3 0 0 0 1.2-.9c-.2 0 .9-7.1-1.4-15.5zm-23.3-6.4c-10.3 0-13 9-13.6 14.1H546c-.9-11.9-7.6-14.1-12.7-14.1z"
            />
        </svg>
    );
}

/** Angular `sfsc-sync-cell-renderer.component.html` — green cloud only (opens 3-step sync modal); spinner when `in-progress`. No dropdown. */
function AngularGridSfdcSyncCell({
    row,
    sfdcBundleRunning,
    onSyncClick,
}: {
    row: AgreementRow;
    sfdcBundleRunning: boolean;
    onSyncClick: (row: AgreementRow) => void;
}) {
    const raw = String(row.sfdcSyncDisplay ?? '').trim();
    if (raw.toLowerCase() === 'in-progress') {
        return (
            <div className="obd-agreements-modal__angular-grid-cell">
                <LoadingOutlined className="obd-agreements-modal__angular-orange-spin" spin aria-hidden />
            </div>
        );
    }
    if (raw === '') {
        return (
            <div className="obd-agreements-modal__angular-grid-cell">
                <button
                    type="button"
                    className="obd-agreements-modal__icon-btn"
                    title="SFDC Sync"
                    disabled={sfdcBundleRunning}
                    aria-label="SFDC Sync"
                    onClick={() => onSyncClick(row)}
                >
                    <SalesforceCloudIcon />
                </button>
            </div>
        );
    }
    return <span className="obd-agreements-modal__cell-muted">{raw || '—'}</span>;
}

/** Trinity Documents column — same control as before: rounded `icon-btn` + duotone eye (no menus). */
function AngularGridTrinityDocumentsCell({
    row,
    onViewClick,
}: {
    row: AgreementRow;
    onViewClick: (row: AgreementRow) => void;
}) {
    return (
        <div className="obd-agreements-modal__angular-grid-cell">
            <button
                type="button"
                className="obd-agreements-modal__icon-btn"
                title="View Trinity Contract"
                aria-label="View Trinity Contract"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onViewClick(row);
                }}
            >
                <Eye size={AGREEMENT_TOOL_ICON_PX} weight="duotone" className="obd-agreements-modal__ag-icon" aria-hidden />
            </button>
        </div>
    );
}

function partnerSectionTitle(financePartnerLabel: string): string {
    const x = financePartnerLabel.replace(/\s/g, '').toLowerCase();
    if (x.includes('good') && x.includes('leap')) return 'Good Leap Agreements';
    if (x.includes('enfin')) return 'Enfin Agreements';
    if (x.includes('sunlight')) return 'Sunlight Agreements';
    if (x.includes('sunnova')) return 'Sunnova Agreements';
    if (x.includes('lightreach')) return 'Lightreach Agreements';
    if (x.includes('igs')) return 'IGS Agreements';
    if (x.includes('cash')) return 'Cash Agreements';
    const base = financePartnerLabel.trim() || 'Other';
    return `${base} Agreements`;
}

/**
 * Partner blocks — “Display Order for Each Finance Partner”:
 * `_.sortBy(contractList, item => _.indexOf(customFinancePartnerOrder, item.name))` with
 * `customFinancePartnerOrder = ['Sunnova','IGS','Service Finance','Cash']`.
 * Names not in that list get index `-1` and sort *before* `0..3` (lodash ascending); original
 * `contractList` order among `-1` is Good Leap → Sunlight → Lightreach → Enfin.
 *
 * Rows within each partner: `_.sortBy(rowData, ProductType)` then `_.sortBy(rowData, FinanceMethod)`.
 * Lodash stable second sort → **FinanceMethod** is the effective primary key; **ProductType** breaks ties.
 * Unknown `FinanceMethod` values get `indexOf` **`-1`**, which sorts **before** listed methods (e.g. `Roof Loan` before `Loan`).
 */
/** Trinity `contract-list.retrieveContract` — must match `customFinancePartnerOrder` exactly. */
const ANGULAR_CUSTOM_FINANCE_PARTNER_ORDER = ['Sunnova', 'IGS', 'Service Finance', 'Cash'] as const;

function indexInAngularFinancePartnerOrder(displayName: string): number {
    const t = displayName.trim();
    return (ANGULAR_CUSTOM_FINANCE_PARTNER_ORDER as readonly string[]).findIndex(
        (x) => x === t || x.toLowerCase() === t.toLowerCase(),
    );
}

/**
 * Numeric key so React `.sort` matches Angular `_.sortBy(..., indexOf(ANGULAR_CUSTOM_FINANCE_PARTNER_ORDER, name))`:
 * Good Leap / Sunlight / Lightreach / Enfin → `0..3`; Sunnova..Cash → `100..103` (after the `-1` group).
 */
function partnerSortKey(row: Pick<AgreementRow, 'financePartner' | 'financePartnerRaw'>): number {
    const raw = String(row.financePartnerRaw || row.financePartner || '').trim();
    const angularIdx = indexInAngularFinancePartnerOrder(raw);
    if (angularIdx !== -1) return 100 + angularIdx;

    const k = detectAgreementPartnerKind(row);
    switch (k) {
        case 'goodLeap':
            return 0;
        case 'sunlight':
            return 1;
        case 'lightreach':
            return 2;
        case 'enfin':
            return 3;
        case 'sunnova':
            return 100;
        case 'igs':
            return 101;
        case 'serviceFinance':
            return 102;
        case 'cash':
            return 103;
        default:
            return 199;
    }
}

/** Per-partner row order */
const TRINITY_PRODUCT_TYPE_ORDER = ['S', 'SBR', 'RO', 'RRR', 'BO'] as const;

/** `_.indexOf(customProductTypeOrder, ProductType)` — unknown types are `-1` and sort *before* `S` (0). */
function productTypeSortIndex(code: string): number {
    const c = code.trim();
    /** `R` (Roof Only / RR map) sorts with `RO` per `customProductTypeOrder` intent. */
    const forOrder = c === 'R' ? 'RO' : c;
    const i = TRINITY_PRODUCT_TYPE_ORDER.findIndex(
        (x) => x === forOrder || x.toUpperCase() === forOrder.toUpperCase(),
    );
    return i;
}

/** finance method sort. */
const TRINITY_FINANCE_METHOD_ORDER = [
    'PPA-EZ',
    'Loan',
    'Prepaid PPA-EZ',
    'Loan Storage',
    'Lease Storage',
    'Accessory_Loan',
    'Battery Loan',
] as const;

/**
 * `_.indexOf(customFinanceMethodOrder, FinanceMethod)` — **not listed** (e.g. `Roof Loan`, `Cash Contract`)
 * returns **`-1`**, which sorts *before* `PPA-EZ` (0) and `Loan` (1). Using `999` for unknowns reversed that order.
 */
function financeMethodSortIndex(method: string): number {
    const m = method.trim();
    if (!m || m === '—') return -1;
    const direct = TRINITY_FINANCE_METHOD_ORDER.indexOf(m as (typeof TRINITY_FINANCE_METHOD_ORDER)[number]);
    if (direct !== -1) return direct;
    const canon = (s: string) => s.toLowerCase().replace(/[\s_-]+/g, '');
    const mc = canon(m);
    for (let i = 0; i < TRINITY_FINANCE_METHOD_ORDER.length; i++) {
        const k = TRINITY_FINANCE_METHOD_ORDER[i];
        if (canon(k) === mc) return i;
    }
    return -1;
}

/** Trinity `contract-list.setProductTypes` + long-name map from `quote-vesrion.buildQuoteVersionsLayout`. */
function setProductTypesFromContract(c: Record<string, unknown>): string {
    const raw = String(c.ProductType ?? c.productType ?? '').trim();
    const pt = mapQuoteProductTypeLongNameToCode(raw);
    const rr = c.RR_Price ?? c.rr_Price;
    const rrNum = rr != null && rr !== '' ? Number(rr) : NaN;
    if ((pt === 'RO' || pt === 'R') && Number.isFinite(rrNum) && rrNum !== 0) {
        return 'RRR';
    }
    return pt;
}

/** Trinity `ProductTypeRendererComponent.getLabelForProductType` — grid label only; `productTypeCode` stays raw for sort/API. */
function formatProductTypeDisplay(code: string): string {
    const c = code.trim();
    switch (c) {
        case 'RO':
            return 'R';
        case 'BO':
            return 'B';
        default:
            return c;
    }
}

/** Same numeric formatting as `contract-list.retrieveContract` on `AnnualProduction`. */
function formatAnnualProductionTrinity(v: unknown): string {
    if (v == null || v === '') return '';
    const n = Number(String(v).replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(n)) return String(v).trim();
    return n.toFixed(3);
}

function sortRowsLikeContractList(rows: AgreementRow[]): AgreementRow[] {
    return [...rows].sort((a, b) => {
        const pa = partnerSortKey(a);
        const pb = partnerSortKey(b);
        if (pa !== pb) return pa - pb;
        const fa = financeMethodSortIndex(a.financeMethod === '—' ? '' : a.financeMethod);
        const fb = financeMethodSortIndex(b.financeMethod === '—' ? '' : b.financeMethod);
        if (fa !== fb) return fa - fb;
        const pta = productTypeSortIndex(a.productTypeCode || '');
        const ptb = productTypeSortIndex(b.productTypeCode || '');
        return pta - ptb;
    });
}

/**
 * Same key as Angular `row.ProductType` on the contract row (Trinity `setProductTypes` output).
 * Do not use display `productType` (e.g. RO → "R") — that breaks max-ContractId matching vs `getLatestQuoteByProduct`.
 */
function productTypeKeyForLatestRow(r: AgreementRow): string {
    return String(r.productTypeCode ?? '').trim();
}

function parseContractIdForLatest(raw: string | undefined): number {
    return parseInt(String(raw || '').replace(/\D/g, ''), 10);
}

/**
 * Trinity `contract-list.getLatestQuoteByProduct`: max `ContractId` per `ProductType` across **all**
 * partner grids (sorting each block by `ContractId` desc first does not change the max).
 */
function getLatestQuoteByProduct(rows: AgreementRow[]): Record<string, number> {
    const latestQuoteByProduct: Record<string, number> = {};
    for (const row of rows) {
        const productType = productTypeKeyForLatestRow(row);
        const contractId = parseContractIdForLatest(row.contractIdRaw);
        if (!productType || !Number.isFinite(contractId)) continue;

        if (latestQuoteByProduct[productType] != null) {
            latestQuoteByProduct[productType] = Math.max(latestQuoteByProduct[productType], contractId);
        } else {
            latestQuoteByProduct[productType] = contractId;
        }
    }

    return latestQuoteByProduct;
}

/** Trinity `rowClassRules['row-green-background']`: row is latest contract for its `ProductType` (not full-width row in Angular). */
function attachLatestContractFlags(rows: AgreementRow[], latest: Record<string, number>): AgreementRow[] {
    return rows.map((r) => {
        const pt = productTypeKeyForLatestRow(r);
        const cid = parseContractIdForLatest(r.contractIdRaw);
        if (!pt || !Number.isFinite(cid)) return { ...r, isLatestContractForProductType: false };
        const max = latest[pt];
        const isLatest = max != null && max > 0 && cid === max;
        return { ...r, isLatestContractForProductType: isLatest };
    });
}

async function mergeServiceFinanceApplicationStatus(
    opportunityId: string | number,
    rows: AgreementRow[],
): Promise<AgreementRow[] | null> {
    const sf = 'Service Finance';
    const pending = rows.filter(
        (r) =>
            (r.financePartnerRaw || r.financePartner).trim() === sf &&
            !!r.financePartnerContractIdRaw &&
            String(r.contractStatus || '').trim().toLowerCase() !== 'signed',
    );
    if (!pending.length) return null;
    const ids = pending.map((r) => r.financePartnerContractIdRaw!);
    try {
        const res = await contractService.getApplicationStatus({
            opportunityId,
            financePartnerContractIds: ids,
        });
        if (!Array.isArray(res) || !res.length) return null;
        const signed = new Set(
            res
                .map((x: Record<string, unknown>) =>
                    String(x.FinancePartnerContractId ?? x.financePartnerContractId ?? '').trim(),
                )
                .filter(Boolean),
        );
        if (!signed.size) return null;
        let changed = false;
        const next = rows.map((r) => {
            const fp = r.financePartnerContractIdRaw;
            if (!fp || !signed.has(fp)) return r;
            if (String(r.contractStatus || '').trim().toLowerCase() === 'signed') return r;
            changed = true;
            return { ...r, contractStatus: 'Signed' };
        });
        return changed ? next : null;
    } catch {
        return null;
    }
}

function groupRowsByPartner(rows: AgreementRow[]): { partner: string; rows: AgreementRow[] }[] {
    const order: string[] = [];
    const map = new Map<string, AgreementRow[]>();
    for (const r of rows) {
        const k = String(r.financePartnerRaw || r.financePartner || '').trim();
        if (!map.has(k)) {
            map.set(k, []);
            order.push(k);
        }
        map.get(k)!.push(r);
    }
    const sortedKeys = [...order].sort((a, b) => {
        const ia = partnerSortKey({ financePartner: a, financePartnerRaw: a });
        const ib = partnerSortKey({ financePartner: b, financePartnerRaw: b });
        if (ia !== ib) return ia - ib;
        return a.localeCompare(b);
    });
    return sortedKeys.map((partner) => ({
        partner,
        /** Re-apply Trinity contract-list sort within each finance-partner section. */
        rows: sortRowsLikeContractList(map.get(partner)!),
    }));
}

/** Trinity `IsContractSigned` — API may send bool, number, or string; avoid treating `"No"` as signed. */
function parseIsContractSignedRaw(c: Record<string, unknown>): boolean {
    const v = c.IsContractSigned ?? c.isContractSigned;
    if (v === true || v === 'true' || v === 1 || v === '1') return true;
    if (v === false || v === 'false' || v === 0 || v === '0') return false;
    if (v == null || v === '') return false;
    if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        if (s === 'yes' || s === 'signed') return true;
        if (s === 'no' || s === 'unsigned' || s === 'in-progress' || s === 'in_progress') return false;
    }
    return false;
}

function normalizeRows(raw: any[]): AgreementRow[] {
    if (!Array.isArray(raw)) return [];
    return raw.map((c: any, i: number) => {
        const productTypeCode = setProductTypesFromContract(c);
        const pricingTypeRaw =
            (typeof c.Pricing_Type === 'string' && c.Pricing_Type) ||
            (typeof c.pricingType === 'string' && c.pricingType) ||
            (typeof c.PricingType === 'string' && c.PricingType) ||
            '';
        const pricingTierDisplay = pricingTypeRaw
            ? pricingTypeRaw === 'PricingOverride'
                ? 'Pricing Override'
                : pricingTypeRaw
            : '';
        const pricingValueRaw = c.PricingValue ?? c.pricingValue ?? null;
        return {
        key: `${c.QuoteId ?? c.quoteId ?? 'q'}_${c.AgreementId ?? ''}_${i}`,
        financePartner: dash(String(c.FinancePartner ?? c.financePartner ?? '')),
        productType: dash(formatProductTypeDisplay(productTypeCode)),
        productTypeCode,
        financeMethod: dash(String(c.FinanceMethod ?? c.financeMethod ?? '')),
        financeMethodRaw: String(c.FinanceMethod ?? c.financeMethod ?? '').trim(),
        quoteId: dash(String(c.QuoteId ?? c.quoteId ?? '')),
        agreementId: dash(String(c.AgreementId ?? c.agreementId ?? '')),
        applicationId: dash(String(c.Application_Id ?? c.applicationId ?? '')),
        applicationStatus: dash(String(c.Application_Status ?? c.applicationStatus ?? '')),
        contractStatus: dash(
            String(c.Opportunity_Contract_Status ?? c.contractStatus ?? c.TrinityContractStatus ?? ''),
        ),
        agreementSignStatus: dash(String(c.AgreementDetails?.Status ?? c.contractSignedStatus ?? '')),
        quoteIdRaw: String(c.QuoteId ?? c.quoteId ?? '').trim(),
        agreementIdRaw: String(c.AgreementId ?? c.agreementId ?? '').trim(),
        partnerFileUrl:
            (typeof c.SunnovaContractFileURL === 'string' && c.SunnovaContractFileURL) ||
            (typeof c.AgreementDetails?.Url === 'string' && c.AgreementDetails.Url) ||
            null,
        primaryContactDisplay: dash(String(c.PrimaryContact ?? c.PrimaryContactDisplay ?? '')),
        totalEpcDisplay: formatMoney(c.TotalEPC ?? c.TotalEpc ?? c.TotalLoanEPC),
        totalEpcNetDisplay: formatMoney(c.TotalLoanEPC ?? c.TotalLoanEPC ?? c.Dealer_Epc ?? c.TrinityLoanEPC),
        /** Trinity `ReadyToSignRendererComponent.agInit`: `(params.data.IsReadyToSign == true)`. */
        isReadyToSign: c.IsReadyToSign == true || c.isReadyToSign == true,
        quoteGroupId: c.QuoteGroupId != null && String(c.QuoteGroupId).trim() !== '' ? String(c.QuoteGroupId) : '',
        contractIdRaw: String(
            c.Contract_Id ??
                c.ContractId ??
                c.ContractID ??
                c.Opportunity_Contract_Id ??
                c.Finance_Partner_Contract_Id ??
                c.financePartnerContractId ??
                '',
        ).trim(),
        leadIdRaw: String(c.LeadId ?? c.Lead_Id ?? '').trim(),
        externalLink: typeof c.link === 'string' && c.link.trim() ? c.link.trim() : null,
        loanStatus: c.LoanStatus ?? c.loanStatus ?? null,
        isQuoteChangeOrder: !!(c.IsQuote_Change_Order ?? c.isQuote_Change_Order),
        sfdcSyncDisplay: String(c.SFDCsync ?? c.SfdcSync ?? c.SFDC_Sync ?? '').trim(),
        creditStatusRaw: String(c.CreditStatus ?? c.creditStatus ?? '').trim(),
        financePartnerRaw: String(c.FinancePartner ?? c.financePartner ?? '').trim(),
        viewProposalRaw: String(c.ViewProposal ?? c.viewProposal ?? '').trim(),
        quoteUuidRaw: String(c.Quote ?? c.quote ?? '').trim(),
        inPersonSigningUrlRaw:
            c.inPersonSigningUrl != null && String(c.inPersonSigningUrl).trim() !== ''
                ? String(c.inPersonSigningUrl).trim()
                : null,
        goodLeapOfferId: String(c.Offer_Id ?? c.offer_Id ?? '').trim(),
        goodLeapCustomers: Array.isArray(c.Customers)
            ? c.Customers
            : Array.isArray(c.customers)
              ? c.customers
              : [],
        annualProductionRaw: formatAnnualProductionTrinity(c.AnnualProduction ?? c.annualProduction ?? ''),
        systemSizeRaw: String(c.SystemSize ?? c.systemSize ?? '').trim(),
        financedAmountRaw: c.Financed_Amount ?? c.FinancedAmount ?? c.financedAmount ?? null,
        batteryManufacturerNameRaw: String(c.BatteryManufacturerName ?? c.batteryManufacturerName ?? '').trim(),
        batteryModelNameRaw: String(c.BatteryModelName ?? c.batteryModelName ?? '').trim(),
        batteryQuantityRaw: String(c.Quantity ?? c.quantity ?? '').trim(),
        batteryAdderSubTotalRaw: String(c.Battery_Adder_Sub_Total ?? c.battery_Adder_Sub_Total ?? '').trim(),
        batteryPriceSubTotalRaw: String(c.Battery_Price_Sub_Total ?? c.battery_Price_Sub_Total ?? '').trim(),
        roofManufacturerDisplay: dash(String(c.ManufacturerName ?? c.manufacturerName ?? '')),
        roofProductDisplay: dash(String(c.RoofProduct ?? c.roofProduct ?? '')),
        pricingTierDisplay: dash(pricingTierDisplay),
        purchaseMethodRaw: String(c.PurchaseMethod ?? c.purchaseMethod ?? '').trim(),
        termRaw: String(c.Term ?? c.term ?? '').trim(),
        termAprRaw: String(c.TermAPR ?? c.termApr ?? c.termAPR ?? '').trim(),
        pricingValueRaw,
        pricingMethodRaw: String(c.PricingMethod ?? c.pricingMethod ?? '').trim(),
        adderQuantityRaw: c.Adder_Quantity ?? c.adder_Quantity ?? c.adderQuantity ?? null,
        monthlyPaymentRaw: c.MonthlyPayment ?? c.monthlyPayment ?? null,
        estimatedProductionRaw: c.EstimatedProduction ?? c.estimatedProduction ?? null,
        year1SavingsRaw: c.Year1Savings ?? c.year1Savings ?? null,
        nonEcEpcRaw: c.Non_EC_EPC ?? c.non_EC_EPC ?? null,
        nonEcPpwRaw: c.Non_EC_PPW ?? c.non_EC_PPW ?? null,
        downPaymentRaw: c.DownPayment ?? c.downPayment ?? null,
        invoicesRaw: c.Invoices ?? c.invoices ?? null,
        paymentsRaw: c.Payments ?? c.payments ?? null,
        balanceRemainingRaw: c.BalanceRemaining ?? c.balanceRemaining ?? null,
        financePartnerContractIdRaw: String(
            c.Finance_Partner_Contract_Id ?? c.finance_Partner_Contract_Id ?? c.financePartnerContractId ?? '',
        ).trim(),
        isContractSignedBool: parseIsContractSignedRaw(c as Record<string, unknown>),
        isContractSignedRawDisplay: String(c.IsContractSigned ?? c.isContractSigned ?? ''),
        systemIdRaw: String(c.SystemId ?? c.systemId ?? '').trim(),
        opportunityContractIdRaw: String(
            c.Contract_Id ?? c.ContractId ?? c.ContractID ?? c.Opportunity_Contract_Id ?? '',
        ).trim(),
        desiredPpwNetDisplay: formatMoney(c.Desired_PPW_Net ?? c.desired_PPW_Net),
        depositAmountDisplay: formatMoneyZero(c.Deposit_Amount ?? c.deposit_Amount),
        quoteExpiresInDisplay: dash(String(c.QuoteExpiresIn ?? c.quoteExpiresIn ?? '')),
        financedAmountDisplay: formatMoney(c.Financed_Amount ?? c.FinancedAmount ?? c.financedAmount),
        hoverEstimateID: readAgreementHoverEstimateId(c as Record<string, unknown>),
        //commenting below line: data only available in root level, assigning outside (OBA-5345)
        // loadCalculatorDataExists: !!(c.LoadCalculatorDataExists ?? c.loadCalculatorDataExists), 
        manualOverride: !!(c.ManualOverride ?? c.manualOverride),
        applicationIdRaw: String(c.Application_Id ?? c.applicationId ?? '').trim(),
        applicationReferenceNoRaw: String(
            c.Application_Reference_No ?? c.application_Reference_No ?? c.applicationReferenceNo ?? '',
        ).trim(),
        };
    });
}

/** Trinity `contract-list.retrieveContract` row shaping: sort + “latest quote per product” highlight + SF status merge input. */
export function finalizeAgreementRowsFromQuoteContract(raw: any[]): AgreementRow[] {
    const list = normalizeRows(raw);
    const sorted = sortRowsLikeContractList(list);
    const latest = getLatestQuoteByProduct(sorted);
    return attachLatestContractFlags(sorted, latest);
}

/** API `Message` may be JSON `{"message":"..."}` (e.g. Lightreach create quote). */
function trinityContractMessageDescription(raw: unknown, fallback: string): string {
    const s = String(raw ?? '').trim();
    if (!s) return fallback;
    try {
        const o = JSON.parse(s) as { message?: unknown };
        if (o?.message != null && String(o.message).trim()) return String(o.message).trim();
    } catch {
        /* plain text */
    }
    return s || fallback;
}

export type SfdcBundleStep = { id: string; label: string; status: 'pending' | 'running' | 'done' | 'error'; detail?: string };

/** Trinity `SfscSyncSunnovaModelComponent` — API error rows from `SFDC_Sync_*` endpoints. */
export type SfdcApiErrorDetail = { objectName: string; name: string; message: string };

function parseSfdcSyncApiError(e: unknown): { details: SfdcApiErrorDetail[]; fallbackMessage: string } {
    const ax = e as { response?: { data?: Record<string, unknown> }; message?: string };
    const data = ax?.response?.data;
    const raw =
        (data?.ErrorDetails as unknown) ??
        (data?.errorDetails as unknown) ??
        (Array.isArray(data) ? data : null);
    if (Array.isArray(raw) && raw.length > 0) {
        const details: SfdcApiErrorDetail[] = raw.map((el: Record<string, unknown>) => ({
            objectName: el?.objectName != null ? String(el.objectName) : '',
            name: el?.name != null ? String(el.name) : '',
            message: el?.message != null ? String(el.message) : '',
        }));
        const fallbackMessage = details.map((d) => `${d.objectName} > ${d.name} > ${d.message}`).join(', ');
        return { details, fallbackMessage };
    }
    const msg =
        e instanceof Error
            ? e.message
            : ax?.message
              ? String(ax.message)
              : typeof e === 'string'
                ? e
                : 'Request failed';
    return { details: [], fallbackMessage: msg };
}

function uniqueSfdcErrorObjectNames(details: SfdcApiErrorDetail[]): string[] {
    const out: string[] = [];
    for (const d of details) {
        const on = d.objectName.trim();
        if (on && !out.includes(on)) out.push(on);
    }
    return out;
}

/** Map bundle step → `FinancePartnerCreateLeadProgressModal` step modifier classes. */
function sfdcBundleStepProgressClass(status: SfdcBundleStep['status']): 'pending' | 'syncing' | 'success' | 'error' {
    switch (status) {
        case 'running':
            return 'syncing';
        case 'done':
            return 'success';
        case 'error':
            return 'error';
        default:
            return 'pending';
    }
}

/** SFDC bundle: success = tick in solid green circle (see AgreementsModal.css); else match finance progress modal. */
function getSfdcBundleStatusIcon(status: SfdcBundleStep['status']) {
    switch (status) {
        case 'running':
            return <Spin size="small" />;
        case 'done':
            return (
                <Check size={14} weight="bold" className="obd-agreements-modal__sfdc-step-check" aria-hidden />
            );
        case 'error':
            return <Warning size={22} weight="fill" className="sync-icon sync-icon--error" aria-hidden />;
        default:
            return <Circle size={22} weight="regular" className="sync-icon sync-icon--pending" aria-hidden />;
    }
}

/** Trinity `getSyncStatusList` → `ContractCreationStatuses` row for this finance-partner quote. */
function findContractCreationRow(syncRes: unknown, quoteId: string): Record<string, unknown> | null {
    const o = syncRes as Record<string, unknown> | null;
    const list = (o?.ContractCreationStatuses ?? o?.contractCreationStatuses) as unknown;
    if (!Array.isArray(list) || !quoteId) return null;
    const q = String(quoteId);
    const hit = list.find(
        (c: unknown) =>
            String((c as Record<string, unknown>)?.Finance_Partner_Quote_Id ?? (c as Record<string, unknown>)?.QuoteId ?? '') === q,
    );
    return (hit as Record<string, unknown>) || null;
}

function isSfdcRowFullySynced(cr: Record<string, unknown> | null): boolean {
    if (!cr) return false;
    return !!(cr.SFDC_Lead_Is_Sync && cr.SFDC_Contact_Is_Sync && cr.SFDC_Contract_Is_Sync);
}

function canOpenGoodLeapInPerson(row: AgreementRow, opportunityId: string | number | null | undefined): boolean {
    const opp = opportunityId != null && String(opportunityId).trim() !== '';
    return (
        opp &&
        (!!row.contractIdRaw ||
            !!pickGoodLeapApplicationUrl(row.loanStatus) ||
            !!(row.externalLink && /^https?:\/\//i.test(String(row.externalLink).trim())))
    );
}

/** Trinity `detail-cell-renderer.updateIGSApplicationStatus` / `OpportunityDetails.Finance_Partner_Sync_LeadID`. */
function financePartnerSyncLeadIdFromOpportunity(od: unknown): string {
    const o = od as Record<string, unknown> | null | undefined;
    if (!o) return '';
    const nested = o.OpportunityDetails as Record<string, unknown> | undefined;
    return String(o.Finance_Partner_Sync_LeadID ?? nested?.Finance_Partner_Sync_LeadID ?? '').trim();
}

/** Trinity `detail-cell-renderer.displayCancelApplicationButton`. */
function goodLeapDisplayCancelApplicationButton(
    getCapabilityValue: (name: string) => string,
    row: AgreementRow,
): boolean {
    const ls = readLoanStatusRecord(row.loanStatus);
    if (!Object.keys(ls).length || !(ls.isActive ?? ls.IsActive)) return false;

    const canCx = ls.canContractBeCancelled ?? ls.CanContractBeCancelled;
    if (canCx !== true && !/^(true|yes|1)$/i.test(String(canCx ?? '').trim())) return false;

    const appId = ls.Application_Id ?? ls.application_Id ?? ls.ApplicationID ?? ls.applicationId;
    const appSt = ls.Application_Status ?? ls.application_Status ?? ls.applicationStatus ?? ls.ApplicationStatus;
    if (!String(appId ?? '').trim() || !String(appSt ?? '').trim()) return false;

    const cap =
        String(appSt).toLowerCase().includes('signed')
            ? 'GoodLeap_Cancel_Contract_After_Signing'
            : 'GoodLeap_Cancel_Contract';
    return oneButtonCapabilityRawAllows(getCapabilityValue(cap));
}

function isGlOrEnfinPartner(kind: AgreementPartnerKind): boolean {
    return kind === 'goodLeap' || kind === 'enfin';
}

/** Trinity `detail-cell-renderer.enfinCancelApplicationHiddenStatuses`. */
const ENFIN_CANCEL_APPLICATION_HIDDEN_STATUSES = [
    'M2 SUBMITTED',
    'READY FOR M3',
    'M3 SUBMITTED',
    'M3 COMPLETED',
    'READY FOR M2',
] as const;

/** Trinity `detail-cell-renderer.cancelReasons`. */
const ENFIN_CANCEL_REASONS = [
    { label: 'Incorrect Information Provided', value: 'Incorrect Information Provided' },
    { label: 'Homeowner Decision', value: 'Homeowner Decision' },
    { label: 'Non-English Speaking', value: 'Non-English Speaking' },
    { label: 'Add-on System', value: 'Add-on System' },
    { label: 'Partner Installer Requested Cancellation', value: 'Partner Installer Requested Cancellation' },
] as const;

function shouldHideEnfinCancelApplication(applicationStatus: string): boolean {
    const normalizedStatus = String(applicationStatus ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
    return (ENFIN_CANCEL_APPLICATION_HIDDEN_STATUSES as readonly string[]).includes(normalizedStatus);
}

/** Trinity `detail-cell-renderer.displayCancelApplicationButton` — Enfin branch. */
function enfinDisplayCancelApplicationButton(
    getCapabilityValue: (name: string) => string,
    row: AgreementRow,
): boolean {
    const ls = readLoanStatusRecord(row.loanStatus);
    if (!Object.keys(ls).length || !(ls.isActive ?? ls.IsActive)) return false;

    const canCx = ls.canContractBeCancelled ?? ls.CanContractBeCancelled;
    if (canCx !== true && !/^(true|yes|1)$/i.test(String(canCx ?? '').trim())) return false;

    const appId = ls.Application_Id ?? ls.application_Id ?? ls.ApplicationID ?? ls.applicationId;
    const appSt = ls.Application_Status ?? ls.application_Status ?? ls.applicationStatus ?? ls.ApplicationStatus;
    if (!String(appId ?? '').trim() || !String(appSt ?? '').trim()) return false;

    if (shouldHideEnfinCancelApplication(String(appSt))) return false;

    return oneButtonCapabilityRawAllows(getCapabilityValue('Enfin_Cancelproject'));
}

function displayCancelApplicationButton(
    getCapabilityValue: (name: string) => string,
    row: AgreementRow,
): boolean {
    const kind = detectAgreementPartnerKind(row);
    if (kind === 'enfin') return enfinDisplayCancelApplicationButton(getCapabilityValue, row);
    if (kind === 'goodLeap') return goodLeapDisplayCancelApplicationButton(getCapabilityValue, row);
    return false;
}

function partnerKindAbbrev(kind: AgreementPartnerKind): string {
    switch (kind) {
        case 'goodLeap':
            return 'GL';
        case 'enfin':
            return 'EF';
        case 'sunlight':
            return 'SL';
        case 'sunnova':
            return 'SN';
        case 'cash':
            return 'Cash';
        case 'lightreach':
            return 'LR';
        case 'igs':
            return 'IGS';
        case 'serviceFinance':
            return 'Svc';
        default:
            return 'FP';
    }
}

function readLoanStatusRecord(ls: unknown): Record<string, unknown> {
    if (ls == null || typeof ls !== 'object') return {};
    return mergeApiShape(ls as Record<string, unknown>);
}

function goodLeapLoanApplicationStatus(row: AgreementRow): string | null {
    const o = readLoanStatusRecord(row.loanStatus);
    const v = o.Application_Status ?? o.application_Status;
    return v != null && String(v).trim() ? String(v).trim() : null;
}

/** Trinity `LoanStatus`: inactive only when `isActive === false` (null/undefined counts as active). */
function goodLeapLoanIsActive(row: AgreementRow): boolean {
    const o = readLoanStatusRecord(row.loanStatus);
    const a = o.isActive ?? o.IsActive;
    if (a === false) return false;
    return true;
}

/** Trinity Good Leap expand — In-person / Send email when loan is active and in pending-style statuses. */
function computeShowGoodLeapAppButtons(row: AgreementRow): boolean {
    const kind = detectAgreementPartnerKind(row);
    if (!isGlOrEnfinPartner(kind)) return false;
    if (!Object.keys(readLoanStatusRecord(row.loanStatus)).length) return false;
    if (!goodLeapLoanIsActive(row)) return false;
    const st = String(goodLeapLoanApplicationStatus(row) || '').trim();
    return st === 'Pending' || st === 'AccountSubmitted' || st === 'ApplicationSubmitted';
}

function sunlightApplicationStatus(row: AgreementRow): string | null {
    const o = readLoanStatusRecord(row.loanStatus);
    const v = o.Application_Status ?? o.application_Status;
    return v != null && String(v).trim() ? String(v).trim() : null;
}

function sunlightCreditStatus(row: AgreementRow): string {
    const o = readLoanStatusRecord(row.loanStatus);
    const fromLoan = o.CreditStatus ?? o.creditStatus;
    if (fromLoan != null && String(fromLoan).trim()) return String(fromLoan).trim();
    return row.creditStatusRaw || '';
}

function sunlightApplicationLink(row: AgreementRow): string | null {
    const o = readLoanStatusRecord(row.loanStatus);
    const raw = o.Link ?? o.link;
    if (typeof raw === 'string' && /^https?:\/\//i.test(raw.trim())) return raw.trim();
    return pickGoodLeapApplicationUrl(row.loanStatus);
}

function loanStatusText(row: AgreementRow): string | null {
    const o = readLoanStatusRecord(row.loanStatus);
    const v = o.finalStatus ?? o.FinalStatus ?? o.status ?? o.Status;
    return v != null && String(v).trim() ? String(v).trim() : null;
}

function loanSubmitStatusList(row: AgreementRow): string[] {
    const o = readLoanStatusRecord(row.loanStatus);
    const list = o.submitStatus ?? o.SubmitStatus;
    if (Array.isArray(list)) return list.map((x) => String(x ?? '').trim()).filter(Boolean);
    return [];
}

function loanApplicationId(row: AgreementRow): string | null {
    const o = readLoanStatusRecord(row.loanStatus);
    const fromLoan = [
        o.Application_Id,
        o.application_Id,
        o.ApplicationID,
        o.applicationId,
        o.LoanNumber,
        o.loanNumber,
        o.Loan_Number,
        o.Loan_Id,
        o.loan_Id,
    ];
    for (const v of fromLoan) {
        if (v != null && String(v).trim()) return String(v).trim();
    }
    const fromQuote = String(row.applicationIdRaw ?? '').trim();
    if (fromQuote) return fromQuote;
    return null;
}

function loanReferenceNumber(row: AgreementRow): string | null {
    const o = readLoanStatusRecord(row.loanStatus);
    const v = o.Reference_Number ?? o.reference_Number ?? o.ReferenceNumber ?? o.referenceNumber;
    return v != null && String(v).trim() ? String(v).trim() : null;
}

function sunlightProjectId(row: AgreementRow): string | null {
    const o = readLoanStatusRecord(row.loanStatus);
    const v = o.Application_Id ?? o.application_Id ?? o.ProjectId ?? o.projectId;
    return v != null && String(v).trim() ? String(v).trim() : null;
}

function sunlightLoanCreditStatus(row: AgreementRow): string | null {
    const o = readLoanStatusRecord(row.loanStatus);
    const v = o.CreditStatus ?? o.creditStatus;
    return v != null && String(v).trim() ? String(v).trim() : null;
}

function sunlightSyncLeadId(row: AgreementRow): string | null {
    const o = readLoanStatusRecord(row.loanStatus);
    const v =
        o.SyncLeadId ??
        o.syncLeadId ??
        o.syncLeadID ??
        o.Sync_Lead_Id ??
        o.sync_Lead_Id ??
        o.Application_Id ??
        o.application_Id;
    const s = v != null ? String(v).trim() : '';
    if (s) return s;
    if (row.leadIdRaw && String(row.leadIdRaw).trim()) return String(row.leadIdRaw).trim();
    const appId = loanApplicationId(row);
    return appId && appId.trim() ? appId.trim() : null;
}

function shouldShowUpload(row: AgreementRow, type: 'Utility' | 'System Design' | 'Document Sent'): boolean {
    const o = readLoanStatusRecord(row.loanStatus);
    const appId = loanApplicationId(row) ?? o.Application_Id ?? o.application_Id;
    if (!appId || !String(appId).trim()) return false;
    const submitStatus = (o.submitStatus ?? o.SubmitStatus) as unknown;
    const list = Array.isArray(submitStatus) ? submitStatus : [];
    const lowerIncludes = (s: unknown, needle: string) => String(s ?? '').toLowerCase().includes(needle);
    switch (type) {
        case 'Utility':
            return list.some((s) => (lowerIncludes(s, 'failed') || lowerIncludes(s, 'pending')) && lowerIncludes(s, 'utility'));
        case 'System Design':
            return list.some(
                (s) => (lowerIncludes(s, 'failed') || lowerIncludes(s, 'pending')) && lowerIncludes(s, 'system design'),
            );
        case 'Document Sent':
            return list.some((s) => (lowerIncludes(s, 'failed') || lowerIncludes(s, 'pending')) && lowerIncludes(s, 'document'));
        default:
            return false;
    }
}

function hideUploadAndResync(row: AgreementRow): boolean {
    const o = readLoanStatusRecord(row.loanStatus);
    const status = String(o.Application_Status ?? o.application_Status ?? '').trim();
    const hidden = new Set([
        'AccountDeclined',
        'Pending',
        'LoanDeclined',
        'Credit Decline',
        'ProjectVerificationFailed',
        'CompletedProjectCertificateDeclined',
        'AccountSubmitted',
        'AccountPending',
    ]);
    return hidden.has(status);
}

/** Good Leap submitStatus line → which sync column(s) Angular binds icons to (`detail-cell-renderer.component.html`). */
type GoodLeapSyncColumnKind = 'Utility' | 'System Design' | 'Document Sent';

function goodLeapSubmitLineMatchesSyncColumn(statusText: string, kind: GoodLeapSyncColumnKind): boolean {
    if (kind === 'Utility') {
        return statusText.includes('Utility') || statusText.toLowerCase().includes('utility');
    }
    if (kind === 'System Design') {
        return statusText.includes('System Design') || statusText.toLowerCase().includes('system design');
    }
    const l = statusText.toLowerCase();
    return l.includes('document') || l.includes('loan documents');
}

/** Fallback when line text is not clearly success/fail/pending — mirrors Angular `contractLoanStatus` flags. */
function goodLeapLoanFieldIndicatesSynced(o: Record<string, unknown>, kind: GoodLeapSyncColumnKind): boolean {
    switch (kind) {
        case 'Utility':
            return String(o.Utility_Sync_Status ?? o.utility_Sync_Status ?? '')
                .toLowerCase()
                .includes('success');
        case 'System Design':
            return String(o.System_Design_Sync_Status ?? o.system_Design_Sync_Status ?? '')
                .toLowerCase()
                .includes('success');
        case 'Document Sent': {
            const t = String(o.Document_Sent_Status ?? o.document_Sent_Status ?? '').toLowerCase();
            return t.includes('success') && !t.includes('failed');
        }
        default:
            return false;
    }
}

/**
 * Trinity `DetailCellRendererComponent.getSyncStatus` — per-line icons only for Utility / System Design / Document rows.
 */
function getGoodLeapSyncStatusForLine(
    statusText: string,
    kind: GoodLeapSyncColumnKind,
    row: AgreementRow,
    busyTag: string,
    actionBusyKey: string | null,
): 'success' | 'retry' | 'loading' | null {
    const lower = statusText.toLowerCase();
    if (actionBusyKey === `${row.key}:${busyTag}`) return 'loading';
    if (lower.includes('successful')) return 'success';
    if (lower.includes('failed') || lower.includes('pending')) return 'retry';
    const o = readLoanStatusRecord(row.loanStatus);
    return goodLeapLoanFieldIndicatesSynced(o, kind) ? 'success' : null;
}

function goodLeapSyncBusyTag(kind: GoodLeapSyncColumnKind): string {
    switch (kind) {
        case 'Utility':
            return 'gl-sync-utility';
        case 'System Design':
            return 'gl-sync-sd';
        default:
            return 'gl-sync-doc';
    }
}

function renderGoodLeapSubmitLineSyncIcons(args: {
    statusText: string;
    row: AgreementRow;
    appId: string | null;
    hideUpload: boolean;
    actionBusyKey: string | null;
    opportunityId: string | number | null | undefined;
    wrap: (tag: string, fn: () => Promise<void>) => Promise<void>;
    load: () => Promise<void>;
    pushNotice: AgreementNoticePush;
    syncHooks?: GoodLeapLoanSyncHooks;
}): React.ReactNode {
    const { statusText, row, appId, hideUpload, actionBusyKey, opportunityId, wrap, load, pushNotice, syncHooks } =
        args;
    const financePartner = row.financePartnerRaw || row.financePartner;
    if (!goodLeapLoanIsActive(row) || !appId || hideUpload) return null;

    const kinds: GoodLeapSyncColumnKind[] = ['Utility', 'System Design', 'Document Sent'];
    const nodes: React.ReactNode[] = [];

    for (const kind of kinds) {
        if (!goodLeapSubmitLineMatchesSyncColumn(statusText, kind)) continue;
        const busyTag = goodLeapSyncBusyTag(kind);
        const st = getGoodLeapSyncStatusForLine(statusText, kind, row, busyTag, actionBusyKey);
        if (!st) continue;

        const tooltip =
            kind === 'Utility'
                ? 'Retry Utility Sync'
                : kind === 'System Design'
                  ? 'Retry System Design Sync'
                  : 'Retry Sending Documents';

        const syncing = st === 'loading';
        if (syncing) {
            nodes.push(
                <Spin key={kind} size="small" className="obd-agreements-modal__loan-status-sync-spin" />,
            );
        } else if (st === 'retry') {
            nodes.push(
                <Tooltip key={kind} title={tooltip}>
                    <button
                        type="button"
                        className="obd-agreements-modal__loan-retry-btn"
                        aria-label={tooltip}
                        onClick={() =>
                            void wrap(busyTag, async () => {
                                if (!opportunityId || !appId) return;
                                const ok = await runGoodLeapLoanStageSync(
                                    kind,
                                    opportunityId,
                                    appId,
                                    financePartner,
                                    pushNotice,
                                    row.quoteId
                                );
                                if (ok) await load();
                            })
                        }
                    >
                        <ArrowsClockwise size={16} weight="bold" aria-hidden />
                    </button>
                </Tooltip>,
            );
        } else {
            nodes.push(
                <CheckCircle
                    key={kind}
                    size={18}
                    weight="fill"
                    className="obd-agreements-modal__loan-status-sync-ok"
                    aria-hidden
                />,
            );
        }
    }

    if (!nodes.length) return null;
    return <span className="obd-agreements-modal__loan-status-sync-icons">{nodes}</span>;
}

type GoodLeapLoanSyncStage = 'Utility' | 'System Design' | 'Document Sent';

/** Sets `actionBusyKey` while a loan-stage sync API runs (Trinity `isResyncing*` flags). */
function createLoanSyncBusyHandlers(
    rowKey: string,
    actionBusyKey: string | null,
    setActionBusyKey: React.Dispatch<React.SetStateAction<string | null>>,
) {
    const wrap = async (tag: string, fn: () => Promise<void>) => {
        setActionBusyKey(`${rowKey}:${tag}`);
        try {
            await fn();
        } finally {
            setActionBusyKey(null);
        }
    };
    const isBusy = (stage: GoodLeapLoanSyncStage) =>
        actionBusyKey === `${rowKey}:${goodLeapSyncBusyTag(stage)}`;
    return { wrap, isBusy };
}

function loanResyncFieldForStage(stage: GoodLeapLoanSyncStage): keyof LoanSyncResyncErrors {
    if (stage === 'Utility') return 'utility';
    if (stage === 'System Design') return 'systemDesign';
    return 'documentSent';
}

type GoodLeapLoanSyncHooks = {
    onStageSuccess?: (stage: GoodLeapLoanSyncStage) => void;
    onStageFailure?: (stage: GoodLeapLoanSyncStage, storageMessage: string) => void;
};

function goodLeapLoanSyncSuccessMessage(stage: GoodLeapLoanSyncStage): string {
    if (stage === 'Document Sent') return 'Document sent Successfully';
    if (stage === 'Utility') return 'Utility Resynced Successfully';
    return 'System Design Resynced Successfully';
}

function goodLeapLoanSyncFailureNotice(
    stage: GoodLeapLoanSyncStage,
    payload: unknown,
    financePartner: string,
): string {
    if (stage === 'Utility') {
        const raw =
            payload != null && typeof payload === 'object'
                ? String((payload as { Message?: string }).Message ?? '').trim()
                : '';
        return raw || 'Utility sync failed.';
    }
    return (
        goodLeapSyncErrorNotice(payload, financePartner) ||
        formatGoodLeapSyncErrorMessage(
            payload != null && typeof payload === 'object'
                ? String((payload as { Message?: string }).Message ?? '')
                : String(payload ?? ''),
        ) ||
        'Sync failed.'
    );
}

async function runGoodLeapLoanStageSync(
    stage: GoodLeapLoanSyncStage,
    opportunityId: string | number,
    appId: string,
    financePartner: string,
    pushNotice: AgreementNoticePush,
    quoteId: string,
    hooks?: GoodLeapLoanSyncHooks,
    
): Promise<boolean> {
    try {
        let res: { IsSuccessful?: boolean; Message?: string };
        if (stage === 'Utility') {
            res = (await contractService.goodLeapUpdateUtility(opportunityId, appId)) as {
                IsSuccessful?: boolean;
                Message?: string;
            };
        } else if (stage === 'System Design') {
            if (financePartner.toLocaleLowerCase() === 'enfin') {
                const raw = await contractService.createEnfinContract({
                    opportunityId,
                    applicationId: appId,
                    quoteId: quoteId || '',
                });
                if (raw) {
                    hooks?.onStageSuccess?.(stage);
                    pushNotice('success', goodLeapLoanSyncSuccessMessage(stage));
                    return true;
                }
                const storageMessage = formatGoodLeapResyncErrorStorage(stage, raw, financePartner);
                hooks?.onStageFailure?.(stage, storageMessage);
                pushNotice('error', goodLeapLoanSyncFailureNotice(stage, raw, financePartner));
                return false;
            } else {
                res = (await contractService.goodLeapUpdateSystemDesign(opportunityId, appId)) as {
                    IsSuccessful?: boolean;
                    Message?: string;
                };
            }
            
        } else {
            res = (await contractService.goodLeapSendLoanDocument(opportunityId, appId)) as {
                IsSuccessful?: boolean;
                Message?: string;
            };
        }
        if (res?.IsSuccessful) {
            hooks?.onStageSuccess?.(stage);
            pushNotice('success', goodLeapLoanSyncSuccessMessage(stage));
            return true;
        }
        const storageMessage = formatGoodLeapResyncErrorStorage(stage, res, financePartner);
        hooks?.onStageFailure?.(stage, storageMessage);
        pushNotice('error', goodLeapLoanSyncFailureNotice(stage, res, financePartner));
        return false;
    } catch (e: unknown) {
        const raw = contractApiErrorMessage(e);
        const payload = raw != null ? { Message: raw } : e;
        const storageMessage = formatGoodLeapResyncErrorStorage(stage, payload, financePartner);
        hooks?.onStageFailure?.(stage, storageMessage);
        pushNotice('error', goodLeapLoanSyncFailureNotice(stage, payload, financePartner));
        return false;
    }
}

function loanStageSyncContext(row: AgreementRow): { quoteId?: string; partnerKind?: AgreementPartnerKind } {
    return {
        quoteId: row.quoteIdRaw,
        partnerKind: detectAgreementPartnerKind(row),
    };
}

/** Phosphor stand-in for Trinity `SharedService.getStatusIcon` (Font Awesome) in submit-column renderers. */
function trinityStatusGlyph(status: string): React.ReactNode {
    const s = status || '';
    if (!s.trim()) {
        return (
            <Eye
                size={AGREEMENT_TOOL_ICON_PX}
                weight="duotone"
                className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--primary"
                aria-hidden
            />
        );
    }
    const t = s.trim();
    const lower = t.toLowerCase();
    if (lower.includes('signed') && !lower.includes('void')) {
        return (
            <Handshake
                size={AGREEMENT_TOOL_ICON_PX}
                weight="duotone"
                className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--success"
                aria-hidden
            />
        );
    }
    if (lower.includes('canceled') || lower.includes('voided')) {
        return (
            <Prohibit
                size={AGREEMENT_TOOL_ICON_PX}
                weight="duotone"
                className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--danger"
                aria-hidden
            />
        );
    }
    if (lower.includes('decline') || lower.includes('denied')) {
        return (
            <WarningCircle
                size={AGREEMENT_TOOL_ICON_PX}
                weight="duotone"
                className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--danger"
                aria-hidden
            />
        );
    }
    /** Angular `SharedService.getStatusIcon` — envelope / viewed buckets (case-insensitive for `Opportunity_Contract_Status`). */
    const envelopeStatuses = new Set([
        'documents/agreement sent',
        'agreement sent',
        'docssubmitted',
        'docssent',
        'agreementsent',
        'sent',
        'submitted',
        'accountsubmitted',
        'applicationsubmitted',
        'docsrequested',
        'agreementrequested',
    ]);
    if (envelopeStatuses.has(lower)) {
        return (
            <EnvelopeSimple
                size={AGREEMENT_TOOL_ICON_PX}
                weight="duotone"
                className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--success"
                aria-hidden
            />
        );
    }
    const viewedStatuses = new Set([
        'documents/agreement viewed',
        'agreement viewed',
        'docsviewed',
        'agreementviewed',
    ]);
    if (viewedStatuses.has(lower)) {
        return (
            <Article
                size={AGREEMENT_TOOL_ICON_PX}
                weight="duotone"
                className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--primary"
                aria-hidden
            />
        );
    }
    switch (t) {
        case 'Submit Loan Application':
        case 'Submit Document':
        case 'Submit Application':
        case 'Generated':
            return (
                <PencilSimple
                    size={AGREEMENT_TOOL_ICON_PX}
                    weight="regular"
                    className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--primary"
                    aria-hidden
                />
            );
        case 'Pending':
            return (
                <Hourglass
                    size={AGREEMENT_TOOL_ICON_PX}
                    weight="duotone"
                    className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--warning"
                    aria-hidden
                />
            );
        case 'NEW CREDIT APPLICATION':
            return (
                <PencilSimple
                    size={AGREEMENT_TOOL_ICON_PX}
                    weight="regular"
                    className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--primary"
                    aria-hidden
                />
            );
        case 'Approved':
        case 'Credit Approval':
        case 'LoanApproved':
        case 'CreditApproval':
        case 'AccountApproved':
        case 'ProjectDescriptionChanged':
            return (
                <CheckCircle
                    size={AGREEMENT_TOOL_ICON_PX}
                    weight="duotone"
                    className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--success"
                    aria-hidden
                />
            );
        case 'Declined':
        case 'DECLINED':
        case 'Rejected':
        case 'Credit Decline':
        case 'LoanDeclined':
        case 'AccountDeclined':
        case 'Denied':
            return (
                <WarningCircle
                    size={AGREEMENT_TOOL_ICON_PX}
                    weight="duotone"
                    className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--danger"
                    aria-hidden
                />
            );
        case 'Cancelled':
        case 'Voided':
        case 'DocsVoided':
        case 'AccountCanceled':
        case 'AgreementVoided':
        case 'LoanCanceled':
            return (
                <Prohibit
                    size={AGREEMENT_TOOL_ICON_PX}
                    weight="duotone"
                    className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--danger"
                    aria-hidden
                />
            );
        default:
            return (
                <Eye
                    size={AGREEMENT_TOOL_ICON_PX}
                    weight="duotone"
                    className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--primary"
                    aria-hidden
                />
            );
    }
}

/**
 * Trinity `detail-cell-renderer.component.html` — Good Leap Loan Number: read-only when `Application_Id` is set and
 * status is not Pending / AccountSubmitted / ApplicationSubmitted; otherwise input + Get Credit Status (same as Angular).
 */
function GoodLeapLoanNumberExpand({
    row,
    opportunityId,
    onRefresh,
    enableGetCreditStatusView,
    pushNotice,
}: {
    row: AgreementRow;
    opportunityId: string | number | null | undefined;
    onRefresh: () => Promise<void>;
    /** Angular `getCapability('Enable_GoodLeap_Get_Credit_Status') === 'View'` for pending credit input + button. */
    enableGetCreditStatusView: boolean;
    pushNotice: AgreementNoticePush;
}) {
    const o = readLoanStatusRecord(row.loanStatus);
    if (!Object.keys(o).length || !goodLeapLoanIsActive(row)) return null;

    const appStatus = String(goodLeapLoanApplicationStatus(row) ?? '').trim();
    const pendingCredit = new Set(['Pending', 'AccountSubmitted', 'ApplicationSubmitted']);
    const isPendingCreditFlow = pendingCredit.has(appStatus);

    const resolved = loanApplicationId(row);

    const [text, setText] = React.useState(resolved ?? '');
    const [busy, setBusy] = React.useState(false);

    React.useEffect(() => {
        setText(resolved ?? '');
    }, [row.key, resolved]);

    if (!isPendingCreditFlow && resolved) {
        return (
            <div className="obd-agreements-modal__detail-kv">
                <span className="obd-agreements-modal__detail-k">Loan Number</span>
                <span className="obd-agreements-modal__detail-v">{resolved}</span>
            </div>
        );
    }

    if (isPendingCreditFlow) {
        if (!enableGetCreditStatusView) {
            return (
                <div className="obd-agreements-modal__detail-kv">
                    <span className="obd-agreements-modal__detail-k">Loan Number</span>
                    <span className="obd-agreements-modal__detail-v">{dash(text.trim() || resolved)}</span>
                </div>
            );
        }
        const canSubmit =
            !!opportunityId &&
            String(opportunityId).trim() !== '' &&
            !!row.contractIdRaw &&
            !!(text.trim() || resolved);
        return (
            <div className="obd-agreements-modal__detail-kv">
                <span className="obd-agreements-modal__detail-k">Loan Number</span>
                <span className="obd-agreements-modal__detail-v">
                    <div className="obd-agreements-modal__goodleap-loan-row">
                        <Input
                            className="obd-agreements-modal__goodleap-loan-input"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            aria-label="GoodLeap loan application ID"
                        />
                        <Button
                            type="primary"
                            size="small"
                            loading={busy}
                            disabled={!canSubmit}
                            onClick={async () => {
                                const appId = text.trim() || resolved;
                                if (!appId || !opportunityId || !row.contractIdRaw) return;
                                setBusy(true);
                                try {
                                    await contractService.goodLeapApplicationCreditStatus(
                                        opportunityId,
                                        appId,
                                        row.contractIdRaw,
                                    );
                                    pushNotice('success', 'Credit status updated.');
                                    await onRefresh();
                                } catch (e: unknown) {
                                    const ax = e as { response?: { data?: { Message?: string } }; message?: string };
                                    pushNotice(
                                        'error',
                                        ax?.response?.data?.Message ?? ax?.message ?? 'Get credit status failed.',
                                    );
                                } finally {
                                    setBusy(false);
                                }
                            }}
                        >
                            Get Credit Status
                        </Button>
                    </div>
                </span>
            </div>
        );
    }

    return null;
}

/** Trinity `good-leap-renderer.component.html` — pen submit, then status / refresh by `Application_Status`. */
function GoodLeapSubmitApplicationCell({
    row,
    opportunityId,
    actionBusyKey,
    setActionBusyKey,
    openGoodLeapSubmitApplication,
    submitGoodLeapChangeOrder,
    refreshGoodLeapLoanStatus,
    openGoodLeapApplicationIframe,
    busyPrefix = 'gl',
    partnerName = 'GoodLeap',
}: {
    row: AgreementRow;
    opportunityId: string | number | null | undefined;
    actionBusyKey: string | null;
    setActionBusyKey: (k: string | null) => void;
    openGoodLeapSubmitApplication: (row: AgreementRow) => Promise<void>;
    submitGoodLeapChangeOrder: (row: AgreementRow) => Promise<void>;
    refreshGoodLeapLoanStatus: (row: AgreementRow) => Promise<void>;
    openGoodLeapApplicationIframe: (url: string) => void;
    busyPrefix?: string;
    partnerName?: string;
}) {
    const wrap = async (tag: string, fn: () => Promise<void>) => {
        const k = `${row.key}:${tag}`;
        setActionBusyKey(k);
        try {
            await fn();
        } finally {
            setActionBusyKey(null);
        }
    };
    const opp = opportunityId != null && String(opportunityId).trim() !== '';
    const canApiGl = opp && !!row.contractIdRaw;
    const spin = (tag: string) => actionBusyKey === `${row.key}:${tag}`;
    const isEnfinPartner = partnerName === 'Enfin';

    if (row.isQuoteChangeOrder) {
        if (!goodLeapLoanIsActive(row)) {
            /** Trinity `good-leap-renderer.component.html` — `!LoanStatus?.isActive` → voided icon (not the submit button). */
            return (
                <div className="obd-agreements-modal__angular-grid-cell">
                    <Tooltip title={goodLeapLoanApplicationStatus(row) || 'Voided'}>
                        <span
                            className="obd-agreements-modal__icon-btn obd-agreements-modal__icon-btn--static"
                            aria-label={goodLeapLoanApplicationStatus(row) || 'Voided'}
                        >
                            {trinityStatusGlyph('Voided')}
                        </span>
                    </Tooltip>
                </div>
            );
        }
        return (
            <div className="obd-agreements-modal__angular-grid-cell">
                <Tooltip title="Change Order">
                    <Button
                        type="primary"
                        size="small"
                        className="obd-agreements-modal__submit-change-order-btn"
                        loading={spin(`${busyPrefix}-change-order`)}
                        disabled={!opp || !row.contractIdRaw}
                        onClick={() => void submitGoodLeapChangeOrder(row)}
                        icon={
                            <ArrowsLeftRight
                                size={14}
                                weight="bold"
                                className="obd-agreements-modal__submit-change-order-btn__icon"
                                aria-hidden
                            />
                        }
                    >
                        Submit Change Order
                    </Button>
                </Tooltip>
            </div>
        );
    }

    const app = goodLeapLoanApplicationStatus(row);
    const active = goodLeapLoanIsActive(row);

    if (!app) {
        return (
            <div className="obd-agreements-modal__icon-strip obd-agreements-modal__icon-strip--submit">
                <Tooltip title="Submit Application">
                    <button
                        type="button"
                        className="obd-agreements-modal__icon-btn"
                        disabled={!opp || !row.contractIdRaw}
                        aria-label="Submit Application"
                        onClick={() => void wrap(`${busyPrefix}-submit`, () => openGoodLeapSubmitApplication(row))}
                    >
                        {spin(`${busyPrefix}-submit`) ? (
                            <AgreementIconSpin />
                        ) : (
                            <PencilSimple
                                size={AGREEMENT_TOOL_ICON_PX}
                                weight="regular"
                                className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--primary"
                                aria-hidden
                            />
                        )}
                    </button>
                </Tooltip>
            </div>
        );
    }

    if (app === 'Signed') {
        return (
            <div className="obd-agreements-modal__angular-grid-cell">
                <Tooltip title={app}>
                    <span className="obd-agreements-modal__icon-btn obd-agreements-modal__icon-btn--static" aria-label={app}>
                        {trinityStatusGlyph(app)}
                    </span>
                </Tooltip>
            </div>
        );
    }

    if (!active) {
        return (
            <div className="obd-agreements-modal__angular-grid-cell">
                <Tooltip title={app}>
                    <span className="obd-agreements-modal__icon-btn obd-agreements-modal__icon-btn--static" aria-label={app}>
                        {trinityStatusGlyph('Voided')}
                    </span>
                </Tooltip>
            </div>
        );
    }

    if (app === 'Pending' && active) {
        const url = pickGoodLeapApplicationUrl(row.loanStatus);
        return (
            <div className="obd-agreements-modal__icon-strip obd-agreements-modal__icon-strip--submit">
                <Tooltip title={app}>
                    <button
                        type="button"
                        className="obd-agreements-modal__icon-btn"
                        disabled={!url}
                        aria-label={`${app} — open application`}
                        onClick={() => {
                            if (url) openGoodLeapApplicationIframe(url);
                        }}
                    >
                        {trinityStatusGlyph(app)}
                    </button>
                </Tooltip>
                <Tooltip title={canApiGl ? `Refresh loan status from ${partnerName}` : 'Need contract id on this row to refresh'}>
                    <button
                        type="button"
                        className="obd-agreements-modal__icon-btn"
                        disabled={!canApiGl}
                        aria-label={`Refresh ${partnerName} loan status`}
                        onClick={() => void wrap(`${busyPrefix}-loan-refresh`, () => refreshGoodLeapLoanStatus(row))}
                    >
                        {spin(`${busyPrefix}-loan-refresh`) ? (
                            <AgreementIconSpin />
                        ) : (
                            <ArrowsClockwise size={AGREEMENT_TOOL_ICON_PX} weight="bold" className="obd-agreements-modal__ag-icon" aria-hidden />
                        )}
                    </button>
                </Tooltip>
            </div>
        );
    }

    /** Trinity `good-leap-renderer` — Enfin-only: `NEW CREDIT APPLICATION` opens iframe + refresh. */
    if (app === 'NEW CREDIT APPLICATION' && active) {
        if (!isEnfinPartner) return null;
        const url = pickGoodLeapApplicationUrl(row.loanStatus);
        return (
            <div className="obd-agreements-modal__icon-strip obd-agreements-modal__icon-strip--submit">
                <Tooltip title={app}>
                    <button
                        type="button"
                        className="obd-agreements-modal__icon-btn"
                        disabled={!url}
                        aria-label={`${app} — open application`}
                        onClick={() => {
                            if (url) openGoodLeapApplicationIframe(url);
                        }}
                    >
                        {trinityStatusGlyph(app)}
                    </button>
                </Tooltip>
                <Tooltip title={canApiGl ? `Refresh loan status from ${partnerName}` : 'Need contract id on this row to refresh'}>
                    <button
                        type="button"
                        className="obd-agreements-modal__icon-btn"
                        disabled={!canApiGl}
                        aria-label={`Refresh ${partnerName} loan status`}
                        onClick={() => void wrap(`${busyPrefix}-loan-refresh`, () => refreshGoodLeapLoanStatus(row))}
                    >
                        {spin(`${busyPrefix}-loan-refresh`) ? (
                            <AgreementIconSpin />
                        ) : (
                            <ArrowsClockwise size={AGREEMENT_TOOL_ICON_PX} weight="bold" className="obd-agreements-modal__ag-icon" aria-hidden />
                        )}
                    </button>
                </Tooltip>
            </div>
        );
    }

    if (app === 'Denied' && active) {
        return (
            <div className="obd-agreements-modal__icon-strip obd-agreements-modal__icon-strip--submit">
                <Tooltip title={app}>
                    <span className="obd-agreements-modal__icon-btn obd-agreements-modal__icon-btn--static" aria-label={app}>
                        {trinityStatusGlyph(app)}
                    </span>
                </Tooltip>
                <Tooltip title={canApiGl ? `Refresh loan status from ${partnerName}` : 'Need contract id on this row to refresh'}>
                    <button
                        type="button"
                        className="obd-agreements-modal__icon-btn"
                        disabled={!canApiGl}
                        aria-label={`Refresh ${partnerName} loan status`}
                        onClick={() => void wrap(`${busyPrefix}-loan-refresh`, () => refreshGoodLeapLoanStatus(row))}
                    >
                        {spin(`${busyPrefix}-loan-refresh`) ? (
                            <AgreementIconSpin />
                        ) : (
                            <ArrowsClockwise size={AGREEMENT_TOOL_ICON_PX} weight="bold" className="obd-agreements-modal__ag-icon" aria-hidden />
                        )}
                    </button>
                </Tooltip>
            </div>
        );
    }

    return (
        <div className="obd-agreements-modal__icon-strip obd-agreements-modal__icon-strip--submit">
            <Tooltip title={app}>
                <span className="obd-agreements-modal__icon-btn obd-agreements-modal__icon-btn--static" aria-label={app}>
                    {trinityStatusGlyph(app)}
                </span>
            </Tooltip>
            <Tooltip title={canApiGl ? `Refresh loan status from ${partnerName}` : 'Need contract id on this row to refresh'}>
                <button
                    type="button"
                    className="obd-agreements-modal__icon-btn"
                    disabled={!canApiGl}
                    aria-label={`Refresh ${partnerName} loan status`}
                    onClick={() => void wrap(`${busyPrefix}-loan-refresh`, () => refreshGoodLeapLoanStatus(row))}
                >
                    {spin(`${busyPrefix}-loan-refresh`) ? (
                        <AgreementIconSpin />
                    ) : (
                        <ArrowsClockwise size={AGREEMENT_TOOL_ICON_PX} weight="bold" className="obd-agreements-modal__ag-icon" aria-hidden />
                    )}
                </button>
            </Tooltip>
        </div>
    );
}

/** Trinity `sunlight-renderer.component.html` — pen + refresh; status row opens iframe modal (`Link`). */
function SunlightPartnerSubmitCell({
    row,
    opportunityId,
    actionBusyKey,
    setActionBusyKey,
    openSignPartnerOrExternal,
    refreshAgreementRow,
    openSunlightApplicationIframe,
}: {
    row: AgreementRow;
    opportunityId: string | number | null | undefined;
    actionBusyKey: string | null;
    setActionBusyKey: (k: string | null) => void;
    openSignPartnerOrExternal: (row: AgreementRow) => Promise<void>;
    refreshAgreementRow: (row: AgreementRow) => Promise<void>;
    openSunlightApplicationIframe: (url: string) => void;
}) {
    const wrap = async (tag: string, fn: () => Promise<void>) => {
        const k = `${row.key}:${tag}`;
        setActionBusyKey(k);
        try {
            await fn();
        } finally {
            setActionBusyKey(null);
        }
    };
    const opp = opportunityId != null && String(opportunityId).trim() !== '';
    const canPortal =
        !!row.externalLink ||
        (opp && !!row.leadIdRaw && !!row.contractIdRaw && !!row.quoteIdRaw);
    /** Trinity `sunlight-renderer` refresh → `GenerateContractService.getLoanStatus(opportunityId, ContractId)` — not `AgreementId`. */
    const canRefresh = opp && !!row.contractIdRaw;
    const spin = (tag: string) => actionBusyKey === `${row.key}:${tag}`;

    const app = sunlightApplicationStatus(row);
    /** Trinity template: `CreditStatus === 'Credit Approval'`; decline uses `Application_Status === 'Credit Decline'`. */
    const credit = sunlightCreditStatus(row);

    if (!app) {
        return (
            <div className="obd-agreements-modal__icon-strip obd-agreements-modal__icon-strip--submit">
                <Tooltip title="Submit Application">
                    <button
                        type="button"
                        className="obd-agreements-modal__icon-btn"
                        disabled={!canPortal}
                        aria-label="Submit Application"
                        onClick={() => void wrap('sl-submit', () => openSignPartnerOrExternal(row))}
                    >
                        {spin('sl-submit') ? (
                            <AgreementIconSpin />
                        ) : (
                            <PencilSimple
                                size={AGREEMENT_TOOL_ICON_PX}
                                weight="regular"
                                className="obd-agreements-modal__ag-icon obd-agreements-modal__status-icon--primary"
                                aria-hidden
                            />
                        )}
                    </button>
                </Tooltip>
                <Tooltip title={canRefresh ? 'Refresh loan status from server' : 'Need contract id on this row to refresh'}>
                    <button
                        type="button"
                        className="obd-agreements-modal__icon-btn"
                        disabled={!canRefresh}
                        aria-label="Refresh loan status"
                        onClick={() => void wrap('sl-refresh', () => refreshAgreementRow(row))}
                    >
                        {spin('sl-refresh') ? (
                            <AgreementIconSpin />
                        ) : (
                            <ArrowsClockwise size={AGREEMENT_TOOL_ICON_PX} weight="bold" className="obd-agreements-modal__ag-icon" aria-hidden />
                        )}
                    </button>
                </Tooltip>
            </div>
        );
    }

    if (app === 'Credit Decline') {
        return (
            <div className="obd-agreements-modal__angular-grid-cell">
                <Tooltip title={app}>
                    <span className="obd-agreements-modal__icon-btn obd-agreements-modal__icon-btn--static" aria-label={app}>
                        {trinityStatusGlyph(app)}
                    </span>
                </Tooltip>
            </div>
        );
    }

    if (credit === 'Credit Approval') {
        return (
            <div className="obd-agreements-modal__icon-strip obd-agreements-modal__icon-strip--submit">
                <Tooltip title={credit}>
                    <span className="obd-agreements-modal__icon-btn obd-agreements-modal__icon-btn--static" aria-label={credit}>
                        {trinityStatusGlyph(credit)}
                    </span>
                </Tooltip>
                {app !== 'Signed' ? (
                    <Tooltip title={canRefresh ? 'Refresh loan status from server' : 'Need contract id on this row to refresh'}>
                        <button
                            type="button"
                            className="obd-agreements-modal__icon-btn"
                            disabled={!canRefresh}
                            aria-label="Refresh loan status"
                            onClick={() => void wrap('sl-refresh', () => refreshAgreementRow(row))}
                        >
                            {spin('sl-refresh') ? (
                                <AgreementIconSpin />
                            ) : (
                                <ArrowsClockwise size={AGREEMENT_TOOL_ICON_PX} weight="bold" className="obd-agreements-modal__ag-icon" aria-hidden />
                            )}
                        </button>
                    </Tooltip>
                ) : null}
            </div>
        );
    }

    const link = sunlightApplicationLink(row);

    return (
        <div className="obd-agreements-modal__icon-strip obd-agreements-modal__icon-strip--submit">
            <Tooltip title={app}>
                <button
                    type="button"
                    className="obd-agreements-modal__icon-btn"
                    disabled={!link}
                    aria-label={`${app} — open application`}
                    onClick={() => {
                        if (link) openSunlightApplicationIframe(link);
                    }}
                >
                    {trinityStatusGlyph(app)}
                </button>
            </Tooltip>
            <Tooltip title={canRefresh ? 'Refresh loan status from server' : 'Need contract id on this row to refresh'}>
                <button
                    type="button"
                    className="obd-agreements-modal__icon-btn"
                    disabled={!canRefresh}
                    aria-label="Refresh loan status"
                    onClick={() => void wrap('sl-refresh', () => refreshAgreementRow(row))}
                >
                    {spin('sl-refresh') ? (
                        <AgreementIconSpin />
                    ) : (
                        <ArrowsClockwise size={AGREEMENT_TOOL_ICON_PX} weight="bold" className="obd-agreements-modal__ag-icon" aria-hidden />
                    )}
                </button>
            </Tooltip>
        </div>
    );
}

/** Trinity `OpportunityConstant.CommonConstants.ServiceFinancePartnerName`. */
const ANGULAR_SERVICE_FINANCE_PARTNER = 'Service Finance';

function serviceFinanceContractStatusLabel(row: AgreementRow): string {
    return row.contractStatus === '—' ? '' : String(row.contractStatus || '').trim();
}

/**
 * Trinity `ServiceFinanceRendererComponent` — single `getStatusIcon(contractStatus)` glyph.
 * Spinner while `ServiceFinancesApplicationLoader === 'in-progress'`; `disable_option` when `isDisabled`.
 */
function ServiceFinanceSubmitCell({
    row,
    actionBusyKey,
    openServiceFinanceSubmit,
}: {
    row: AgreementRow;
    actionBusyKey: string | null;
    openServiceFinanceSubmit: (row: AgreementRow) => void;
}) {
    const statusTitle = serviceFinanceContractStatusLabel(row);
    const disabled = isServiceFinanceSubmitDisabled(row);
    const isSigned = statusTitle === 'Signed';
    const spinning = actionBusyKey === `${row.key}:sf-refresh`;

    if (spinning) {
        return (
            <div className="obd-agreements-modal__angular-grid-cell">
                <LoadingOutlined className="obd-agreements-modal__angular-orange-spin" spin aria-hidden />
            </div>
        );
    }

    return (
        <div
            className={
                disabled
                    ? 'obd-agreements-modal__angular-grid-cell obd-agreements-modal__sf-submit--disabled'
                    : 'obd-agreements-modal__angular-grid-cell'
            }
        >
            <Tooltip title={statusTitle || undefined}>
                {isSigned || disabled ? (
                    <span className="obd-agreements-modal__icon-btn obd-agreements-modal__icon-btn--static" aria-label={statusTitle || 'Service Finance application'}>
                        {trinityStatusGlyph(statusTitle)}
                    </span>
                ) : (
                    <button
                        type="button"
                        className="obd-agreements-modal__icon-btn"
                        aria-label={statusTitle || 'Submit Service Finance application'}
                        onClick={() => openServiceFinanceSubmit(row)}
                    >
                        {trinityStatusGlyph(statusTitle)}
                    </button>
                )}
            </Tooltip>
        </div>
    );
}

function canOfferSignFinancePartnerContract(row: AgreementRow, kind: AgreementPartnerKind): boolean {
    const st = String(row.contractStatus || '').toLowerCase();
    if (st.includes('cancelled') && kind === 'igs') return false;
    if (st === 'signed') return false;
    if (kind === 'lightreach') {
        return !!row.systemIdRaw?.trim() && !!String(row.creditStatusRaw || '').trim();
    }
    if (kind === 'sunnova' || kind === 'igs') return true;
    return false;
}

/** Angular `create-sunnova-contract-renderer.agInit`: `params.data.contractStatus == 'Signed'` → `visible_img_link` (handshake + "Signature Status: Signed"). */
function isSignFinancePartnerContractColumnSigned(row: AgreementRow): boolean {
    const raw = row.contractStatus === '—' ? '' : String(row.contractStatus || '').trim();
    return raw.toLowerCase() === 'signed';
}

/** Angular `OpportunityConstant.CommonConstants.IGSPartnerName` (`opportunity.constanct.ts`). */
const ANGULAR_IGS_FINANCE_PARTNER = 'IGS';

function isIgsFinancePartnerRow(row: AgreementRow): boolean {
    return String(row.financePartnerRaw || row.financePartner || '').trim().toUpperCase() === ANGULAR_IGS_FINANCE_PARTNER;
}

/**
 * Angular `view-proposal-renderer.component.html` for header “View Finance Partner Agreement”:
 * - IGS: no eye (use “Trinity Documents” + `ViewTrinityContractRenderer` instead).
 * - Else: eye when `ViewProposal == ''`, spinner when `in-progress`.
 */
function viewFinancePartnerAgreementCellState(row: AgreementRow): 'hidden' | 'spinner' | 'quote' | 'muted' {
    if (isIgsFinancePartnerRow(row)) return 'hidden';
    const vp = String(row.viewProposalRaw ?? '').trim().toLowerCase();
    if (vp === 'in-progress') return 'spinner';
    if (vp === '') return 'quote';
    return 'muted';
}

/** Trinity `ViewRoofProposal` — non-Sunnova rows show link when unsigned; Sunnova when product line includes roof/solar/battery cues. */
function canViewRoofSolarBatteryProposal(row: AgreementRow): boolean {
    if (row.isContractSignedBool) return false;
    const pt = (row.productTypeCode || row.productType || '').toUpperCase();
    const fp = (row.financePartnerRaw || row.financePartner).trim();
    if (fp === 'Sunnova') {
        return pt.includes('R') || pt === 'S' || pt === 'SB' || pt === 'SBR' || pt === 'BO' || pt === 'B';
    }
    return true;
}

/** Trinity `ServiceFinanceRendererComponent.isDisabled` — Service Finance + roof (`ProductType.includes('R')`). */
function isServiceFinanceSubmitDisabled(row: AgreementRow): boolean {
    const fp = (row.financePartnerRaw || row.financePartner).trim();
    const pt = String(row.productTypeCode || row.productType || '');
    return !(fp === ANGULAR_SERVICE_FINANCE_PARTNER && pt.includes('R'));
}

/** Trinity `handleClick`: open submit modal unless `contractStatus === 'Signed'` or `isDisabled`. */
function canServiceFinanceSubmitApplication(row: AgreementRow): boolean {
    if (isServiceFinanceSubmitDisabled(row)) return false;
    return serviceFinanceContractStatusLabel(row) !== 'Signed';
}

/** Extra grid actions from `generate-contract` cell renderers (Sign contract, proposals, Sunlight credit, Service Finance). */
function PartnerWorkflowDropdown({
    row,
    opportunityId,
    salesforceId,
    actionBusyKey,
    setActionBusyKey,
    handlers,
    showServiceFinanceSubmitView,
    isSunlightView,
}: {
    row: AgreementRow;
    opportunityId: string | number | null | undefined;
    salesforceId: string | null | undefined;
    actionBusyKey: string | null;
    setActionBusyKey: (k: string | null) => void;
    /** Angular `contract-list`: `showServiceFinanceSubmitOption === 'View'` for submit column / actions. */
    showServiceFinanceSubmitView: boolean;
    /** Angular `contract-list`: `isSunlight === 'View'` for Sunlight `Application_Status` columns / workflow. */
    isSunlightView: boolean;
    handlers: {
        signFinancePartnerContract: (row: AgreementRow) => void | Promise<void>;
        openRoofingProposalUrl: (row: AgreementRow) => Promise<void>;
        openViewPartnerAgreement: (row: AgreementRow) => Promise<void>;
        openServiceFinanceSubmit: (row: AgreementRow) => void;
        refreshServiceFinanceContractStatus: (row: AgreementRow) => Promise<void>;
        refreshLightreachFormStatus: (row: AgreementRow) => Promise<void>;
        startSunlightCreditFlow: (row: AgreementRow) => void;
        startSunlightSendDocsFlow: (row: AgreementRow) => void;
    };
}) {
    const kind = detectAgreementPartnerKind(row);
    const opp = opportunityId != null && String(opportunityId).trim() !== '';
    const items: MenuProps['items'] = [];

    if ((kind === 'sunnova' || kind === 'igs' || kind === 'lightreach') && canOfferSignFinancePartnerContract(row, kind)) {
        items.push({
            key: 'pw-sign',
            label: 'Sign finance partner contract',
            disabled: !opp || !row.quoteIdRaw,
        });
    }
    if (canViewRoofSolarBatteryProposal(row) && row.quoteIdRaw) {
        items.push({
            key: 'pw-roof',
            label: 'View roof / solar / battery proposal (GetRoofingProposalURL)',
        });
    }
    if (kind === 'sunnova' || kind === 'igs' || kind === 'lightreach') {
        const igsRow = isIgsFinancePartnerRow(row);
        items.push({
            key: 'pw-agreement',
            label: igsRow ? 'View Trinity contract' : 'View finance partner agreement (quote)',
            disabled: !row.quoteIdRaw || (igsRow && !String(salesforceId ?? '').trim()),
        });
    }
    if (showServiceFinanceSubmitView && kind === 'serviceFinance' && canServiceFinanceSubmitApplication(row)) {
        items.push({ key: 'pw-sf-submit', label: 'Submit Service Finance application (full form)' });
    }
    if (kind === 'serviceFinance' && opp && row.opportunityContractIdRaw) {
        items.push({ key: 'pw-sf-refresh', label: 'Refresh Service Finance contract status' });
    }
    if (kind === 'lightreach' && opp && row.opportunityContractIdRaw) {
        items.push({ key: 'pw-lr-refresh', label: 'Refresh Lightreach form status' });
    }
    if (
        isSunlightView &&
        kind === 'sunlight' &&
        String(row.contractStatus || '').toLowerCase() !== 'signed' &&
        row.quoteIdRaw
    ) {
        items.push({ key: 'pw-sl-credit', label: 'Sunlight — loan application (credit + iframe)' });
    }
    if (isSunlightView && kind === 'sunlight' && row.leadIdRaw) {
        items.push({ key: 'pw-sl-docs', label: 'Sunlight — send loan documents' });
    }

    if (!items.length) return null;

    const wrap = async (tag: string, fn: () => Promise<void>) => {
        const k = `${row.key}:${tag}`;
        setActionBusyKey(k);
        try {
            await fn();
        } finally {
            setActionBusyKey(null);
        }
    };

    const onClick: MenuProps['onClick'] = ({ key, domEvent }) => {
        domEvent.preventDefault();
        if (key === 'pw-sign') void wrap('pw-sign', () => handlers.signFinancePartnerContract(row));
        else if (key === 'pw-roof') void wrap('pw-roof', () => handlers.openRoofingProposalUrl(row));
        else if (key === 'pw-agreement') void wrap('pw-agreement', () => handlers.openViewPartnerAgreement(row));
        else if (key === 'pw-sf-submit') {
            handlers.openServiceFinanceSubmit(row);
        } else if (key === 'pw-sf-refresh') void handlers.refreshServiceFinanceContractStatus(row);
        else if (key === 'pw-lr-refresh') void wrap('pw-lr-refresh', () => handlers.refreshLightreachFormStatus(row));
        else if (key === 'pw-sl-credit') handlers.startSunlightCreditFlow(row);
        else if (key === 'pw-sl-docs') handlers.startSunlightSendDocsFlow(row);
    };

    const busyTags = [
        'pw-sign',
        'pw-roof',
        'pw-agreement',
        'sf-refresh',
        'pw-lr-refresh',
    ] as const;
    const busy = busyTags.some((t) => actionBusyKey === `${row.key}:${t}`);

    return (
        <OBDDropdown menu={{ items, onClick }} trigger={['click']}>
            <Tooltip title="Partner workflow — Sign contract, proposals, Service Finance, Sunlight (Trinity grids)">
                <button type="button" className="obd-agreements-modal__icon-btn obd-agreements-modal__icon-btn--dropdown" aria-label="Partner workflow">
                    {busy ? <AgreementIconSpin /> : <RocketLaunch size={AGREEMENT_TOOL_ICON_PX} weight="bold" className="obd-agreements-modal__ag-icon" aria-hidden />}
                    <CaretDown size={10} weight="bold" className="obd-agreements-modal__icon-caret" aria-hidden />
                </button>
            </Tooltip>
        </OBDDropdown>
    );
}

function AgreementRowIconActions({
    row,
    opportunityId,
    salesforceId,
    actionBusyKey,
    setActionBusyKey,
    sfdcBundleRunning,
    toolVis,
    openTrinityContractViewGated,
    openSigning,
    openSigningDetailsPreview,
    openCombinedPack,
    openSignPartnerOrExternal,
    openSignPartnerEmail,
    runSfdcBundle,
    reloadAgreements,
    refreshAgreementRow,
    partnerWorkflowHandlers,
    showServiceFinanceSubmitView,
    isSunlightView,
    pushNotice,
}: {
    row: AgreementRow;
    opportunityId: string | number | null | undefined;
    salesforceId: string | null | undefined;
    actionBusyKey: string | null;
    setActionBusyKey: (k: string | null) => void;
    sfdcBundleRunning: boolean;
    toolVis: AgreementToolVisibility;
    showServiceFinanceSubmitView: boolean;
    isSunlightView: boolean;
    openTrinityContractViewGated: (row: AgreementRow) => Promise<void>;
    openSigning: (row: AgreementRow) => Promise<void>;
    openSigningDetailsPreview: (row: AgreementRow) => Promise<void>;
    openCombinedPack: (row: AgreementRow) => Promise<void>;
    openSignPartnerOrExternal: (row: AgreementRow) => Promise<void>;
    openSignPartnerEmail: (row: AgreementRow) => Promise<void>;
    runSfdcBundle: (row: AgreementRow, afterAllSuccess?: () => Promise<void>) => Promise<void>;
    reloadAgreements: () => Promise<void>;
    refreshAgreementRow: (row: AgreementRow) => Promise<void>;
    partnerWorkflowHandlers: {
        signFinancePartnerContract: (row: AgreementRow) => void | Promise<void>;
        openRoofingProposalUrl: (row: AgreementRow) => Promise<void>;
        openViewPartnerAgreement: (row: AgreementRow) => Promise<void>;
        openServiceFinanceSubmit: (row: AgreementRow) => void;
        refreshServiceFinanceContractStatus: (row: AgreementRow) => Promise<void>;
        refreshLightreachFormStatus: (row: AgreementRow) => Promise<void>;
        startSunlightCreditFlow: (row: AgreementRow) => void;
        startSunlightSendDocsFlow: (row: AgreementRow) => void;
    };
    pushNotice: AgreementNoticePush;
}) {
    const wrap = async (tag: string, fn: () => Promise<void>) => {
        const k = `${row.key}:${tag}`;
        setActionBusyKey(k);
        try {
            await fn();
        } finally {
            setActionBusyKey(null);
        }
    };

    const opp = opportunityId != null && String(opportunityId).trim() !== '';
    const canSfdcLead = opp && !!row.quoteIdRaw;
    const canTrinityView = !!String(salesforceId || '').trim() && !!row.quoteIdRaw;

    const canSignPortal =
        !!row.externalLink ||
        (opp && !!row.leadIdRaw && !!row.contractIdRaw && !!row.quoteIdRaw);
    const canPartnerEmail = opp && !!row.leadIdRaw && !!row.contractIdRaw && !!row.quoteIdRaw;
    const canRefreshAgreement = !!row.agreementIdRaw;
    const canAppMenu =
        toolVis.applicationPortal && (canRefreshAgreement || canSignPortal || canPartnerEmail);
    const canSigning = !!row.agreementIdRaw;
    const canPack = !!String(salesforceId || '').trim() && !!row.quoteIdRaw && row.quoteId !== '—';

    const appMenu: MenuProps['items'] = [
        { key: 'app-refresh', label: 'Refresh agreement from server', disabled: !canRefreshAgreement || sfdcBundleRunning },
        {
            key: 'app-portal',
            label: row.externalLink ? 'Open external application link' : 'Open partner signing portal',
            disabled: !canSignPortal || sfdcBundleRunning,
        },
        { key: 'app-email', label: 'Email partner signing link', disabled: !canPartnerEmail || sfdcBundleRunning },
    ];

    const signMenu: MenuProps['items'] = [
        { key: 'sign-open', label: 'Open signing URL in browser', disabled: !canSigning || sfdcBundleRunning },
        { key: 'sign-preview', label: 'Show signing API response', disabled: !canSigning || sfdcBundleRunning },
    ];

    const packMenu: MenuProps['items'] = toolVis.combinedPack
        ? [
              { key: 'pack-templates', label: 'View combined pack templates', disabled: !canPack || sfdcBundleRunning },
              { key: 'pack-refresh', label: 'Reload agreements list', disabled: sfdcBundleRunning },
          ]
        : [{ key: 'pack-refresh', label: 'Reload agreements list', disabled: sfdcBundleRunning }];

    const creditMenu: MenuProps['items'] = [
        {
            key: 'credit-copy',
            label: row.creditStatusRaw ? `Copy status: ${row.creditStatusRaw}` : 'No credit status on this row',
            disabled: !row.creditStatusRaw,
        },
    ];

    const onAppMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
        domEvent.preventDefault();
        if (key === 'app-refresh') void wrap('refresh-row', () => refreshAgreementRow(row));
        else if (key === 'app-portal') void wrap('partner-portal', () => openSignPartnerOrExternal(row));
        else if (key === 'app-email') void wrap('partner-email', () => openSignPartnerEmail(row));
    };

    const onSignMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
        domEvent.preventDefault();
        if (key === 'sign-open') void wrap('signing', () => openSigning(row));
        else if (key === 'sign-preview') void wrap('signing-preview', () => openSigningDetailsPreview(row));
    };

    const onPackMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
        domEvent.preventDefault();
        if (key === 'pack-templates') void wrap('pack', () => openCombinedPack(row));
        else if (key === 'pack-refresh') void wrap('pack-refresh', () => reloadAgreements());
    };

    const onCreditMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
        domEvent.preventDefault();
        if (key === 'credit-copy' && row.creditStatusRaw) {
            void navigator.clipboard?.writeText(row.creditStatusRaw);
            pushNotice('success', 'Credit status copied.');
        }
    };

    const dropdownTrigger = (label: string, main: React.ReactNode, disabled: boolean, busyTags: readonly string[]) => {
        const busy =
            busyTags.length > 0 && busyTags.some((tag) => actionBusyKey === `${row.key}:${tag}`);
        return (
            <button
                type="button"
                className="obd-agreements-modal__icon-btn obd-agreements-modal__icon-btn--dropdown"
                disabled={disabled || sfdcBundleRunning}
                aria-label={label}
            >
                {busy ? <AgreementIconSpin /> : main}
                <CaretDown size={10} weight="bold" className="obd-agreements-modal__icon-caret" aria-hidden />
            </button>
        );
    };

    const showSfdc = toolVis.sfdc;
    const showDocs = toolVis.documents;
    const showApp = toolVis.applicationPortal;
    const showSign = toolVis.signing;
    const showCredit = toolVis.credit;
    const showPack = true;

    return (
        <div className="obd-agreements-modal__icon-strip">
            {showSfdc ? (
                <Tooltip title="SFDC Sync">
                    <button
                        type="button"
                        className="obd-agreements-modal__icon-btn"
                        disabled={!canSfdcLead || sfdcBundleRunning}
                        aria-label="SFDC Sync"
                        onClick={() => void wrap('sfdc-bundle', () => runSfdcBundle(row))}
                    >
                        {actionBusyKey === `${row.key}:sfdc-bundle` ? (
                            <AgreementIconSpin />
                        ) : (
                            <SalesforceCloudIcon />
                        )}
                    </button>
                </Tooltip>
            ) : null}

            {showDocs && toolVis.trinityContract ? (
                <Tooltip title="View Trinity Contract">
                    <button
                        type="button"
                        className="obd-agreements-modal__icon-btn"
                        disabled={!canTrinityView || sfdcBundleRunning}
                        aria-label="View Trinity Contract"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (sfdcBundleRunning || !canTrinityView) return;
                            void wrap('trinity-gated', () => openTrinityContractViewGated(row));
                        }}
                    >
                        <Eye size={AGREEMENT_TOOL_ICON_PX} weight="duotone" className="obd-agreements-modal__ag-icon" aria-hidden />
                    </button>
                </Tooltip>
            ) : null}

            {showApp ? (
                <OBDDropdown menu={{ items: appMenu, onClick: onAppMenuClick }} trigger={['click']} disabled={!canAppMenu}>
                    <Tooltip title="Application / partner — refresh, portal, or email">
                        {dropdownTrigger(
                            'Application actions',
                            <ArrowSquareOut size={AGREEMENT_TOOL_ICON_PX} weight="bold" className="obd-agreements-modal__ag-icon" aria-hidden />,
                            !canAppMenu,
                            ['refresh-row', 'partner-portal', 'partner-email'],
                        )}
                    </Tooltip>
                </OBDDropdown>
            ) : null}

            {showSign ? (
                <OBDDropdown menu={{ items: signMenu, onClick: onSignMenuClick }} trigger={['click']} disabled={!canSigning}>
                    <Tooltip title="Signing — click for options">
                        {dropdownTrigger(
                            'Signing menu',
                            <Scroll size={AGREEMENT_TOOL_ICON_PX} weight="bold" className="obd-agreements-modal__ag-icon" aria-hidden />,
                            !canSigning,
                            ['signing', 'signing-preview'],
                        )}
                    </Tooltip>
                </OBDDropdown>
            ) : null}

            {showCredit ? (
                <OBDDropdown menu={{ items: creditMenu, onClick: onCreditMenuClick }} trigger={['click']}>
                    <Tooltip title={row.creditStatusRaw ? `Credit: ${row.creditStatusRaw}` : 'Credit — click to copy if available'}>
                        {dropdownTrigger(
                            'Credit menu',
                            <UserCircle
                                size={AGREEMENT_TOOL_ICON_PX}
                                weight="bold"
                                className={
                                    row.creditStatusRaw
                                        ? 'obd-agreements-modal__ag-icon'
                                        : 'obd-agreements-modal__ag-icon obd-agreements-modal__ag-icon--dim'
                                }
                                aria-hidden
                            />,
                            false,
                            [],
                        )}
                    </Tooltip>
                </OBDDropdown>
            ) : null}

            {showPack ? (
                <OBDDropdown menu={{ items: packMenu, onClick: onPackMenuClick }} trigger={['click']} disabled={sfdcBundleRunning}>
                    <Tooltip title={toolVis.combinedPack ? 'Combined pack — click for options' : 'Reload agreements'}>
                        {dropdownTrigger(
                            'Combined pack menu',
                            <Package size={AGREEMENT_TOOL_ICON_PX} weight="bold" className="obd-agreements-modal__ag-icon" aria-hidden />,
                            false,
                            ['pack', 'pack-refresh'],
                        )}
                    </Tooltip>
                </OBDDropdown>
            ) : null}

            <PartnerWorkflowDropdown
                row={row}
                opportunityId={opportunityId}
                salesforceId={salesforceId}
                actionBusyKey={actionBusyKey}
                setActionBusyKey={setActionBusyKey}
                handlers={partnerWorkflowHandlers}
                showServiceFinanceSubmitView={showServiceFinanceSubmitView}
                isSunlightView={isSunlightView}
            />
        </div>
    );
}

/** Trinity `detail-cell-renderer` — Direct Lead PPA Signed (`Sales- Direct` + `GetDirectLeadStatus` false). Gated by `Enable_PPA_Signed`. */
function displayNameForPpaProductCategory(category: string): string {
    const c = String(category ?? '').trim().toUpperCase();
    const labels: Record<string, string> = {
      S: 'Solar', B: 'Battery', R: 'Roof', RO: 'Roof', BO: 'Battery',
      SB: 'Solar + Battery', SR: 'Solar + Roof', SBR: 'Solar + Battery + Roof',
      BR: 'Battery + Roof', RR: 'RR', RRR: 'RRR',
    };
    return labels[c] || (category ? String(category) : 'Product');
}

function normalizePpaStatusFromApiRowArray(rows: any[], qc: any[] = []) {
    const isSigned = (row: any) =>
      row?.PPA_Signed_Date != null && String(row.PPA_Signed_Date).trim() !== '';

    // Exact Angular logic from computeFilteredPpaProductStatus
    const allProductTypes = qc.map((q: any) => {
        const pt = String(q.ProductType || '').toUpperCase();
        if ((pt === 'RO' || pt === 'R') && q.RR_Price != null && q.RR_Price != 0) {
            return 'RRR';
        }
        return pt;
    });

    const validCategories = new Set<string>();
    allProductTypes.forEach(pt => {
        if (pt.includes('S')) validCategories.add('S');
        if (pt.includes('B') || pt === 'BO') validCategories.add('B');
        if (pt.includes('RRR')) validCategories.add('RRR');
        else if (pt.includes('RR')) validCategories.add('RR');
        else if (pt.includes('R') || pt === 'RO') validCategories.add('R');
    });

    const validRows = rows.filter((item: any) => {
        const cat = String(item?.Product_Category ?? item?.product_Category ?? '').toUpperCase();
        if (cat === 'S' && validCategories.has('S')) return true;
        if (cat === 'B' && validCategories.has('B')) return true;
        if (validCategories.has(cat)) return true;
        return false;
    });

    const unsignedProducts = validRows
      .filter((row) => !isSigned(row))
      .map((row) => {
        const cat = (row?.Product_Category ?? row?.product_Category ?? '').trim().toUpperCase();
        return {
          displayName: displayNameForPpaProductCategory(cat),
          apiProductCategory: cat,
        };
      })
      .filter((u) => u.apiProductCategory);

    const signedProductKeys = Array.from(
      new Set(
        validRows
          .filter((row) => isSigned(row))
          .map((row) => (row?.Product_Category ?? row?.product_Category ?? '').trim().toUpperCase())
          .filter(Boolean)
      )
    );

    const signedCount = validRows.filter((row) => isSigned(row)).length;
    return {
      initialPpaModalCompleted: signedCount > 0,
      allUnsigned: signedCount === 0,
      unsignedProducts,
      signedProductKeys,
    };
}

type ContractSignedDetailRowProps = {
    row: AgreementRow;
    opportunityId: string | number | null | undefined;
    opportunityDetails: unknown;
    onUpdated: () => void | Promise<void>;
    /** Angular `getCapability('Enable_PPA_Signed') === 'View'`. */
    ppaSignedCapabilityView: boolean;
    /** `gridCell` — single key/value in the expand grid (after Application). `fullRow` — full-width row (non–Good Leap partners). */
    variant?: 'fullRow' | 'gridCell';
    onConfirm?: (options: {
        title?: React.ReactNode;
        content: React.ReactNode;
        okText?: string;
        cancelText?: string;
        onOk?: () => void | Promise<void>;
    }) => void;
    pushNotice: AgreementNoticePush;
    ppaProductStatus?: any;
    setSectionLoading?: (loading: boolean) => void;
};

function ContractSignedDetailRow({
    row,
    opportunityId,
    opportunityDetails,
    onUpdated,
    ppaSignedCapabilityView,
    variant = 'fullRow',
    onConfirm,
    pushNotice,
    ppaProductStatus,
    setSectionLoading
}: ContractSignedDetailRowProps) {
    const [modalOpen, setModalOpen] = useState(false);

    const salesDiv = String(
        (opportunityDetails as { SalesPerson?: { Sales_Division?: string } } | null | undefined)?.SalesPerson
            ?.Sales_Division ?? '',
    ).trim();
    const directSales = salesDiv === 'Sales- Direct';

    if (!ppaSignedCapabilityView || !directSales || !ppaProductStatus) return null;

    const rowPt = String(row.productTypeCode || row.productType || '').toUpperCase();
    const signed = ppaProductStatus.signedProductKeys || [];
    const needsS = rowPt.includes('S');
    const needsB = rowPt.includes('B') || rowPt === 'BO';
    let roofPart = '';
    if (rowPt.includes('RRR')) roofPart = 'RRR';
    else if (rowPt.includes('RR')) roofPart = 'RR';
    else if (rowPt.includes('R') || rowPt === 'RO') roofPart = 'R';

    let isRowSigned = false;
    if ((needsS || needsB || !!roofPart) && 
        (!needsS || signed.includes('S')) && 
        (!needsB || signed.includes('B')) && 
        (!roofPart || signed.includes(roofPart))) {
        isRowSigned = true;
    }

    if (isRowSigned) {
        const kv = (
            <div className="obd-agreements-modal__detail-kv obd-agreements-modal__detail-kv--ppa-signed">
                <span className="obd-agreements-modal__detail-k">Contract Signed</span>
                <span className="obd-agreements-modal__detail-v" style={{ color: 'var(--obd-success-color)', fontWeight: 500 }}>
                    Signed
                </span>
            </div>
        );
        return variant === 'gridCell' ? kv : <div className="obd-agreements-modal__detail-row-4 obd-agreements-modal__detail-row-upload">{kv}</div>;
    }

    if (ppaProductStatus.unsignedProducts.length === 0) return null;

    const ppaButton = (
        <>
            <Button
                type="primary"
                size="small"
                onClick={() => setModalOpen(true)}
            >
                Contract Signed
            </Button>
            <ContractSignedProductsModal
                open={modalOpen}
                title="Contract Signed"
                instruction="Are you sure you want to update the Direct Lead stage to 'Contract Signed' for the selected products?"
                lineItems={ppaProductStatus.unsignedProducts.map((p: any) => ({
                    displayName: p.displayName,
                    apiProductCategory: p.apiProductCategory,
                    selected: true
                }))}
                onClose={async (selected) => {
                    setModalOpen(false);
                    if (!selected || selected.length === 0) return;
                    
                    const oppIdNum = Number(opportunityId);
                    const quoteIdForMatch = row.quoteIdRaw || row.quoteId.replace(/^quote\s*/i, '');
                    
                    const payload = selected.map((cat: string) => {
                        let isMatching = false;
                        if (cat === 'S') isMatching = rowPt.includes('S');
                        else if (cat === 'B') isMatching = rowPt.includes('B') || rowPt === 'BO';
                        else if (cat === roofPart && roofPart !== '') isMatching = true;

                        return {
                            Opportunity_Id: Number.isFinite(oppIdNum) ? oppIdNum : 0,
                            Finance_Partner_Quote_Id: isMatching ? Number(quoteIdForMatch) : 0,
                            Opportunity_Contract_Id: isMatching ? Number(row.contractIdRaw) : 0,
                            Quote_Type: cat,
                            FinancePartner: isMatching ? row.financePartnerRaw || row.financePartner : null
                        };
                    });

                    try {
                        setSectionLoading?.(true);
                        const out = await contractService.updatePpaSignedProducts(payload);
                        const ok = (out as { IsSuccessful?: boolean })?.IsSuccessful !== false;
                        const msg = String((out as { Message?: string })?.Message ?? '');
                        if (ok) {
                            pushNotice('success', msg || 'Contract Signed status updated.');
                            await onUpdated();
                        } else {
                            pushNotice('error', msg || 'Update failed.');
                            setSectionLoading?.(false);
                        }
                    } catch (e: unknown) {
                        pushNotice('error', contractApiErrorMessage(e) ?? 'Request failed.');
                        setSectionLoading?.(false);
                    }
                }}
            />
        </>
    );

    const kv = (
        <div className="obd-agreements-modal__detail-kv obd-agreements-modal__detail-kv--ppa-signed">
            <span className="obd-agreements-modal__detail-k">Contract Signed</span>
            <span className="obd-agreements-modal__detail-v">{ppaButton}</span>
        </div>
    );

    if (variant === 'gridCell') {
        return kv;
    }

    return <div className="obd-agreements-modal__detail-row-4 obd-agreements-modal__detail-row-upload">{kv}</div>;
}

export const AgreementsModal: React.FC<AgreementsModalProps> = ({
    open,
    onClose,
    opportunityId,
    variant = 'modal',
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    const changeOrderDetails = useOneButtonStore((s) => s.changeOrderDetails);
    const agreementsRefreshTrigger = useOneButtonStore((s) => s.agreementsRefreshTrigger);
    const setAgreementsRefreshTrigger = useOneButtonStore((s) => s.setAgreementsRefreshTrigger);
    const setAgreementsPendingExpandQuoteId = useOneButtonStore((s) => s.setAgreementsPendingExpandQuoteId);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const setCreditCheckModalOpen = useOneButtonStore((s) => s.setCreditCheckModalOpen);
    const setOpportunityDetails = useOneButtonStore((s) => s.setOpportunityDetails);
    const setFinanceSelectionRefreshTrigger = useOneButtonStore((s) => s.setFinanceSelectionRefreshTrigger);
    const setFinancePartnerRawSyncStatuses = useOneButtonStore((s) => s.setFinancePartnerRawSyncStatuses);
    const setContractCreationStatuses = useOneButtonStore((s) => s.setContractCreationStatuses);

    const handleClose = useCallback(() => {
        setAgreementsRefreshTrigger(Date.now());
        onClose();
    }, [onClose, setAgreementsRefreshTrigger]);

    const { getCapabilityValue } = useOneButtonCapability();
    const agreementsCapability = useMemo(
        () => ({
            goodLeapGetCreditStatusView: getCapabilityValue('Enable_GoodLeap_Get_Credit_Status') === 'View',
            goodLeapSendCreditEmailView: getCapabilityValue('Enable_GoodLeap_Send_Credit_Email') === 'View',
            goodLeapTimelineView: getCapabilityValue('Enable_GoodLeap_TimeLine') === 'View',
            enablePpaSignedView: getCapabilityValue('Enable_PPA_Signed') === 'View',
            igsCancelContractView: getCapabilityValue('IGS_Cancel_Contract') === 'View',
            showServiceFinanceSubmitView: getCapabilityValue('showServiceFinanceSubmitOption') === 'View',          
            isSunlightView: getCapabilityValue('isSunlight') === 'View',
            isGoodLeapView: getCapabilityValue('isGoodLeap') === 'View',
            ccPaymentView: getCapabilityValue('CC_Payment') === 'View',
        }),
        [getCapabilityValue],
    );
    const [noticeAPI, noticeContextHolder] = notification.useNotification({ maxCount: 3 });
    const noticeClass = useMemo(() => `obd-message obd-theme-${theme}`, [theme]);
    const pushAgreementNotice = useCallback(
        (type: 'success' | 'error' | 'info' | 'warning', description: string) => {
            noticeAPI[type]({ message: '', description, className: noticeClass });
        },
        [noticeAPI, noticeClass],
    );

    const [loanResyncErrorsByRowKey, setLoanResyncErrorsByRowKey] = useState<Record<string, LoanSyncResyncErrors>>({});

    const makeGoodLeapSyncHooks = useCallback((rowKey: string): GoodLeapLoanSyncHooks => {
        return {
            onStageSuccess: (stage) => {
                const field = loanResyncFieldForStage(stage);
                setLoanResyncErrorsByRowKey((prev) => {
                    const cur = prev[rowKey];
                    if (!cur?.[field]) return prev;
                    const next = { ...cur };
                    delete next[field];
                    if (!Object.keys(next).length) {
                        const { [rowKey]: _, ...rest } = prev;
                        return rest;
                    }
                    return { ...prev, [rowKey]: next };
                });
            },
            onStageFailure: (stage, storageMessage) => {
                const field = loanResyncFieldForStage(stage);
                setLoanResyncErrorsByRowKey((prev) => ({
                    ...prev,
                    [rowKey]: { ...prev[rowKey], [field]: storageMessage },
                }));
            },
        };
    }, []);

    const roof_count = useMemo(() => {
        let count = 0;
        const buildings = (opportunityDetails as any)?.Opportunityinfo?.Buildings || [];
        buildings.forEach((b: any) => {
            if (b.Roofs) count += b.Roofs.length;
        });
        return count;
    }, [opportunityDetails]);

    const isIgsEnergyCommunity = useMemo(() => {
        const list = (opportunityDetails as any)?.financePartnerLeadListModels;
        if (!Array.isArray(list)) return false;
        const solarLead = list.find((ele: any) => ele?.ProductTypeName === 'Solar');
        return !!solarLead?.Is_Energy_Community;
    }, [opportunityDetails]);

    const statesFullName = useOneButtonStore((s) => s.opportunityinfoMasterData?.StatesFullName);
    /** Match QuotesContractsCard / dashboard: opportunity detail first, then session (Trinity often stores SF id here). */
    const salesforceId = useOneButtonStore((s) => {
        const od = s.opportunityDetails as { SalesForce_Id?: string; Salesforce_Id?: string } | null | undefined;
        const fromDetail =
            (od?.SalesForce_Id != null && String(od.SalesForce_Id).trim()) ||
            (od?.Salesforce_Id != null && String(od.Salesforce_Id).trim()) ||
            '';
        if (fromDetail) return fromDetail;
        if (typeof window !== 'undefined') {
            const sid = sessionStorage.getItem('SalesForceId');
            if (sid && String(sid).trim()) return String(sid).trim();
        }
        return null;
    });
    const embedded = variant === 'embedded';
    /** Coalesces overlapping `load()` runs (merged open + refresh effects, Strict Mode double-invoke). */
    const agreementsLoadSeq = useRef(0);
    const [loading, setLoading] = useState(false);
    const [ppaProductStatus, setPpaProductStatus] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<AgreementRow[]>([]);
    const [actionBusyKey, setActionBusyKey] = useState<string | null>(null);
    const [sfdcBundleUi, setSfdcBundleUi] = useState<{
        row: AgreementRow;
        steps: SfdcBundleStep[];
        running: boolean;
        errorDetails: SfdcApiErrorDetail[];
    } | null>(null);
    const [goodLeapShareRow, setGoodLeapShareRow] = useState<AgreementRow | null>(null);
    const [serviceFinanceModalRow, setServiceFinanceModalRow] = useState<AgreementRow | null>(null);
    const [sunlightFlow, setSunlightFlow] = useState<null | { mode: 'credit' | 'sendDocs'; row: AgreementRow }>(null);
    const [sunlightDelivery, setSunlightDelivery] = useState<'inPerson' | 'email'>('inPerson');
    const [sunlightIframeOpen, setSunlightIframeOpen] = useState(false);
    const [sunlightIframeUrl, setSunlightIframeUrl] = useState<string | null>(null);
    /** Trinity `GoodLeapRendererComponent.viewApplicationLink` — `GoodLeapIframeModelComponent` (pending → open Link in modal). */
    const [goodLeapIframeOpen, setGoodLeapIframeOpen] = useState(false);
    const [goodLeapIframeUrl, setGoodLeapIframeUrl] = useState<string | null>(null);
    /** Trinity `GoodLeapRendererComponent.viewEnfinApplicationLink` — `EnfinIframeModelComponent`. */
    const [enfinIframeOpen, setEnfinIframeOpen] = useState(false);
    const [enfinIframeUrl, setEnfinIframeUrl] = useState<string | null>(null);
    const [enfinCancelModal, setEnfinCancelModal] = useState<{ row: AgreementRow; appId: string } | null>(null);
    const [enfinCancelReason, setEnfinCancelReason] = useState('');
    const [enfinCancelReasonError, setEnfinCancelReasonError] = useState(false);
    const [enfinCancelBusy, setEnfinCancelBusy] = useState(false);
    /** Angular-style master-detail: which rows are expanded. */
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    /** Collapse entire partner section (blue header chevron). */
    const [partnerSectionCollapsed, setPartnerSectionCollapsed] = useState<Record<string, boolean>>({});
    const [quoteDetailsModal, setQuoteDetailsModal] = useState<null | { quoteId: string; partner: string; product: string; hoverEstimateID?: string | number | null }>(null);
    /** Angular `CreateSunnovaContractRenderer` → `CreateSunnovaContractComponent` modal (no API until “Generate Contract”). */
    const [signFinancePartnerContractRow, setSignFinancePartnerContractRow] = useState<AgreementRow | null>(null);
    const [viewTrinityModalOpen, setViewTrinityModalOpen] = useState(false);
    const [viewTrinityModalRow, setViewTrinityModalRow] = useState<AgreementRow | null>(null);
    const [viewTrinityModalMode, setViewTrinityModalMode] = useState<'contract' | 'proposal'>('contract');
    const [dialogBusy, setDialogBusy] = useState(false);
    const [dialogState, setDialogState] = useState<{
        mode: 'info' | 'warning' | 'error' | 'confirm';
        title?: React.ReactNode;
        content: React.ReactNode;
        okText?: string;
        cancelText?: string;
        width?: number;
        onOk?: () => void | Promise<void>;
    } | null>(null);
    /**
     * When `dialogState` is cleared, Ant Design still runs the close animation; `width={dialogState?.width ?? 560}` was
     * snapping wide confirms (e.g. 750) to 560 mid-transition. Keep the last shell width until the next open.
     */
    const lastDialogShellWidthRef = useRef(560);

    /** Dedicated shell so Good Leap timeline never shares `dialogState` with JSON debug dialogs (signing details, etc.). */
    const [goodLeapTimelineDialog, setGoodLeapTimelineDialog] = useState<{
        response: unknown;
        applicationId: string;
        applicationLink: string | null;
    } | null>(null);
    const closeGoodLeapTimelineDialog = useCallback(() => setGoodLeapTimelineDialog(null), []);

    const showInfoDialog = useCallback(
        (options: { title?: React.ReactNode; content: React.ReactNode; width?: number; okText?: string }) =>
            setDialogState({
                mode: 'info',
                title: options.title,
                content: options.content,
                width: options.width,
                okText: options.okText ?? 'OK',
            }),
        [],
    );
    const showWarningDialog = useCallback(
        (options: { title?: React.ReactNode; content: React.ReactNode; width?: number; okText?: string }) =>
            setDialogState({
                mode: 'warning',
                title: options.title,
                content: options.content,
                width: options.width,
                okText: options.okText ?? 'OK',
            }),
        [],
    );
    const showErrorDialog = useCallback(
        (options: { title?: React.ReactNode; content: React.ReactNode; width?: number; okText?: string }) =>
            setDialogState({
                mode: 'error',
                title: options.title,
                content: options.content,
                width: options.width,
                okText: options.okText ?? 'OK',
            }),
        [],
    );

    const openLoanSyncErrorsDialog = useCallback(
        (row: AgreementRow) => {
            const loanStatus = readLoanStatusRecord(row.loanStatus);
            showErrorDialog({
                title: 'Sync Errors',
                content: (
                    <LoanSyncErrorsDialogContent
                        loanStatus={loanStatus}
                        financePartner={row.financePartnerRaw || row.financePartner}
                        resyncErrors={loanResyncErrorsByRowKey[row.key]}
                    />
                ),
                width: 640,
                okText: 'Close',
            });
        },
        [showErrorDialog, loanResyncErrorsByRowKey],
    );

    const showConfirmDialog = useCallback(
        (options: {
            title?: React.ReactNode;
            content: React.ReactNode;
            okText?: string;
            cancelText?: string;
            width?: number;
            onOk?: () => void | Promise<void>;
        }) =>
            setDialogState({
                mode: 'confirm',
                title: options.title,
                content: options.content,
                okText: options.okText ?? 'Yes',
                cancelText: options.cancelText ?? 'No',
                width: options.width,
                onOk: options.onOk,
            }),
        [],
    );
    const closeDialog = useCallback(() => {
        if (dialogBusy) return;
        setDialogState(null);
    }, [dialogBusy]);
    const runDialogOk = useCallback(async () => {
        if (!dialogState) return;
        if (!dialogState.onOk) {
            setDialogState(null);
            return;
        }
        setDialogBusy(true);
        try {
            await dialogState.onOk();
            setDialogState(null);
        } finally {
            setDialogBusy(false);
        }
    }, [dialogState]);

    const salesPersonEmail = String(
        (opportunityDetails as { SalesPerson?: { Email?: string } } | null | undefined)?.SalesPerson?.Email ?? '',
    ).trim();

    /**
     * Tracks GoodLeap “loan number generated” transitions so we refresh `opportunityDetails` only when the
     * Loan Number is actually available (matches Angular detail renderer conditions).
     */
    const goodLeapLoanNumberGeneratedRef = useRef<Record<string, boolean>>({});

    /** Refreshes `opportunityDetails` so Finance Partner Sync lead chips update when GoodLeap loan # is generated. */
    const refreshOpportunityDetails = useCallback(async () => {
        const sf = salesforceId ?? (opportunityId != null ? String(opportunityId) : null);
        if (!sf) return;
        try {
            const detail = await opportunityInfoServices.getOpportunityDetail(String(sf));
            if (detail) setOpportunityDetails(detail);
        } catch {
            // Non-blocking: agreements grid can still refresh via `load()`.
        }
    }, [opportunityId, salesforceId, setOpportunityDetails]);

    const load = useCallback(async () => {
        if (!opportunityId) return;
        const seq = ++agreementsLoadSeq.current;
        setLoading(true);
        setError(null);
        let loadedRowCount = 0;
        /** Snapshot of rows from this load for pending-expand (avoids React state race with stale `rows`). */
        let rowsForExpand: AgreementRow[] | null = null;
        try {
            /** Angular `refreshData` / `contract-list.retrieveContract`: `RetrieveContract` before `getLoanStatus`. */
            const res = await quoteServices.retrieveContract(opportunityId);
            if (seq !== agreementsLoadSeq.current) return;
            try {
                await quoteServices.contractGetLoanStatusByOpportunity(opportunityId);
            } catch {
                /* non-blocking */
            }
            if (seq !== agreementsLoadSeq.current) return;

            if (seq !== agreementsLoadSeq.current) return;
            const qc = res?.QuoteContract ?? res?.quoteContract ?? [];
            const list = finalizeAgreementRowsFromQuoteContract(Array.isArray(qc) ? qc : []);
            const enriched = list.map(r => ({...r,  loadCalculatorDataExists: !!res.LoadCalculatorDataExists })); //OBA-5345
            const merged = await mergeServiceFinanceApplicationStatus(opportunityId as string | number, enriched);
            if (seq !== agreementsLoadSeq.current) return;

            // Fetch PPA signed products status
            if (agreementsCapability.enablePpaSignedView) {
                const salesDiv = String(opportunityDetails?.SalesPerson?.Sales_Division ?? '').trim();
                if (salesDiv === 'Sales- Direct') {
                    try {
                        const ppaRes = await contractService.getPpaSignedProductsStatus(opportunityId);
                        if (Array.isArray(ppaRes)) {
                            setPpaProductStatus(normalizePpaStatusFromApiRowArray(ppaRes, qc));
                        } else {
                            setPpaProductStatus({ allUnsigned: false, unsignedProducts: [], signedProductKeys: [] });
                        }
                    } catch {
                        setPpaProductStatus(null);
                    }
                } else {
                    setPpaProductStatus(null);
                }
            }

            const fList = merged ?? enriched;
            setRows(fList);

            // Refresh Finance Partner Sync lead ids ONLY when GoodLeap Loan Number becomes available.
            const currentRows = merged ?? list;
            loadedRowCount = currentRows.length;
            rowsForExpand = currentRows;
            let shouldRefreshOpp = false;
            for (const r of currentRows) {
                if (detectAgreementPartnerKind(r) !== 'goodLeap') continue;
                const idKey = String(r.contractIdRaw || r.quoteIdRaw || r.key);
                const appId = loanApplicationId(r);
                const st = String(goodLeapLoanApplicationStatus(r) || '').trim();
                const loanNumberGenerated =
                    !!appId && st !== 'Pending' && st !== 'AccountSubmitted' && st !== 'ApplicationSubmitted';
                const prev = !!goodLeapLoanNumberGeneratedRef.current[idKey];
                goodLeapLoanNumberGeneratedRef.current[idKey] = loanNumberGenerated;
                if (loanNumberGenerated && !prev) shouldRefreshOpp = true;
            }
            if (shouldRefreshOpp) {
                void refreshOpportunityDetails();
            }
        } catch (e: unknown) {
            if (seq !== agreementsLoadSeq.current) return;
            const msg = e && typeof e === 'object' && 'message' in e ? String((e as Error).message) : 'Failed to load agreements.';
            setError(msg);
            setRows([]);
            rowsForExpand = null;
        } finally {
            if (seq === agreementsLoadSeq.current) {
                if (rowsForExpand && rowsForExpand.length > 0) {
                    const pending = useOneButtonStore.getState().agreementsPendingExpandQuoteId;
                    if (pending) {
                        const target = String(pending).trim();
                        const match = rowsForExpand.find((r) => {
                            const raw = String(r.quoteIdRaw ?? '').trim();
                            const disp = String(r.quoteId ?? '')
                                .trim()
                                .replace(/^#/, '');
                            return raw === target || disp === target;
                        });
                        if (match) {
                            const sectionKey = String(match.financePartner || 'unknown').trim() || 'unknown';
                            setPartnerSectionCollapsed((prev) => ({ ...prev, [sectionKey]: false }));
                            setExpandedRowKeys([match.key]);
                        }
                        setAgreementsPendingExpandQuoteId(null);
                    }
                }
                setLoading(false);
            }
        }
    }, [
        opportunityId,
        opportunityDetails,
        refreshOpportunityDetails,
        setAgreementsPendingExpandQuoteId,
        setExpandedRowKeys,
        setPartnerSectionCollapsed,
    ]);

    /** Angular `create-sunnova-contract-renderer` `model.afterClose`: `refreshData(true)` → `retrieveContract`, `getLoanStatus()`, `getChecklistStatus()` (`GetPageChanges`). */
    const refreshAfterFinancePartnerContractSigningIframe = useCallback(() => {
        void (async () => {
            setAgreementsRefreshTrigger(Date.now());
            await load();
            const oid =
                opportunityId != null && String(opportunityId).trim() !== ''
                    ? (opportunityId as string | number)
                    : null;
            if (oid == null) return;
            try {
                await quoteServices.getPageChanges(oid);
            } catch {
                /* non-fatal */
            }
        })();
    }, [load, opportunityId, setAgreementsRefreshTrigger]);

    /** Single effect: opening the shell OR bumping `agreementsRefreshTrigger` must not double-call `load()` (was two effects + truthy trigger). */
    useEffect(() => {
        if (!opportunityId || !(embedded || open)) return;
        void load();
    }, [open, opportunityId, load, embedded, agreementsRefreshTrigger]);

    /** On Agreements open — refresh Finance Partner Contracts (`DesignFinanceSummaryCard` → `GetFinanceSelection`). */
    useEffect(() => {
        if (!opportunityId || !(embedded || open)) return;
        setFinanceSelectionRefreshTrigger(Date.now());
    }, [open, opportunityId, embedded, setFinanceSelectionRefreshTrigger]);

    /** Modal stays mounted with `open={false}`; clear expand/collapse UI so reopening starts from the initial view. */
    useEffect(() => {
        if (embedded) return;
        if (open) return;
        setExpandedRowKeys([]);
        setPartnerSectionCollapsed({});
        setAgreementsPendingExpandQuoteId(null);
    }, [open, embedded, setAgreementsPendingExpandQuoteId]);

    useEffect(() => {
        return () => {
            if (embedded) {
                setAgreementsPendingExpandQuoteId(null);
            }
        };
    }, [embedded, setAgreementsPendingExpandQuoteId]);

    const openPartnerFile = useCallback((row: AgreementRow) => {
        if (row.partnerFileUrl) {
            window.open(row.partnerFileUrl, '_blank', 'noopener,noreferrer');
        }
    }, []);

    const openSigning = useCallback(async (row: AgreementRow) => {
        if (!row.agreementIdRaw) {
            pushAgreementNotice('warning', 'No agreement id for this row.');
            return;
        }
        try {
            const res = await quoteServices.getAgreementSigningDetails(row.agreementIdRaw);
            const url = firstSigningLikeUrl(res);
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                pushAgreementNotice('info', 'No signing URL returned. Try “View document” if available.');
            }
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load signing details.';
            pushAgreementNotice('error', msg);
        }
    }, [pushAgreementNotice]);

    const openSigningDetailsPreview = useCallback(async (row: AgreementRow) => {
        if (!row.agreementIdRaw) {
            pushAgreementNotice('warning', 'No agreement id for this row.');
            return;
        }
        try {
            const res = await quoteServices.getAgreementSigningDetails(row.agreementIdRaw);
            showInfoDialog({
                title: 'Signing details (API)',
                width: 560,
                content: (
                    <pre className="obd-agreements-modal__json-pre">{JSON.stringify(res, null, 2).slice(0, 12000)}</pre>
                ),
            });
        } catch (e: unknown) {
            pushAgreementNotice('error', e instanceof Error ? e.message : 'Failed to load signing details.');
        }
    }, [showInfoDialog, pushAgreementNotice]);

    const closeViewTrinityModal = useCallback(() => {
        setViewTrinityModalOpen(false);
        setViewTrinityModalRow(null);
        setViewTrinityModalMode('contract');
        setAgreementsRefreshTrigger(Date.now());
    }, [setAgreementsRefreshTrigger]);

    const openTrinityContractView = useCallback(async (row: AgreementRow, mode: 'contract' | 'proposal' = 'contract') => {
        if (!row.quoteIdRaw) {
            pushAgreementNotice('warning', 'No quote id for this row.');
            return;
        }
        setViewTrinityModalMode(mode);
        setViewTrinityModalRow(row);
        setViewTrinityModalOpen(true);
    }, [pushAgreementNotice]);

    const runSfdcBundle = useCallback(
        async (row: AgreementRow, afterAllSuccess?: () => Promise<void>) => {
            if (!opportunityId || !row.quoteIdRaw) return;
            const oid = opportunityId as string | number;
            const qgid = row.quoteGroupId || undefined;
            const stepsInit: SfdcBundleStep[] = [
                { id: 'lead', label: 'SFDC Sync — Lead', status: 'pending' },
                { id: 'contact', label: 'SFDC Sync — Contact', status: 'pending' },
                { id: 'contract', label: 'SFDC Sync — Opportunity contract', status: 'pending' },
            ];
            setSfdcBundleUi({ row, steps: stepsInit, running: true, errorDetails: [] });

            const patchStep = (id: string, status: SfdcBundleStep['status'], detail?: string) => {
                setSfdcBundleUi((prev) => {
                    if (!prev || prev.row.key !== row.key) return prev;
                    return {
                        ...prev,
                        steps: prev.steps.map((s) => (s.id === id ? { ...s, status, detail } : s)),
                    };
                });
            };

            const runStep = async (id: 'lead' | 'contact' | 'contract', fn: () => Promise<void>) => {
                patchStep(id, 'running');
                try {
                    await fn();
                    patchStep(id, 'done');
                    const stepName = id === 'lead' ? 'SFDC Sync - Lead' : id === 'contact' ? 'SFDC Sync - Contact' : 'SFDC Sync - Opportunity Contract';
                    useOneButtonStore.getState().setSfdcSyncError(stepName, null);
                    return true;
                } catch (e: unknown) {
                    const { details, fallbackMessage } = parseSfdcSyncApiError(e);
                    const stepName = id === 'lead' ? 'SFDC Sync - Lead' : id === 'contact' ? 'SFDC Sync - Contact' : 'SFDC Sync - Opportunity Contract';
                    const errStr = fallbackMessage;
                    useOneButtonStore.getState().setSfdcSyncError(stepName, errStr);
                    if (details.length > 0) {
                        setSfdcBundleUi((prev) => {
                            if (!prev || prev.row.key !== row.key) return prev;
                            return { ...prev, errorDetails: [...prev.errorDetails, ...details] };
                        });
                        /** Match Angular: step row shows fail state; full text lives in grouped `ErrorDetails` below. */
                        patchStep(id, 'error');
                    } else {
                        patchStep(id, 'error', fallbackMessage);
                    }
                    return false;
                }
            };

            const okLead = await runStep('lead', () => quoteServices.sfdcSyncLead(oid, row.quoteIdRaw, qgid));
            const okContact = await runStep('contact', () =>
                quoteServices.sfdcSyncContactInformation(oid, row.quoteIdRaw, qgid),
            );
            const okContract = await runStep('contract', () => quoteServices.sfdcSyncContractSingle(oid, row.quoteIdRaw));

            try {
                await load();
            } catch {
                /* ignore */
            }

            setSfdcBundleUi((prev) => (prev && prev.row.key === row.key ? { ...prev, running: false } : prev));

            if (okLead && okContact && okContract) {
                pushAgreementNotice('success', 'Salesforce sync sequence completed.');
                if (afterAllSuccess) await afterAllSuccess();
            } else {
                pushAgreementNotice(
                    'warning',
                    'One or more Salesforce sync steps failed. Review the modal, fix issues, then retry or use individual sync actions.',
                );
            }
        },
        [opportunityId, load, pushAgreementNotice],
    );

    const openTrinityContractViewGated = useCallback(
        async (row: AgreementRow) => {
            await openTrinityContractView(row);
        },
        [openTrinityContractView],
    );



    const openCombinedPack = useCallback(
        async (row: AgreementRow) => {
            const sf = String(salesforceId || '').trim();
            if (!sf || !row.quoteIdRaw) {
                pushAgreementNotice('warning', 'Need Salesforce id and quote id for combined pack.');
                return;
            }
            try {
                const res = await quoteServices.retrieveCombinedPackTemplates({
                    salesforceId: sf,
                    financePartnerQuoteId: row.quoteIdRaw,
                    agreementType: 'Combined',
                });
                showInfoDialog({
                    title: 'Combined pack templates',
                    width: 600,
                    content: (
                        <pre className="obd-agreements-modal__json-pre">
                            {JSON.stringify(res, null, 2).slice(0, 8000)}
                        </pre>
                    ),
                });
            } catch (e: unknown) {
                const msg = e instanceof Error ? e.message : 'Failed to load combined pack templates.';
                pushAgreementNotice('error', msg);
            }
        },
        [salesforceId, showInfoDialog, pushAgreementNotice],
    );

    const openSfdcSyncLead = useCallback(
        async (row: AgreementRow) => {
            if (!opportunityId || !row.quoteIdRaw) return;
            try {
                await quoteServices.sfdcSyncLead(
                    opportunityId as string | number,
                    row.quoteIdRaw,
                    row.quoteGroupId || undefined,
                );
                useOneButtonStore.getState().setSfdcSyncError('SFDC Sync - Lead', null);
                pushAgreementNotice('success', 'Salesforce lead sync completed.');
                await load();
            } catch (e: unknown) {
                const { details, fallbackMessage } = parseSfdcSyncApiError(e);
                const errStr = fallbackMessage;
                useOneButtonStore.getState().setSfdcSyncError('SFDC Sync - Lead', errStr);
                pushAgreementNotice(
                    'error',
                    e instanceof Error ? e.message : 'Salesforce lead sync failed.',
                );
            }
        },
        [opportunityId, load, pushAgreementNotice],
    );

    const openSfdcSyncContact = useCallback(
        async (row: AgreementRow) => {
            if (!opportunityId || !row.quoteIdRaw) return;
            try {
                await quoteServices.sfdcSyncContactInformation(
                    opportunityId as string | number,
                    row.quoteIdRaw,
                    row.quoteGroupId || undefined,
                );
                useOneButtonStore.getState().setSfdcSyncError('SFDC Sync - Contact', null);
                pushAgreementNotice('success', 'Salesforce contact sync completed.');
                await load();
            } catch (e: unknown) {
                const { details, fallbackMessage } = parseSfdcSyncApiError(e);
                const errStr = fallbackMessage;
                useOneButtonStore.getState().setSfdcSyncError('SFDC Sync - Contact', errStr);
                pushAgreementNotice(
                    'error',
                    e instanceof Error ? e.message : 'Salesforce contact sync failed.',
                );
            }
        },
        [opportunityId, load, pushAgreementNotice],
    );

    const openSfdcSyncContractSingle = useCallback(
        async (row: AgreementRow) => {
            if (!opportunityId || !row.quoteIdRaw) return;
            try {
                await quoteServices.sfdcSyncContractSingle(opportunityId as string | number, row.quoteIdRaw);
                useOneButtonStore.getState().setSfdcSyncError('SFDC Sync - Opportunity Contract', null);
                pushAgreementNotice('success', 'Salesforce opportunity contract sync completed.');
                await load();
            } catch (e: unknown) {
                const { details, fallbackMessage } = parseSfdcSyncApiError(e);
                const errStr = fallbackMessage;
                useOneButtonStore.getState().setSfdcSyncError('SFDC Sync - Opportunity Contract', errStr);
                pushAgreementNotice(
                    'error',
                    e instanceof Error ? e.message : 'Salesforce contract sync failed.',
                );
            }
        },
        [opportunityId, load, pushAgreementNotice],
    );

    const openSignPartnerOrExternal = useCallback(
        async (row: AgreementRow) => {
            if (row.externalLink) {
                window.open(row.externalLink, '_blank', 'noopener,noreferrer');
                return;
            }
            if (!opportunityId || !row.leadIdRaw || !row.contractIdRaw || !row.quoteIdRaw) {
                pushAgreementNotice('warning', 'Partner portal requires lead id, contract id, and quote id on this row.');
                return;
            }
            try {
                const res = await quoteServices.signFinancePartnerContract({
                    opportunityId: opportunityId as string | number,
                    leadId: row.leadIdRaw,
                    FinancePartnerQuoteId: row.quoteIdRaw,
                    OpportunityContractId: row.contractIdRaw,
                    isEmail: false,
                });
                const url = firstSigningLikeUrl(res);
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
                else pushAgreementNotice('info', 'Partner sign response did not include a URL.');
            } catch (e: unknown) {
                pushAgreementNotice('error', e instanceof Error ? e.message : 'Partner sign request failed.');
            }
        },
        [opportunityId, pushAgreementNotice],
    );

    const openSignPartnerEmail = useCallback(
        async (row: AgreementRow) => {
            if (!opportunityId || !row.leadIdRaw || !row.contractIdRaw || !row.quoteIdRaw) {
                pushAgreementNotice('warning', 'Email link requires lead id, contract id, and quote id on this row.');
                return;
            }
            try {
                const res = await quoteServices.signFinancePartnerContract({
                    opportunityId: opportunityId as string | number,
                    leadId: row.leadIdRaw,
                    FinancePartnerQuoteId: row.quoteIdRaw,
                    OpportunityContractId: row.contractIdRaw,
                    isEmail: true,
                });
                const url = firstSigningLikeUrl(res);
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
                else
                    pushAgreementNotice(
                        'success',
                        'Partner sign-by-email was requested. Check notifications or partner portal.',
                    );
            } catch (e: unknown) {
                pushAgreementNotice('error', e instanceof Error ? e.message : 'Partner email sign request failed.');
            }
        },
        [opportunityId, pushAgreementNotice],
    );

    const refreshAgreementRow = useCallback(
        async (row: AgreementRow) => {
            /** Trinity `sunlight-renderer.onRefreshClick` → `GenerateContractService.getLoanStatus(opportunityId, ContractId)` (same path as GoodLeap loan refresh). */
            if (detectAgreementPartnerKind(row) === 'sunlight') {
                if (!opportunityId || !row.contractIdRaw) {
                    pushAgreementNotice('warning', 'Missing opportunity or contract id for Sunlight.');
                    return;
                }
                try {
                    await quoteServices.goodLeapGetLoanStatus(opportunityId as string | number, row.contractIdRaw);
                    pushAgreementNotice('success', 'Loan status refreshed.');
                    await load();
                } catch (e: unknown) {
                    pushAgreementNotice('error', e instanceof Error ? e.message : 'Sunlight loan status refresh failed.');
                }
                return;
            }
            if (!row.agreementIdRaw) return;
            try {
                await quoteServices.updateAgreementSignDetails(row.agreementIdRaw);
                pushAgreementNotice('success', 'Agreement details updated.');
                await load();
            } catch (e: unknown) {
                pushAgreementNotice('error', e instanceof Error ? e.message : 'Agreement refresh failed.');
            }
        },
        [load, opportunityId, pushAgreementNotice],
    );

    /**
     * `updateReadyToSignStatus` (driven by `ReadyToSignRendererComponent.changeReadyToSign` after `updateIsReadyToSign` returns `true`).
     */
    const updateReadyToSignStatus = useCallback((quoteId: string, isReadyToSign: boolean) => {
        setRows((prev) =>
            prev.map((r) => (String(r.quoteIdRaw) === String(quoteId) ? { ...r, isReadyToSign } : r)),
        );
    }, []);

    /**
     *`changeReadyToSign` (loader + `GenerateContractService.updateIsReadyToSign`, strict `res === true` to apply local state).
     */
    const onReadyToSignChange = useCallback(
        async (row: AgreementRow, checked: boolean) => {
            const quoteId = String(row.quoteIdRaw ?? '').trim();
            if (!quoteId) {
                pushAgreementNotice('error', 'Missing quote id for Ready to Sign.');
                return;
            }
            const busyKey = `${row.key}:readyToSign`;
            setActionBusyKey(busyKey);
            try {
                const res = await contractService.updateIsReadyToSign(quoteId, checked);
                if (res === true) {
                    updateReadyToSignStatus(quoteId, checked);
                } else {
                    pushAgreementNotice('success', 'Problem updating IsReadyToSign');
                }
            } catch {
                pushAgreementNotice('error', 'Something went wrong, Please try again');
            } finally {
                setActionBusyKey((k) => (k === busyKey ? null : k));
            }
        },
        [updateReadyToSignStatus, pushAgreementNotice],
    );

    /** Do not call `load()` here — closing the modal is not a data change; `GoodLeapShareLoanModal` invokes `onCompleted` after successful submit / iframe link / email sent. */
    const closeGoodLeapShareModal = useCallback(() => {
        setGoodLeapShareRow(null);
    }, []);

    /** Trinity `GoodLeapRendererComponent.handleClick` → design readiness (non-Sunnova loan) → `GoodLeapShareLoanModelComponent`. */
    const openGoodLeapSubmitApplication = useCallback(
        async (row: AgreementRow) => {
            if (!opportunityId) {
                pushAgreementNotice('warning', 'No opportunity id.');
                return;
            }
            if (!row.contractIdRaw) {
                pushAgreementNotice(
                    'warning',
                    'This row needs Contract_Id (opportunity contract) before submitting to GoodLeap.',
                );
                return;
            }
            const fm = (row.financeMethod || '').toLowerCase();
            if (!fm.includes('loan')) {
                try {
                    const r = await quoteServices.GetOneDrawDesignReadiness(opportunityId);
                    if (r && !r.hasSystemLosses) {
                        showWarningDialog({
                            title: 'Design losses required',
                            content:
                                'Design losses are missing in oneBUTTON. Re-finalize the design in oneDRAW, then refresh and save the Design page. After that you can submit the GoodLeap loan application.',
                            okText: 'OK',
                        });
                        return;
                    }
                } catch {
                    /* non-blocking if readiness endpoint fails */
                }
            }
            try {
                await quoteServices.goodLeapGetLoanStatus(opportunityId as string | number, row.contractIdRaw);
            } catch {
                /* row may still be submittable; share modal will call loanApplication */
            }
            try {
                const res = await quoteServices.retrieveContract(opportunityId);
                const qc = res?.QuoteContract ?? res?.quoteContract ?? [];
                const list = finalizeAgreementRowsFromQuoteContract(Array.isArray(qc) ? qc : []);
                setRows(list);
                const fresh =
                    list.find((x) => x.key === row.key) ??
                    list.find((x) => x.contractIdRaw === row.contractIdRaw && x.quoteIdRaw === row.quoteIdRaw) ??
                    row;
                setGoodLeapShareRow(fresh);
            } catch (e: unknown) {
                pushAgreementNotice('error', e instanceof Error ? e.message : 'Failed to refresh agreements before GoodLeap.');
                setGoodLeapShareRow(row);
            }
        },
        [opportunityId, showWarningDialog, pushAgreementNotice],
    );

    const refreshGoodLeapLoanStatus = useCallback(
        async (row: AgreementRow) => {
            if (!opportunityId || !row.contractIdRaw) {
                pushAgreementNotice('warning', `Missing opportunity or contract id for ${row.financePartner}.`);
                return;
            }
            try {
                await quoteServices.goodLeapGetLoanStatus(opportunityId as string | number, row.contractIdRaw);
                pushAgreementNotice('success', `${row.financePartner} loan status refreshed.`);
                await load();
            } catch (e: unknown) {
                pushAgreementNotice('error', e instanceof Error ? e.message : `${row.financePartner} refresh failed.`);
            }
        },
        [opportunityId, load, pushAgreementNotice],
    );

    /** Trinity `GoodLeapRendererComponent.CheckSystemDesignLosses` → `changeOrder` (`CreateChangeOrder` + `onRefreshClick` + `getUpdateLoanStatus`). */
    const submitGoodLeapChangeOrder = useCallback(
        async (row: AgreementRow) => {
            if (!opportunityId) {
                pushAgreementNotice('warning', 'No opportunity id.');
                return;
            }
            if (!row.contractIdRaw) {
                pushAgreementNotice('warning', 'This row needs Contract_Id before submitting a change order.');
                return;
            }
            /** `OpportunityConstant.CommonConstants.SunnovaLoanName` — `'Loan'`; skip design readiness when present. */
            const financeMethod = String(row.financeMethod ?? '');
            if (!financeMethod.includes('Loan')) {
                try {
                    const r = await quoteServices.GetOneDrawDesignReadiness(opportunityId);
                    if (r && !r.hasSystemLosses) {
                        showWarningDialog({
                            title: '',
                            content:
                                'Design losses are missing in oneBUTTON. Please re-finalize the design in oneDRAW, then refresh and save the Design page. Once done, you can complete and submit the Change Order.',
                            okText: 'OK',
                        });
                        return;
                    }
                } catch {
                    /* non-blocking if readiness endpoint fails (Angular has no loader on this step). */
                }
            }

            const busyKey = `${row.key}:gl-change-order`;
            setActionBusyKey(busyKey);
            try {
                const res = (await quoteServices.goodLeapCreateChangeOrder(
                    opportunityId as string | number,
                    row.contractIdRaw,
                )) as { IsSuccessful?: boolean; Message?: string; Data?: unknown };
                if (res?.IsSuccessful) {
                    pushAgreementNotice(
                        'success',
                        String(res?.Message ?? '').trim() || 'Change order submitted.',
                    );
                    try {
                        await quoteServices.goodLeapGetLoanStatus(opportunityId as string | number, row.contractIdRaw);
                    } catch {
                        /* Trinity `onRefreshClick` — continue to full reload */
                    }
                    try {
                        await quoteServices.contractGetLoanStatusByOpportunity(opportunityId as string | number);
                    } catch {
                        /* Trinity `getUpdateLoanStatus` — optional for global change-order cache */
                    }
                    await load();
                } else {
                    let errMsg = String(res?.Data ?? res?.Message ?? 'Submit change order failed.').trim();
                    /** Trinity frequently returns JSON strings with `{ message }`. */
                    try {
                        const parsed = JSON.parse(errMsg);
                        if (parsed?.message) errMsg = String(parsed.message);
                    } catch {
                        /* keep errMsg */
                    }
                    pushAgreementNotice('error', errMsg || 'Submit change order failed.');
                }
            } catch (e: unknown) {
                const ax = e as { response?: { data?: { Message?: string } }; message?: string };
                let errMsg =
                    String(ax?.response?.data?.Message ?? ax?.message ?? (e instanceof Error ? e.message : '')).trim() ||
                    'Submit change order failed.';
                try {
                    const parsed = JSON.parse(errMsg);
                    if (parsed?.message) errMsg = String(parsed.message);
                } catch {
                    /* keep errMsg */
                }
                pushAgreementNotice('error', errMsg);
            } finally {
                setActionBusyKey(null);
            }
        },
        [opportunityId, load, showWarningDialog, pushAgreementNotice],
    );

    /** Trinity `SunlightRendererComponent.viewApplicationLink` — `SunlightIframeModelComponent` with `LoanStatus.Link`. */
    const openSunlightApplicationIframe = useCallback((url: string) => {
        const u = url?.trim();
        if (!u) return;
        setSunlightIframeUrl(u);
        setSunlightIframeOpen(true);
    }, []);

    /** Trinity `GoodLeapRendererComponent.viewApplicationLink` — iframe modal, not `window.open`. */
    const openGoodLeapApplicationIframe = useCallback((url: string) => {
        const u = url?.trim();
        if (!u) return;
        setGoodLeapIframeUrl(u);
        setGoodLeapIframeOpen(true);
    }, []);

    /** Trinity `GoodLeapRendererComponent.viewEnfinApplicationLink`. */
    const openEnfinApplicationIframe = useCallback((url: string) => {
        const u = url?.trim();
        if (!u) return;
        setEnfinIframeUrl(u);
        setEnfinIframeOpen(true);
    }, []);

    /** Trinity `GoodLeapRendererComponent.handleClick` — Enfin branch (`CreateEnfinLoanApplcation`, `co: false`). */
    const openEnfinSubmitApplication = useCallback(
        async (row: AgreementRow) => {
            if (!opportunityId) {
                pushAgreementNotice('warning', 'No opportunity id.');
                return;
            }
            if (!row.contractIdRaw) {
                pushAgreementNotice(
                    'warning',
                    'This row needs Contract_Id (opportunity contract) before submitting to Enfin.',
                );
                return;
            }
            const busyKey = `${row.key}:enfin-submit`;
            setActionBusyKey(busyKey);
            try {
                const res = (await contractService.createEnfinLoanApplication({
                    opportunityId: opportunityId as string | number,
                    contractId: Number(row.contractIdRaw) as number,
                    co: false,
                })) as { EmbeddedUrl?: string; ApplicationId?: string; Reason?: string };
                if (res?.EmbeddedUrl && res?.ApplicationId) {
                    openEnfinApplicationIframe(res.EmbeddedUrl);
                } else if (res?.Reason) {
                    pushAgreementNotice('success', String(res.Reason));
                }
                try {
                    await quoteServices.goodLeapGetLoanStatus(opportunityId as string | number, row.contractIdRaw);
                } catch {
                    /* Trinity continues after refresh */
                }
                await load();
            } catch (e: unknown) {
                pushAgreementNotice('error', contractApiErrorMessage(e) || 'Enfin submit failed.');
            } finally {
                setActionBusyKey(null);
            }
        },
        [opportunityId, load, openEnfinApplicationIframe, pushAgreementNotice],
    );

    /** Trinity `GoodLeapRendererComponent.submitEnfinApplication` — change order (`co: true`). */
    const submitEnfinChangeOrder = useCallback(
        async (row: AgreementRow) => {
            if (!opportunityId) {
                pushAgreementNotice('warning', 'No opportunity id.');
                return;
            }
            if (!row.contractIdRaw) {
                pushAgreementNotice('warning', 'This row needs Contract_Id before submitting a change order.');
                return;
            }
            const busyKey = `${row.key}:enfin-change-order`;
            setActionBusyKey(busyKey);
            try {
                const res = (await contractService.createEnfinLoanApplication({
                    opportunityId: opportunityId as string | number,
                    contractId: row.contractIdRaw,
                    co: true,
                })) as { EmbeddedUrl?: string; ApplicationId?: string; Reason?: string };
                if (res?.EmbeddedUrl && res?.ApplicationId) {
                    openEnfinApplicationIframe(res.EmbeddedUrl);
                } else if (res?.Reason) {
                    pushAgreementNotice('success', String(res.Reason));
                }
                try {
                    await quoteServices.goodLeapGetLoanStatus(opportunityId as string | number, row.contractIdRaw);
                } catch {
                    /* continue */
                }
                await load();
            } catch (e: unknown) {
                pushAgreementNotice('error', contractApiErrorMessage(e) || 'Enfin change order failed.');
            } finally {
                setActionBusyKey(null);
            }
        },
        [opportunityId, load, openEnfinApplicationIframe, pushAgreementNotice],
    );

    const openEnfinCancelModal = useCallback((row: AgreementRow, appId: string) => {
        setEnfinCancelReason('');
        setEnfinCancelReasonError(false);
        setEnfinCancelModal({ row, appId });
    }, []);

    const runEnfinCancelApplication = useCallback(async () => {
        if (!enfinCancelModal || !opportunityId) return;
        if (!enfinCancelReason.trim()) {
            setEnfinCancelReasonError(true);
            return;
        }
        setEnfinCancelBusy(true);
        try {
            const res = (await contractService.cancelEnfinApplication({
                opportunityId: opportunityId as string | number,
                applicationNo: enfinCancelModal.appId,
                cancelReason: enfinCancelReason.trim(),
            })) as { Message?: string };
            pushAgreementNotice('success', String(res?.Message ?? 'Application cancelled.'));
            try {
                await quoteServices.contractGetLoanStatusByOpportunity(opportunityId as string | number);
            } catch {
                /* optional global refresh */
            }
            setEnfinCancelModal(null);
            await load();
        } catch (e: unknown) {
            pushAgreementNotice('error', contractApiErrorMessage(e) || 'Enfin cancel failed.');
        } finally {
            setEnfinCancelBusy(false);
        }
    }, [enfinCancelModal, enfinCancelReason, load, opportunityId, pushAgreementNotice]);

    const resendGoodLeapApplicationEmail = useCallback(
        async (row: AgreementRow) => {
            if (!opportunityId || !row.contractIdRaw) return;
            try {
                const res = await quoteServices.goodLeapResendEmail(opportunityId as string | number, row.contractIdRaw);
                const msg =
                    (res as { Message?: string })?.Message ??
                    (res as { message?: string })?.message ??
                    'Request completed.';
                showInfoDialog({
                    title: 'GoodLeap application',
                    width: 520,
                    content: <p style={{ whiteSpace: 'pre-wrap' }}>{String(msg)}</p>,
                });
            } catch (e: unknown) {
                const ax = e as { response?: { data?: { Message?: string } }; message?: string };
                const errMsg = ax?.response?.data?.Message ?? ax?.message ?? 'Resend email failed.';
                showErrorDialog({ title: 'GoodLeap', content: String(errMsg) });
            }
        },
        [opportunityId, showErrorDialog, showInfoDialog],
    );

    const openSignFinancePartnerContractModal = useCallback(
        (row: AgreementRow) => {
            if (!opportunityId) {
                pushAgreementNotice('warning', 'Need opportunity id.');
                return;
            }
            if (!row.quoteIdRaw) {
                pushAgreementNotice('warning', 'Need quote id on this row.');
                return;
            }
            setSignFinancePartnerContractRow(row);
        },
        [opportunityId, pushAgreementNotice],
    );

    const openRoofingProposalUrl = useCallback(async (row: AgreementRow) => {
        void openTrinityContractView(row, 'proposal');
    }, [openTrinityContractView]);

    const openQuoteDetailsFromRow = useCallback(
        (row: AgreementRow) => {
            if (!row.quoteIdRaw) {
                pushAgreementNotice('warning', 'No quote id for this row.');
                return;
            }
            setQuoteDetailsModal({
                quoteId: row.quoteIdRaw,
                partner: row.financePartnerRaw || row.financePartner,
                product: quoteDetailsProductLabel(row),
                hoverEstimateID: row.hoverEstimateID,
            });
        },
        [pushAgreementNotice],
    );

    /** Angular: IGS uses `ViewTrinityContractRenderer` (Trinity Documents); other partners use `ViewProposalRenderer` → Quote modal. */
    const openViewPartnerAgreementWorkflow = useCallback(
        async (row: AgreementRow) => {
            if (isIgsFinancePartnerRow(row)) {
                await openTrinityContractViewGated(row);
            } else {
                openQuoteDetailsFromRow(row);
            }
        },
        [openTrinityContractViewGated, openQuoteDetailsFromRow],
    );

    const openServiceFinanceSubmit = useCallback((row: AgreementRow) => {
        setServiceFinanceModalRow(row);
    }, []);

    /** Trinity `ServiceFinanceRendererComponent.getServiceFinanceApplicationStatus` (`getServiceFincaeContractStatus`). */
    const refreshServiceFinanceContractStatus = useCallback(
        async (row: AgreementRow) => {
            const oid = opportunityId;
            const cid = row.opportunityContractIdRaw || row.contractIdRaw;
            if (!oid || !cid) {
                pushAgreementNotice('warning', 'Need opportunity id and opportunity contract id on this row.');
                return;
            }
            setActionBusyKey(`${row.key}:sf-refresh`);
            try {
                const res = await contractService.getOpportunityContractStatus(oid as string | number, cid);
                const st =
                    (res as { ContractStatus?: string })?.ContractStatus ??
                    (res as { contractStatus?: string })?.contractStatus;
                if (st) {
                    setRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, contractStatus: String(st) } : r)));
                    pushAgreementNotice('success', `Contract status: ${st}`);
                }
                await load();
            } catch (e: unknown) {
                pushAgreementNotice('error', e instanceof Error ? e.message : 'Refresh failed.');
            } finally {
                setActionBusyKey(null);
            }
        },
        [opportunityId, load, pushAgreementNotice],
    );

    const refreshLightreachFormStatus = useCallback(
        async (row: AgreementRow) => {
            const oid = opportunityId;
            const cid = row.opportunityContractIdRaw || row.contractIdRaw;
            if (!oid || !cid) {
                pushAgreementNotice('warning', 'Need opportunity and contract id.');
                return;
            }
            try {
                await contractService.getLightreachFormStatus(oid as string | number, cid);
                pushAgreementNotice('success', 'Lightreach status refreshed.');
                await load();
            } catch (e: unknown) {
                pushAgreementNotice('error', e instanceof Error ? e.message : 'Refresh failed.');
            }
        },
        [opportunityId, load, pushAgreementNotice],
    );

    const startSunlightCreditFlow = useCallback((row: AgreementRow) => {
        setSunlightDelivery('inPerson');
        setSunlightFlow({ mode: 'credit', row });
    }, []);

    const startSunlightSendDocsFlow = useCallback((row: AgreementRow) => {
        setSunlightDelivery('inPerson');
        setSunlightFlow({ mode: 'sendDocs', row });
    }, []);

    const runSunlightDeliveryConfirm = useCallback(async () => {
        if (!sunlightFlow || !opportunityId) return;
        const { mode, row } = sunlightFlow;
        try {
            if (mode === 'credit') {
                const res = (await contractService.sunlightCredit(
                    opportunityId as string | number,
                    row.quoteIdRaw,
                    sunlightDelivery,
                )) as {
                    IsSuccessful?: boolean;
                    Message?: string;
                    Data?: unknown;
                };
                if (res?.IsSuccessful && sunlightDelivery === 'inPerson' && res.Data) {
                    const url =
                        typeof res.Data === 'string'
                            ? res.Data
                            : firstSigningLikeUrl(res.Data) ||
                              (typeof (res.Data as { url?: string })?.url === 'string'
                                  ? (res.Data as { url: string }).url
                                  : null);
                    if (url && /^https?:\/\//i.test(url)) {
                        setSunlightIframeUrl(url);
                        setSunlightIframeOpen(true);
                    } else {
                        pushAgreementNotice('info', 'Sunlight did not return an iframe URL.');
                    }
                } else if (res?.IsSuccessful && sunlightDelivery === 'email') {
                    pushAgreementNotice('success', res?.Message || 'Email request completed.');
                } else {
                    pushAgreementNotice('error', res?.Message || 'Sunlight credit request failed.');
                }
            } else {
                const res = (await contractService.sunlightSendLoanDocument(row.leadIdRaw, sunlightDelivery)) as {
                    IsSuccessful?: boolean;
                    Message?: string;
                    Data?: unknown;
                };
                if (res?.IsSuccessful && sunlightDelivery === 'inPerson' && res.Data) {
                    const url =
                        typeof res.Data === 'string'
                            ? res.Data
                            : firstSigningLikeUrl(res.Data) ||
                              (typeof (res.Data as { url?: string })?.url === 'string'
                                  ? (res.Data as { url: string }).url
                                  : null);
                    if (url && /^https?:\/\//i.test(url)) {
                        setSunlightIframeUrl(url);
                        setSunlightIframeOpen(true);
                    } else {
                        pushAgreementNotice('success', res?.Message || 'Documents flow completed.');
                    }
                } else if (sunlightDelivery === 'email' && res?.IsSuccessful) {
                    pushAgreementNotice('success', res?.Message || 'Email sent.');
                } else {
                    pushAgreementNotice('error', res?.Message || 'Send loan documents failed.');
                }
            }
            setSunlightFlow(null);
            await load();
        } catch (e: unknown) {
            pushAgreementNotice('error', e instanceof Error ? e.message : 'Sunlight request failed.');
        }
    }, [sunlightFlow, opportunityId, sunlightDelivery, load, pushAgreementNotice]);

    const partnerWorkflowHandlers = useMemo(
        () => ({
            signFinancePartnerContract: openSignFinancePartnerContractModal,
            openRoofingProposalUrl,
            openViewPartnerAgreement: openViewPartnerAgreementWorkflow,
            openServiceFinanceSubmit,
            refreshServiceFinanceContractStatus,
            refreshLightreachFormStatus,
            startSunlightCreditFlow,
            startSunlightSendDocsFlow,
        }),
        [
            openSignFinancePartnerContractModal,
            openRoofingProposalUrl,
            openViewPartnerAgreementWorkflow,
            openServiceFinanceSubmit,
            refreshServiceFinanceContractStatus,
            refreshLightreachFormStatus,
            startSunlightCreditFlow,
            startSunlightSendDocsFlow,
        ],
    );

    const sfdcBundleRunning = !!sfdcBundleUi?.running;

    /** One section per finance partner; avoids per-render regroup and keeps the list stable for the partner tables. */
    const agreementPartnerSections = useMemo(() => groupRowsByPartner(rows), [rows]);

    /**
     * Per-partner columns: Trinity `CashContractFields` has no “Submit Application”; only Good Leap / Sunlight grids do.
     */
    const getAgreementGridColumns = useCallback(
        (kind: AgreementPartnerKind, sectionRows: AgreementRow[] = []): ColumnsType<AgreementRow> => {
            /** Trinity `SubmitQuoteRendererComponent`: `forEachNode` — true if any row in this partner grid has `SystemId`. */
            const lightreachPartnerGridHasSystemId = sectionRows.some((r) => String(r.systemIdRaw ?? '').trim());
            const cols: ColumnsType<AgreementRow> = [
                {
                    title: 'Product Type',
                    dataIndex: 'productType',
                    key: 'productType',
                    width: 88,
                    ellipsis: true,
                    sortable: true,
                    className: 'obd-agreements-modal__col-product-type',
                    onHeaderCell: () => ({ className: 'obd-agreements-modal__th-product-type' }),
                },
            ];

            const addReadyToSign = () =>
                cols.push({
                    title: 'Ready to Sign',
                    key: 'readyToSign',
                    width: 84,
                    align: 'center',
                    className: 'obd-agreements-modal__col-ready-to-sign',
                    onHeaderCell: () => ({ className: 'obd-agreements-modal__th-ready-to-sign' }),
                    render: (_, row) => {
                        const busy = actionBusyKey === `${row.key}:readyToSign`;
                        const noQuote = !String(row.quoteIdRaw ?? '').trim();
                        return (
                            <Checkbox
                                checked={row.isReadyToSign}
                                disabled={busy || noQuote}
                                onChange={(e) => void onReadyToSignChange(row, e.target.checked)}
                            />
                        );
                    },
                });

            const addQuoteId = () => cols.push({
                title: 'Quote ID',
                key: 'quoteId',
                width: 110,
                ellipsis: true,
                render: (_: unknown, row) => {
                    const q = String(row.quoteId ?? '').trim();
                    if (!q || q === '—') return '—';
                    const label = /^quote\s/i.test(q) ? q : `Quote ${q}`;
                    return (
                        <button type="button" className="obd-agreements-modal__quote-link" onClick={() => openQuoteDetailsFromRow(row)}>
                            {label}
                        </button>
                    );
                },
            });

            const addFinancePartnerSystemId = (title = 'Finance Partner System Id') => cols.push({
                title,
                dataIndex: 'systemIdRaw',
                key: 'systemId',
                width: 150,
                ellipsis: true,
            });

            const addLoanId = () => cols.push({
                title: 'Loan ID',
                dataIndex: 'financePartnerContractIdRaw',
                key: 'financePartnerContractId',
                width: 110,
                ellipsis: true,
            });

            const addPurchaseMethod = () => cols.push({
                title: 'Purchase Method',
                dataIndex: 'financeMethod',
                key: 'financeMethod',
                ellipsis: true,
                width: 120,
            });

            const addTotalEpc = () => cols.push({
                title: 'Total EPC',
                dataIndex: 'totalEpcDisplay',
                key: 'totalEpcDisplay',
                width: 96,
            });

            const addPrimarySignee = () => cols.push({
                title: 'Primary Signee',
                dataIndex: 'primaryContactDisplay',
                key: 'primaryContactDisplay',
                ellipsis: true,
                width: 120,
            });

            const addCreditStatus = () => cols.push({
                title: 'Credit Status',
                key: 'creditStatus',
                width: 100,
                render: (_, row) => {
                    const status = sunlightCreditStatus(row) || row.creditStatusRaw || '';
                    if (status) return <span className="obd-agreements-modal__cell-text">{status}</span>;
                    /** Angular `CreditStatusRendererComponent`: green dollar icon, matTooltip “Credit Status”, opens Check Credit modal. */
                    const partnerLabel = String(row.financePartner || row.financePartnerRaw || '').trim() || null;
                    return (
                        <div className="obd-agreements-modal__angular-grid-cell">
                            <Tooltip title="Credit Status">
                                <button
                                    type="button"
                                    className="obd-agreements-modal__icon-btn obd-agreements-modal__credit-status-check-btn"
                                    aria-label="Credit Status"
                                    onClick={() => setCreditCheckModalOpen(true, partnerLabel)}
                                >
                                    <CreditStatusGridIcon />
                                </button>
                            </Tooltip>
                        </div>
                    );
                }
            });

            const addSubmitApplication = (title = 'Submit Application', mode: 'goodLeap' | 'enfin' | 'sunlight' | 'serviceFinance') => {

                cols.push({
                    title,
                    key: `submit-${mode}`,
                    width: AGREEMENT_SUBMIT_APPLICATION_COL_WIDTH,
                    align: 'center',
                    ellipsis: true,
                    render: (_: unknown, row) => {
                        if (mode === 'goodLeap') {
                            return (
                                <GoodLeapSubmitApplicationCell
                                    row={row}
                                    opportunityId={opportunityId}
                                    actionBusyKey={actionBusyKey}
                                    setActionBusyKey={setActionBusyKey}
                                    openGoodLeapSubmitApplication={openGoodLeapSubmitApplication}
                                    submitGoodLeapChangeOrder={submitGoodLeapChangeOrder}
                                    refreshGoodLeapLoanStatus={refreshGoodLeapLoanStatus}
                                    openGoodLeapApplicationIframe={openGoodLeapApplicationIframe}
                                />
                            );
                        }
                        if (mode === 'enfin') {
                            return (
                                <GoodLeapSubmitApplicationCell
                                    row={row}
                                    opportunityId={opportunityId}
                                    actionBusyKey={actionBusyKey}
                                    setActionBusyKey={setActionBusyKey}
                                    openGoodLeapSubmitApplication={openEnfinSubmitApplication}
                                    submitGoodLeapChangeOrder={submitEnfinChangeOrder}
                                    refreshGoodLeapLoanStatus={refreshGoodLeapLoanStatus}
                                    openGoodLeapApplicationIframe={openEnfinApplicationIframe}
                                    busyPrefix="enfin"
                                    partnerName="Enfin"
                                /> 
                            );
                        }
                        if (mode === 'sunlight') {
                            return (
                                <SunlightPartnerSubmitCell
                                    row={row}
                                    opportunityId={opportunityId}
                                    actionBusyKey={actionBusyKey}
                                    setActionBusyKey={setActionBusyKey}
                                    openSignPartnerOrExternal={openSignPartnerOrExternal}
                                    refreshAgreementRow={refreshAgreementRow}
                                    openSunlightApplicationIframe={openSunlightApplicationIframe}
                                />
                            );
                        }
                        if (mode === 'serviceFinance') {
                            return (
                                <ServiceFinanceSubmitCell
                                    row={row}
                                    actionBusyKey={actionBusyKey}
                                    openServiceFinanceSubmit={openServiceFinanceSubmit}
                                />
                            );
                        }
                        return <span className="obd-agreements-modal__cell-muted">—</span>;
                    },
                });
            };

            const addViewFinancePartnerAgreement = () => cols.push({
                title: 'View Finance Partner Agreement',
                key: 'view-fp-agreement',
                width: 150,
                align: 'center',
                render: (_, row) => {
                    const cell = viewFinancePartnerAgreementCellState(row);
                    if (cell === 'hidden') return <span className="obd-agreements-modal__cell-muted">—</span>;
                    if (cell === 'spinner') {
                        return (
                            <div className="obd-agreements-modal__angular-grid-cell">
                                <LoadingOutlined className="obd-agreements-modal__angular-orange-spin" spin aria-hidden />
                            </div>
                        );
                    }
                    if (cell === 'muted') return <span className="obd-agreements-modal__cell-muted">—</span>;
                    return (
                        <div className="obd-agreements-modal__angular-grid-cell">
                            <Tooltip title="View Finance Partner Agreement">
                                <button
                                    type="button"
                                    className="obd-agreements-modal__icon-btn"
                                    aria-label="View Finance Partner Agreement"
                                    disabled={!row.quoteIdRaw}
                                    onClick={() => {
                                        if (!row.quoteIdRaw) return;
                                        openQuoteDetailsFromRow(row);
                                    }}
                                >
                                    <Eye size={AGREEMENT_TOOL_ICON_PX} weight="duotone" className="obd-agreements-modal__ag-icon" />
                                </button>
                            </Tooltip>
                        </div>
                    );
                },
            });

            const addSignFinancePartnerContract = (title = 'Sign Finance Partner Contract') => cols.push({
                title,
                key: 'sign-fp-contract',
                width: AGREEMENT_SIGN_FINANCE_PARTNER_CONTRACT_COL_WIDTH,
                align: 'center',
                render: (_, row) => {
                    const kind = detectAgreementPartnerKind(row);
                    const contractStLabel =
                        row.contractStatus === '—' ? '' : String(row.contractStatus || '').trim();

                    /** Signed state is shown even when `canOffer…` is false (Lightreach needs SystemId + credit to sign). */
                    if (isSignFinancePartnerContractColumnSigned(row)) {
                        return (
                            <div className="obd-agreements-modal__angular-grid-cell">
                                <Tooltip title="Signature Status: Signed">
                                    <span
                                        className="obd-agreements-modal__sign-fp-signed-mark"
                                        aria-label="Partner finance contract signed"
                                    >
                                        {trinityStatusGlyph('Signed')}
                                    </span>
                                </Tooltip>
                            </div>
                        );
                    }

                    const can = canOfferSignFinancePartnerContract(row, kind);
                    if (can) {
                        /** Angular `create-sunnova-contract-renderer` `visible_sign_link`: `[title]="'Partner Contract - '+params.data.contractStatus"` + `getIconClass(contractStatus)` (e.g. envelope for `Sent`). */
                        const partnerContractTip = `Partner Contract - ${contractStLabel || row.contractStatus}`;
                        return (
                            <div className="obd-agreements-modal__angular-grid-cell obd-agreements-modal__sign-fp-actions">
                                <Tooltip title={partnerContractTip}>
                                    <button
                                        type="button"
                                        className="obd-agreements-modal__icon-btn"
                                        aria-label={partnerContractTip}
                                        onClick={() => openSignFinancePartnerContractModal(row)}
                                    >
                                        {trinityStatusGlyph(contractStLabel)}
                                    </button>
                                </Tooltip>
                                {kind === 'lightreach' ? (
                                    <Tooltip title="Refresh">
                                        <button
                                            type="button"
                                            className="obd-agreements-modal__icon-btn"
                                            aria-label="Refresh Lightreach contract status"
                                            disabled={!row.opportunityContractIdRaw}
                                            onClick={() => {
                                                void (async () => {
                                                    const k = `${row.key}:sign-fp-lr-refresh`;
                                                    setActionBusyKey(k);
                                                    try {
                                                        await refreshLightreachFormStatus(row);
                                                    } finally {
                                                        setActionBusyKey(null);
                                                    }
                                                })();
                                            }}
                                        >
                                            {actionBusyKey === `${row.key}:sign-fp-lr-refresh` ? (
                                                <AgreementIconSpin />
                                            ) : (
                                                <ArrowsClockwise size={AGREEMENT_TOOL_ICON_PX} weight="bold" className="obd-agreements-modal__ag-icon" />
                                            )}
                                        </button>
                                    </Tooltip>
                                ) : null}
                            </div>
                        );
                    }

                    /** Angular `visible_view_link`: `SunnovaContractFileURL` with title `Partner Contract - {contractStatus}`. */
                    if (row.partnerFileUrl) {
                        const tip = `Partner Contract - ${contractStLabel || row.contractStatus}`;
                        return (
                            <div className="obd-agreements-modal__angular-grid-cell">
                                <Tooltip title={tip}>
                                    <button
                                        type="button"
                                        className="obd-agreements-modal__icon-btn"
                                        aria-label={tip}
                                        onClick={() => openPartnerFile(row)}
                                    >
                                        {trinityStatusGlyph(contractStLabel)}
                                    </button>
                                </Tooltip>
                            </div>
                        );
                    }

                    return <span className="obd-agreements-modal__cell-muted">—</span>;
                },
            });

            const addViewProposal = () => {
                // Angular: only Sunnova hides roof-related columns when roof_count === 0 (contract-list.component).
                // CashContractFields / IGS / Service Finance always include this column; cells show — when N/A.
                if (kind === 'sunnova' && roof_count === 0) return;
                const viewProposalTitle =
                    kind === 'sunnova' || kind === 'igs' ? 'View Proposal' : 'View Trinity Proposal';
                cols.push({
                    title: viewProposalTitle,
                    key: 'view-proposal',
                    width: 88,
                    align: 'center',
                    render: (_: unknown, row) => {
                        const inProgress = String(row.isContractSignedRawDisplay ?? '').toLowerCase() === 'in-progress';
                        if (inProgress) {
                            return (
                                <div className="obd-agreements-modal__angular-grid-cell">
                                    <LoadingOutlined className="obd-agreements-modal__angular-orange-spin" spin aria-hidden />
                                </div>
                            );
                        }
                        const can = canViewRoofSolarBatteryProposal(row) && !!row.quoteIdRaw;
                        const busy = actionBusyKey === `${row.key}:grid-roof`;
                        if (!can) return <span className="obd-agreements-modal__cell-muted">—</span>;
                        return (
                            <Tooltip title={viewProposalTitle}>
                                <button
                                    type="button"
                                    className={`obd-agreements-modal__icon-btn obd-agreements-modal__trinity-prop-btn${can ? ' obd-agreements-modal__trinity-prop-btn--ok' : ''}`}
                                    disabled={!can}
                                    aria-label={viewProposalTitle}
                                    onClick={() => {
                                        if (!can) return;
                                        void openTrinityContractView(row, 'proposal');
                                    }}
                                >
                                    {busy ? (
                                        <AgreementIconSpin />
                                    ) : (
                                        <Eye
                                            size={AGREEMENT_TOOL_ICON_PX}
                                            weight="duotone"
                                            className="obd-agreements-modal__ag-icon"
                                            aria-hidden
                                        />
                                    )}
                                </button>
                            </Tooltip>
                        );
                    },
                });
            };

            const addSfdcSync = () => cols.push({
                title: 'SFDC Sync',
                key: 'sfdc-sync',
                className: 'obd-agreements-modal__col-sfdc-sync',
                width: AGREEMENT_SFDC_SYNC_COL_WIDTH,
                minWidth: AGREEMENT_SFDC_SYNC_COL_WIDTH,
                align: 'center',
                render: (_: unknown, row) => {
                    const toolVis = agreementToolVisibility(detectAgreementPartnerKind(row));
                    if (!toolVis.sfdc) return <span className="obd-agreements-modal__cell-muted">—</span>;
                    return (
                        <AngularGridSfdcSyncCell
                            row={row}
                            sfdcBundleRunning={sfdcBundleRunning}
                            onSyncClick={(r) => void runSfdcBundle(r)}
                        />
                    );
                },
            });

            const addTrinityDocuments = () => cols.push({
                title: 'Trinity Documents',
                key: 'trinity-documents',
                className: 'obd-agreements-modal__col-trinity-documents',
                width: AGREEMENT_TRINITY_DOCUMENTS_COL_WIDTH,
                minWidth: AGREEMENT_TRINITY_DOCUMENTS_COL_WIDTH,
                align: 'center',
                render: (_: unknown, row) => {
                    const toolVis = agreementToolVisibility(detectAgreementPartnerKind(row));
                    if (!toolVis.documents || !toolVis.trinityContract) {
                        return <span className="obd-agreements-modal__cell-muted">—</span>;
                    }
                    return (
                        <AngularGridTrinityDocumentsCell
                            row={row}
                            onViewClick={(r) => void openTrinityContractViewGated(r)}
                        />
                    );
                },
            });

            /** Trinity `SubmitQuoteRenderer` / `LightReachCreateQuote` — only when `SystemId` is not set yet. */
            const addLightreachSubmitQuote = () =>
                cols.push({
                    title: 'Submit Quote',
                    key: 'lightreach-submit-quote',
                    width: 120,
                    align: 'center',
                    render: (_: unknown, row) => {
                        if (String(row.systemIdRaw ?? '').trim()) {
                            return <span className="obd-agreements-modal__cell-muted">—</span>;
                        }
                        const oid = opportunityId;
                        const cid = String(row.opportunityContractIdRaw ?? '').trim();
                        if (!oid || !cid) {
                            return <span className="obd-agreements-modal__cell-muted">—</span>;
                        }
                        const busy = actionBusyKey === `${row.key}:lr-submit-quote`;
                        /** Trinity `submit-quote-renderer.component.ts` `agInit`: `Is_Change_Order && Finance_Partner_Name == LightreachPartnerName`. */
                        const isChangeOrder = !!changeOrderDetails?.some((x) => {
                            const partner = String(x.Finance_Partner_Name ?? x.finance_Partner_Name ?? '').trim();
                            const co = !!(x.Is_Change_Order ?? x.is_Change_Order);
                            return co && partner === 'Lightreach';
                        });
                        return (
                            <div className="obd-agreements-modal__angular-grid-cell">
                                <Button
                                    type="primary"
                                    size="small"
                                    loading={busy}
                                    disabled={busy}
                                    onClick={() => {
                                        showConfirmDialog({
                                            title: isChangeOrder ? 'Submit Change Order' : 'Submit Quote',
                                            width: 750,
                                            content: (
                                                <div>
                                                    {lightreachPartnerGridHasSystemId ? (
                                                        <p>
                                                            Please note: Submitting this quote will override any previously submitted quotes or signed
                                                            contracts
                                                        </p>
                                                    ) : null}
                                                    {isChangeOrder ? (
                                                        <p>Are you sure you want to submit the change order?</p>
                                                    ) : (
                                                        <p>Are you sure you want to submit the quote to finance partner?</p>
                                                    )}
                                                </div>
                                            ),
                                            okText: 'Submit',
                                            onOk: async () => {
                                                setActionBusyKey(`${row.key}:lr-submit-quote`);
                                                try {
                                                    const res = (await contractService.lightReachCreateQuote(oid, cid)) as {
                                                        IsSuccessful?: boolean;
                                                        isSuccessful?: boolean;
                                                        Message?: string;
                                                        message?: string;
                                                    };
                                                    const lrOk =
                                                        res?.IsSuccessful === true || res?.isSuccessful === true;
                                                    if (lrOk) {
                                                        pushAgreementNotice(
                                                            'success',
                                                            String(res?.Message ?? res?.message ?? '').trim() ||
                                                                (isChangeOrder ? 'Change order submitted.' : 'Quote submitted.'),
                                                        );
                                                        /** Angular `refreshData(true)`: `retrieveContract`, `getLoanStatus`, `getChecklistStatus` (`GetPageChanges`). */
                                                        await load();
                                                        useOneButtonStore.getState().setQuotesRefreshTrigger(Date.now());
                                                        try {
                                                            await quoteServices.getPageChanges(oid);
                                                        } catch {
                                                            /* non-blocking */
                                                        }
                                                    } else {
                                                        pushAgreementNotice(
                                                            'error',
                                                            trinityContractMessageDescription(
                                                                res?.Message ?? res?.message,
                                                                isChangeOrder ? 'Submit change order failed.' : 'Submit quote failed.',
                                                            ),
                                                        );
                                                    }
                                                } catch (e: unknown) {
                                                    const ax = e as { response?: { data?: { Message?: string } } };
                                                    pushAgreementNotice(
                                                        'error',
                                                        trinityContractMessageDescription(
                                                            ax?.response?.data?.Message ??
                                                                contractApiErrorMessage(e) ??
                                                                (e instanceof Error ? e.message : ''),
                                                            isChangeOrder ? 'Submit change order failed.' : 'Submit quote failed.',
                                                        ),
                                                    );
                                                } finally {
                                                    setActionBusyKey(null);
                                                }
                                            },
                                        });
                                    }}
                                    className="obd-agreements-modal__submit-change-order-btn"
                                    icon={
                                        isChangeOrder ? (
                                            <ArrowsLeftRight
                                                size={14}
                                                weight="bold"
                                                className="obd-agreements-modal__submit-change-order-btn__icon"
                                                aria-hidden
                                            />
                                        ) : undefined
                                    }
                                >
                                    {isChangeOrder ? 'Submit Change Order' : 'Submit Quote'}
                                </Button>
                            </div>
                        );
                    },
                });

            switch (kind) {
                case 'sunnova':
                    addReadyToSign();
                    addQuoteId();
                    addFinancePartnerSystemId();
                    addPurchaseMethod();
                    addTotalEpc();
                    addCreditStatus();
                    addViewFinancePartnerAgreement();
                    addSignFinancePartnerContract();
                    addSfdcSync();
                    addTrinityDocuments();
                    addViewProposal();
                    break;
                case 'igs':
                    addReadyToSign();
                    addQuoteId();
                    addFinancePartnerSystemId();
                    addPurchaseMethod();
                    addTotalEpc();
                    addCreditStatus();
                    addViewFinancePartnerAgreement();
                    addSignFinancePartnerContract();
                    addSfdcSync();
                    addTrinityDocuments();
                    addViewProposal();
                    break;
                case 'lightreach':
                    addReadyToSign();
                    addQuoteId();
                    addFinancePartnerSystemId();
                    addPurchaseMethod();
                    addTotalEpc();
                    addCreditStatus();
                    addLightreachSubmitQuote();
                    addSignFinancePartnerContract();
                    addSfdcSync();
                    addTrinityDocuments();
                    break;
                case 'serviceFinance':
                    addQuoteId();
                    addLoanId();
                    addPurchaseMethod();
                    addTotalEpc();
                    addPrimarySignee();
                    if (agreementsCapability.showServiceFinanceSubmitView) {
                        addSubmitApplication('Submit Service Finance Application', 'serviceFinance');
                    }
                    addViewProposal();
                    addSfdcSync();
                    addTrinityDocuments();
                    break;
                case 'goodLeap':
                    addReadyToSign();
                    addQuoteId();
                    addPurchaseMethod();
                    addTotalEpc();
                    addPrimarySignee();
                    if (agreementsCapability.isGoodLeapView) {
                        addSubmitApplication('Submit Application', 'goodLeap');
                    }
                    addSfdcSync();
                    addTrinityDocuments();
                    break;
                case 'enfin':
                    addReadyToSign();
                    addQuoteId();
                    addPurchaseMethod();
                    addTotalEpc();
                    addPrimarySignee();
                    addSubmitApplication('Submit Application', 'enfin');
                    addSfdcSync();
                    addTrinityDocuments();
                    break;
                case 'sunlight':
                    addQuoteId();
                    addPurchaseMethod();
                    addTotalEpc();
                    addPrimarySignee();
                    if (agreementsCapability.isSunlightView) {
                        addSubmitApplication('Submit Application', 'sunlight');
                        addSignFinancePartnerContract('Sign Finance Partner Contract');
                    }
                    addSfdcSync();
                    addTrinityDocuments();
                    break;
                case 'cash':
                case 'other':
                    addQuoteId();
                    addPurchaseMethod();
                    addTotalEpc();
                    addPrimarySignee();
                    addViewProposal();
                    addSfdcSync();
                    addTrinityDocuments();
                    break;
            }

            return cols;
        },

        [
            actionBusyKey,
            load,
            openCombinedPack,
            openGoodLeapSubmitApplication,
            openEnfinSubmitApplication,
            submitEnfinChangeOrder,
            openEnfinApplicationIframe,
            submitGoodLeapChangeOrder,
            openGoodLeapApplicationIframe,
            openPartnerFile,
            openQuoteDetailsFromRow,
            openRoofingProposalUrl,
            openSigning,
            openSigningDetailsPreview,
            openSignPartnerEmail,
            openSignPartnerOrExternal,
            openSunlightApplicationIframe,
            openTrinityContractView,
            openTrinityContractViewGated,
            opportunityId,
            refreshAgreementRow,
            refreshGoodLeapLoanStatus,
            resendGoodLeapApplicationEmail,
            runSfdcBundle,
            salesforceId,
            sfdcBundleRunning,
            partnerWorkflowHandlers,
            roof_count,
            openSignFinancePartnerContractModal,
            setCreditCheckModalOpen,
            openServiceFinanceSubmit,
            refreshLightreachFormStatus,
            setActionBusyKey,
            showConfirmDialog,
            showInfoDialog,
            onReadyToSignChange,
            agreementsCapability,
            changeOrderDetails,
            pushAgreementNotice,
        ],
    );

    const agreementExpandable = useMemo(
        () => ({
            expandIcon: ({
                expanded,
                onExpand,
                record,
            }: {
                expanded: boolean;
                onExpand: (record: AgreementRow, e: React.MouseEvent<HTMLElement>) => void;
                record: AgreementRow;
            }) => (
                <button
                    type="button"
                    className="obd-agreements-modal__expand-chevron"
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Collapse row details' : 'Expand row details'}
                    onClick={(e) => onExpand(record, e)}
                >
                    {expanded ? <CaretDown size={14} weight="bold" aria-hidden /> : <CaretRight size={14} weight="bold" aria-hidden />}
                </button>
            ),
            expandedRowRender: (r: AgreementRow) => {
                const caps = agreementsCapability;
                const showGoodLeapAppButtons = computeShowGoodLeapAppButtons(r);
                const ptExpand = String(r.productTypeCode || r.productType || '').toUpperCase();
                /** Roof-only expand (RO / SR / SBR). Angular hides this layout for RR and RRR — they use the generic descriptions. */
                const showRoofFieldsForExpand =
                    ptExpand.includes('R') && ptExpand !== 'RR' && ptExpand !== 'RRR';
                const goodLeapRoofLoanMerged =
                    isGlOrEnfinPartner(detectAgreementPartnerKind(r)) &&
                    showRoofFieldsForExpand &&
                    Object.keys(readLoanStatusRecord(r.loanStatus)).length > 0;

                return (
                <div className="obd-agreements-modal__expand obd-agreements-modal__expand--angular">
                    {(() => {
                        const kind = detectAgreementPartnerKind(r);
                        const pt = String(r.productTypeCode || r.productType || '').toUpperCase();
                        const isRoofish = pt.includes('R');
                        const isBatteryish = pt.includes('B');
                        const isRR = pt === 'RR';
                        const isRRR = pt === 'RRR';
                        const isRO = pt === 'RO' || pt === 'R';
                        const isBO = pt === 'BO' || pt === 'B';
                        const epcLabel = isRoofish && !isRR && !isRRR ? 'Roofing Contract Amount' : 'Total EPC';
                        const systemSizeLabel = isRR ? 'RR System Size' : 'System Size';
                        /** Dedicated roof expand (RO / SR / SBR). RR skips roof fields; RRR uses generic + roof manufacturer/product/pricing. */
                        const showRoofFields = isRoofish && !isRR && !isRRR;
                        /** Angular: `includes('R') && !== 'RR'` — RRR shows manufacturer, product, pricing; Estimate Source stays hidden. */
                        const showRrrRoofDetailFields = isRoofish && !isRR;
                        const showGrossPpw = pt.includes('S') && kind !== 'sunnova';
                        const showSystemSize = !isRO && !isBO;
                        const showAnnualProduction = !isRO && !isBatteryish && !isRR;
                        const showQuoteExpiresIn = !isGlOrEnfinPartner(kind);
                        const financeMethodRaw = String(r.financeMethodRaw || r.financeMethod || '');
                        const showLoanAmounts = !isRRR && /loan/i.test(financeMethodRaw);
                        const purchaseMethod = String(r.purchaseMethodRaw || '').trim();
                        const showCash = kind === 'cash';
                        /** Dedicated cash S / BO / RO layouts. Angular cash templates are only those three; RR / RRR use the generic block. */
                        const useCashDedicatedLayout = showCash && (pt === 'S' || isBO || isRO);
                        const isSunnova = kind === 'sunnova';
                        const isIgs = kind === 'igs';
                        const isServiceFinance = kind === 'serviceFinance';
                        const pricingValueDisplay = r.pricingValueRaw != null ? formatMoney(r.pricingValueRaw) : '—';
                        const monthlyPaymentDisplay = r.monthlyPaymentRaw != null ? formatMoney(r.monthlyPaymentRaw) : '—';
                        const estimatedProductionDisplay =
                            r.estimatedProductionRaw != null ? `${formatNumberFixed(r.estimatedProductionRaw, 2)} Kwh` : '—';
                        const year1SavingsDisplay = r.year1SavingsRaw != null ? formatMoney(r.year1SavingsRaw) : '—';
                        const nonEcEpcDisplay = r.nonEcEpcRaw != null ? formatMoney(r.nonEcEpcRaw) : '—';
                        const nonEcPpwDisplay = r.nonEcPpwRaw != null ? formatMoney(r.nonEcPpwRaw) : '—';
                        const showNonEc = isIgs && pt.includes('S') && isIgsEnergyCommunity;
                        const showDownPayment =
                            kind !== 'sunnova' &&
                            kind !== 'goodLeap' &&
                            ((isBatteryish && purchaseMethod !== 'Battery Loan' && financeMethodRaw !== 'Battery Loan') ||
                                (pt.includes('BO') && purchaseMethod !== 'Battery Loan' && financeMethodRaw !== 'Battery Loan') ||
                                (purchaseMethod !== 'Loan' && pt.includes('RO')));

                        return (
                    useCashDedicatedLayout ? (
                        pt === 'S' ? (
                            <div className="obd-agreements-modal__detail-kv-flow">
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Finance Partner</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.financePartner)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">System Size</span>
                                    <span className="obd-agreements-modal__detail-v">{formatSystemSizeKwDisplay(r.systemSizeRaw)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Annual Production</span>
                                    <span className="obd-agreements-modal__detail-v">{formatAnnualKwhDisplay(r.annualProductionRaw)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Gross PPW</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.desiredPpwNetDisplay)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Selected Product</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.productTypeCode || r.productType)}</span>
                                </div>
                                {showQuoteExpiresIn ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Quote Expires In</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.quoteExpiresInDisplay)}</span>
                                    </div>
                                ) : null}
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Total EPC</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.totalEpcDisplay)}</span>
                                </div>
                                {caps.ccPaymentView && r.invoicesRaw != null ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Solar Invoices</span>
                                        <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.invoicesRaw)}</span>
                                    </div>
                                ) : null}
                                {caps.ccPaymentView && r.paymentsRaw != null ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Solar Payments</span>
                                        <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.paymentsRaw)}</span>
                                    </div>
                                ) : null}
                                {caps.ccPaymentView && r.balanceRemainingRaw != null ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Solar Balance Remaining</span>
                                        <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.balanceRemainingRaw)}</span>
                                    </div>
                                ) : null}
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Deposit Amount</span>
                                    <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.depositAmountDisplay)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Financed Amount</span>
                                    <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.financedAmountRaw)}</span>
                                </div>
                                <ContractSignedDetailRow
                                    ppaProductStatus={ppaProductStatus}
                                    setSectionLoading={setLoading}
                                    variant="gridCell"
                                    row={r}
                                    opportunityId={opportunityId}
                                    opportunityDetails={opportunityDetails}
                                    onUpdated={load}
                                    onConfirm={showConfirmDialog}
                                    ppaSignedCapabilityView={caps.enablePpaSignedView} pushNotice={pushAgreementNotice}
                                />
                            </div>
                        ) : pt === 'BO' ? (
                            <div className="obd-agreements-modal__detail-kv-flow">
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Finance Partner</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.financePartner)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Selected Product</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.productTypeCode || r.productType)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Pricing Tier</span>
                                    <span className="obd-agreements-modal__detail-v">{pricingTierAngularDisplay(r)}</span>
                                </div>
                                {showQuoteExpiresIn ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Quote Expires In</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.quoteExpiresInDisplay)}</span>
                                    </div>
                                ) : null}
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Battery Contract Amount</span>
                                    <span className="obd-agreements-modal__detail-v">{pricingValueDisplay}</span>
                                </div>
                                {caps.ccPaymentView && r.invoicesRaw != null ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Battery Invoices</span>
                                        <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.invoicesRaw)}</span>
                                    </div>
                                ) : null}
                                {caps.ccPaymentView && r.paymentsRaw != null ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Battery Payments</span>
                                        <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.paymentsRaw)}</span>
                                    </div>
                                ) : null}
                                {caps.ccPaymentView && r.balanceRemainingRaw != null ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Battery Balance Remaining</span>
                                        <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.balanceRemainingRaw)}</span>
                                    </div>
                                ) : null}
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Deposit Amount</span>
                                    <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.depositAmountDisplay)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Battery Price Subtotal</span>
                                    <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.batteryPriceSubTotalRaw)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Adder Price Subtotal(Net)</span>
                                    <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.batteryAdderSubTotalRaw)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Battery Manufacturer</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.batteryManufacturerNameRaw)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Battery Model</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.batteryModelNameRaw)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Battery Count</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.batteryQuantityRaw)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Financed Amount</span>
                                    <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.financedAmountRaw)}</span>
                                </div>
                                <ContractSignedDetailRow
                                    ppaProductStatus={ppaProductStatus}
                                    setSectionLoading={setLoading}
                                    variant="gridCell"
                                    row={r}
                                    opportunityId={opportunityId}
                                    opportunityDetails={opportunityDetails}
                                    onUpdated={load}
                                    onConfirm={showConfirmDialog}
                                    ppaSignedCapabilityView={caps.enablePpaSignedView} pushNotice={pushAgreementNotice}
                                />
                            </div>
                        ) : (
                            <div className="obd-agreements-modal__detail-kv-flow">
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Finance Partner</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.financePartner)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Roof Manufacturer</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.roofManufacturerDisplay)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Roof Product</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.roofProductDisplay)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Selected Product</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.productTypeCode || r.productType)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Pricing Tier</span>
                                    <span className="obd-agreements-modal__detail-v">{pricingTierAngularDisplay(r)}</span>
                                </div>
                                {purchaseMethod !== 'Loan' ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Down Payment</span>
                                        <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.downPaymentRaw)}</span>
                                    </div>
                                ) : null}
                                {showQuoteExpiresIn ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Quote Expires In</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.quoteExpiresInDisplay)}</span>
                                    </div>
                                ) : null}
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Roofing Contract Amount</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.totalEpcDisplay)}</span>
                                </div>
                                {caps.ccPaymentView && r.invoicesRaw != null ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Roof Invoices</span>
                                        <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.invoicesRaw)}</span>
                                    </div>
                                ) : null}
                                {caps.ccPaymentView && r.paymentsRaw != null ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Roof Payments</span>
                                        <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.paymentsRaw)}</span>
                                    </div>
                                ) : null}
                                {caps.ccPaymentView && r.balanceRemainingRaw != null ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Roof Balance Remaining</span>
                                        <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.balanceRemainingRaw)}</span>
                                    </div>
                                ) : null}
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Deposit Amount</span>
                                    <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.depositAmountDisplay)}</span>
                                </div>
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Financed Amount</span>
                                    <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.financedAmountRaw)}</span>
                                </div>
                                <AgreementEstimateSourceKv hoverEstimateID={r.hoverEstimateID} />
                                <ContractSignedDetailRow
                                    ppaProductStatus={ppaProductStatus}
                                    setSectionLoading={setLoading}
                                    variant="gridCell"
                                    row={r}
                                    opportunityId={opportunityId}
                                    opportunityDetails={opportunityDetails}
                                    onUpdated={load}
                                    onConfirm={showConfirmDialog}
                                    ppaSignedCapabilityView={caps.enablePpaSignedView} pushNotice={pushAgreementNotice}
                                />
                            </div>
                        )
                    ) : showRoofFields ? (
                        (() => {
                            const kindRf = detectAgreementPartnerKind(r);
                            const roofGlMerged =
                                isGlOrEnfinPartner(kindRf) &&
                                Object.keys(readLoanStatusRecord(r.loanStatus)).length > 0;
                            if (roofGlMerged) {
                                const appIdRf = loanApplicationId(r);
                                const refRf = loanReferenceNumber(r);
                                const statusRf = loanStatusText(r);
                                const submitsRf = loanSubmitStatusList(r);
                                const loanStatusRf = readLoanStatusRecord(r.loanStatus);
                                const financePartnerRf = r.financePartnerRaw || r.financePartner;
                                const resyncErrRf = loanResyncErrorsByRowKey[r.key];
                                const showSyncErrorsBtnRf = loanSyncHasAnyErrors(
                                    loanStatusRf,
                                    financePartnerRf,
                                    resyncErrRf,
                                );
                                const syncHooksRf = makeGoodLeapSyncHooks(r.key);
                                const hideUpRf = hideUploadAndResync(r);
                                const showURf = !hideUpRf && shouldShowUpload(r, 'Utility');
                                const showSDRf = !hideUpRf && shouldShowUpload(r, 'System Design');
                                const showDocRf = !hideUpRf && shouldShowUpload(r, 'Document Sent');
                                const { wrap: wrapRf, isBusy: isSyncBusyRf } = createLoanSyncBusyHandlers(
                                    r.key,
                                    actionBusyKey,
                                    setActionBusyKey,
                                );
                                return (
                                    <div className="obd-agreements-modal__expand-roof-goodleap">
                                        <div className="obd-agreements-modal__detail-kv-flow">
                                            <div className="obd-agreements-modal__detail-kv">
                                                <span className="obd-agreements-modal__detail-k">Finance Partner</span>
                                                <span className="obd-agreements-modal__detail-v">{dash(r.financePartner)}</span>
                                            </div>
                                            <div className="obd-agreements-modal__detail-kv">
                                                <span className="obd-agreements-modal__detail-k">Roofing Contract Amount</span>
                                                <span className="obd-agreements-modal__detail-v">{dash(r.totalEpcDisplay)}</span>
                                            </div>
                                            <div className="obd-agreements-modal__detail-kv">
                                                <span className="obd-agreements-modal__detail-k">Roof Manufacturer</span>
                                                <span className="obd-agreements-modal__detail-v">{dash(r.roofManufacturerDisplay)}</span>
                                            </div>
                                            <div className="obd-agreements-modal__detail-kv">
                                                <span className="obd-agreements-modal__detail-k">Roof Product</span>
                                                <span className="obd-agreements-modal__detail-v">{dash(r.roofProductDisplay)}</span>
                                            </div>
                                            <div className="obd-agreements-modal__detail-kv">
                                                <span className="obd-agreements-modal__detail-k">Pricing Tier</span>
                                                <span className="obd-agreements-modal__detail-v">{pricingTierAngularDisplay(r)}</span>
                                            </div>
                                            <div className="obd-agreements-modal__detail-kv">
                                                <span className="obd-agreements-modal__detail-k">Selected Product</span>
                                                <span className="obd-agreements-modal__detail-v">{dash(r.productTypeCode || r.productType)}</span>
                                            </div>
                                            <GoodLeapLoanNumberExpand
                                                row={r}
                                                opportunityId={opportunityId}
                                                onRefresh={load}
                                                enableGetCreditStatusView={caps.goodLeapGetCreditStatusView}
                                                pushNotice={pushAgreementNotice}
                                            />
                                            {(() => {
                                                const showInPerson = showGoodLeapAppButtons && canOpenGoodLeapInPerson(r, opportunityId);
                                                const showSendEmail =
                                                    caps.goodLeapSendCreditEmailView &&
                                                    showGoodLeapAppButtons &&
                                                    !!opportunityId &&
                                                    String(opportunityId).trim() !== '' &&
                                                    !!r.contractIdRaw;
                                                const canCancelRf = displayCancelApplicationButton(getCapabilityValue, r);
                                                if (!showInPerson && !showSendEmail && !canCancelRf) {
                                                    return (
                                                        <div className="obd-agreements-modal__detail-row-cell--spacer" aria-hidden />
                                                    );
                                                }
                                                return (
                                                    <div className="obd-agreements-modal__detail-kv obd-agreements-modal__detail-kv--expand-nested">
                                                        <span className="obd-agreements-modal__detail-k">Application</span>
                                                        <span className="obd-agreements-modal__detail-v">
                                                            <Space wrap size="small">
                                                                {showInPerson ? (
                                                                    <Button
                                                                        type="primary"
                                                                        size="small"
                                                                        onClick={() => {
                                                                            if (kindRf === 'enfin') {
                                                                                const url = pickGoodLeapApplicationUrl(r.loanStatus);
                                                                                if (url) openEnfinApplicationIframe(url);
                                                                            } else {
                                                                                void openGoodLeapSubmitApplication(r);
                                                                            }
                                                                        }}
                                                                    >
                                                                        In-person
                                                                    </Button>
                                                                ) : null}
                                                                {showSendEmail ? (
                                                                    <Button
                                                                        type="primary"
                                                                        size="small"
                                                                        onClick={() => void resendGoodLeapApplicationEmail(r)}
                                                                    >
                                                                        Send email link
                                                                    </Button>
                                                                ) : null}
                                                                {canCancelRf ? (
                                                                    <Button
                                                                        type="primary"
                                                                        danger
                                                                        size="small"
                                                                        className="obd-agreements-modal__cancel-app-btn"
                                                                        onClick={() => {
                                                                            if (kindRf === 'enfin') {
                                                                                if (appIdRf) openEnfinCancelModal(r, appIdRf);
                                                                                return;
                                                                            }
                                                                            showConfirmDialog({
                                                                                title: '',
                                                                                content: 'Are you sure you want to cancel this application?',
                                                                                okText: 'Yes',
                                                                                cancelText: 'No',
                                                                                onOk: async () => {
                                                                                    if (!opportunityId || !appIdRf) return;
                                                                                    await contractService.goodLeapCancelLoanApplication(
                                                                                        opportunityId as any,
                                                                                        appIdRf,
                                                                                    );
                                                                                    await load();
                                                                                },
                                                                            });
                                                                        }}
                                                                    >
                                                                        Cancel Application
                                                                    </Button>
                                                                ) : null}
                                                            </Space>
                                                        </span>
                                                    </div>
                                                );
                                            })()}
                                            <ContractSignedDetailRow
                                    ppaProductStatus={ppaProductStatus}
                                    setSectionLoading={setLoading}
                                                variant="gridCell"
                                                row={r}
                                                opportunityId={opportunityId}
                                                opportunityDetails={opportunityDetails}
                                                onUpdated={load}
                                                onConfirm={showConfirmDialog}
                                                ppaSignedCapabilityView={caps.enablePpaSignedView} pushNotice={pushAgreementNotice}
                                            />
                                            {refRf ? (
                                                <div className="obd-agreements-modal__detail-kv">
                                                    <span className="obd-agreements-modal__detail-k">Reference Number</span>
                                                    <span className="obd-agreements-modal__detail-v">{dash(refRf)}</span>
                                                </div>
                                            ) : (
                                                <div className="obd-agreements-modal__detail-row-cell--spacer" aria-hidden />
                                            )}
                                            {showLoanAmounts ? (
                                                <div className="obd-agreements-modal__detail-kv">
                                                    <span className="obd-agreements-modal__detail-k">Deposit Amount</span>
                                                    <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.depositAmountDisplay)}</span>
                                                </div>
                                            ) : null}
                                            {showLoanAmounts ? (
                                                <div className="obd-agreements-modal__detail-kv">
                                                    <span className="obd-agreements-modal__detail-k">Financed Amount</span>
                                                    <span className="obd-agreements-modal__detail-v">{dash(r.financedAmountDisplay)}</span>
                                                </div>
                                            ) : null}
                                            {statusRf || submitsRf.length ? (
                                                <div className="obd-agreements-modal__detail-kv-flow-span">
                                                    <div className="obd-agreements-modal__detail-kv">
                                                        <span className="obd-agreements-modal__detail-k">Loan Status</span>
                                                        <span className="obd-agreements-modal__detail-v">
                                                        {statusRf ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <span>{statusRf}</span>
                                                                {showSyncErrorsBtnRf ? (
                                                                    <Button
                                                                        size="small"
                                                                        type="primary"
                                                                        className="obd-agreements-modal__gl-timeline-btn"
                                                                        onClick={() => openLoanSyncErrorsDialog(r)}
                                                                    >
                                                                        View errors
                                                                    </Button>
                                                                ) : null}
                                                            </div>
                                                        ) : null}
                                                        {submitsRf.length ? (
                                                            <ul className="obd-agreements-modal__loan-status-bullets">
                                                                {submitsRf.map((s, i) => (
                                                                    <li key={`${i}-${s}`}>
                                                                        <span style={{ marginRight: 8 }}>{s}</span>
                                                                        {i === 0 && appIdRf && kindRf === 'goodLeap' && caps.goodLeapTimelineView ? (
                                                                            <Button
                                                                                size="small"
                                                                                type="primary"
                                                                                className="obd-agreements-modal__gl-timeline-btn"
                                                                                loading={actionBusyKey === `${r.key}:gl-timeline`}
                                                                                onClick={() =>
                                                                                    void wrapRf('gl-timeline', async () => {
                                                                                        const res = await contractService.goodLeapGetTimeline(
                                                                                            appIdRf,
                                                                                        );
                                                                                        setGoodLeapTimelineDialog({
                                                                                            response: res,
                                                                                            applicationId: String(appIdRf),
                                                                                            applicationLink: sunlightApplicationLink(r),
                                                                                        });
                                                                                    })
                                                                                }
                                                                            >
                                                                                Good Leap Timeline
                                                                            </Button>
                                                                        ) : null}
                                                                        {renderGoodLeapSubmitLineSyncIcons({
                                                                            statusText: s,
                                                                            row: r,
                                                                            appId: appIdRf,
                                                                            hideUpload: hideUpRf,
                                                                            actionBusyKey,
                                                                            opportunityId,
                                                                            wrap: wrapRf,
                                                                            load,
                                                                            pushNotice: pushAgreementNotice,
                                                                            syncHooks: syncHooksRf,
                                                                        })}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : null}
                                                    </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="obd-agreements-modal__detail-row-cell--spacer" aria-hidden />
                                            )}
                                            {(showURf || showSDRf || showDocRf) && appIdRf ? (
                                                <div className="obd-agreements-modal__detail-kv-flow-span">
                                                    <div className="obd-agreements-modal__detail-kv">
                                                        <span className="obd-agreements-modal__detail-k">Upload</span>
                                                        <span className="obd-agreements-modal__detail-v">
                                                            <Space wrap size="small">
                                                                {showURf ? (
                                                                    <Button
                                                                        type="primary"
                                                                        size="small"
                                                                        loading={isSyncBusyRf('Utility')}
                                                                        onClick={() =>
                                                                            void wrapRf(goodLeapSyncBusyTag('Utility'), async () => {
                                                                                const ok = await runGoodLeapLoanStageSync(
                                                                                    'Utility',
                                                                                    opportunityId as string | number,
                                                                                    appIdRf,
                                                                                    financePartnerRf,
                                                                                    pushAgreementNotice,
                                                                                    r.quoteId,
                                                                                    syncHooksRf,
                                                                                );
                                                                                if (ok) await load(); 
                                                                            })
                                                                        }
                                                                    >
                                                                        Upload Utility
                                                                    </Button>
                                                                ) : null}
                                                                {showSDRf ? (
                                                                    <Button
                                                                        type="primary"
                                                                        size="small"
                                                                        loading={isSyncBusyRf('System Design')}
                                                                        onClick={() =>
                                                                            void wrapRf(goodLeapSyncBusyTag('System Design'), async () => {
                                                                                const ok = await runGoodLeapLoanStageSync(
                                                                                    'System Design',
                                                                                    opportunityId as string | number,
                                                                                    appIdRf,
                                                                                    financePartnerRf,
                                                                                    pushAgreementNotice,
                                                                                    r.quoteId,
                                                                                    syncHooksRf,
                                                                                );
                                                                                if (ok) await load(); 
                                                                            })
                                                                        }
                                                                    >
                                                                        Upload System Design
                                                                    </Button>
                                                                ) : null}
                                                                {showDocRf ? (
                                                                    <Button
                                                                        type="primary"
                                                                        size="small"
                                                                        loading={isSyncBusyRf('Document Sent')}
                                                                        onClick={() =>
                                                                            void wrapRf(goodLeapSyncBusyTag('Document Sent'), async () => {
                                                                                const ok = await runGoodLeapLoanStageSync(
                                                                                    'Document Sent',
                                                                                    opportunityId as string | number,
                                                                                    appIdRf,
                                                                                    financePartnerRf,
                                                                                    pushAgreementNotice,
                                                                                    r.quoteId,
                                                                                    syncHooksRf,
                                                                                );
                                                                                if (ok) await load(); 
                                                                            })
                                                                        }
                                                                    >
                                                                        Send Documents
                                                                    </Button>
                                                                ) : null}
                                                            </Space>
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <div className="obd-agreements-modal__detail-kv-flow">
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Finance Partner</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.financePartner)}</span>
                                    </div>
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Roofing Contract Amount</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.totalEpcDisplay)}</span>
                                    </div>
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Roof Manufacturer</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.roofManufacturerDisplay)}</span>
                                    </div>
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Roof Product</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.roofProductDisplay)}</span>
                                    </div>
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Pricing Tier</span>
                                        <span className="obd-agreements-modal__detail-v">{pricingTierAngularDisplay(r)}</span>
                                    </div>
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Selected Product</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.productTypeCode || r.productType)}</span>
                                    </div>
                                    {showLoanAmounts ? (
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Deposit Amount</span>
                                            <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.depositAmountDisplay)}</span>
                                        </div>
                                    ) : null}
                                    {showLoanAmounts ? (
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Financed Amount</span>
                                            <span className="obd-agreements-modal__detail-v">{dash(r.financedAmountDisplay)}</span>
                                        </div>
                                    ) : null}
                                    {showQuoteExpiresIn ? (
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Quote Expires In</span>
                                            <span className="obd-agreements-modal__detail-v">{dash(r.quoteExpiresInDisplay)}</span>
                                        </div>
                                    ) : null}
                                    <AgreementEstimateSourceKv hoverEstimateID={r.hoverEstimateID} />
                                    <ContractSignedDetailRow
                                        ppaProductStatus={ppaProductStatus}
                                        setSectionLoading={setLoading}
                                        variant="gridCell"
                                        row={r}
                                        opportunityId={opportunityId}
                                        opportunityDetails={opportunityDetails}
                                        onUpdated={load}
                                        onConfirm={showConfirmDialog}
                                        ppaSignedCapabilityView={caps.enablePpaSignedView} pushNotice={pushAgreementNotice}
                                    />
                                </div>
                            );
                        })()
                    ) : (
                        detectAgreementPartnerKind(r) === 'goodLeap' || detectAgreementPartnerKind(r) === 'enfin' ? (
                            (() => {
                                const kindGl = detectAgreementPartnerKind(r);
                                if (!isGlOrEnfinPartner(kindGl)) return null;
                                const loanSyncContext = { quoteId: r.quoteIdRaw, partnerKind: kindGl };
                                const hasLoanStatus = Object.keys(readLoanStatusRecord(r.loanStatus)).length > 0;
                                const appId = loanApplicationId(r);
                                const ref = loanReferenceNumber(r);
                                const status = loanStatusText(r);
                                const submits = loanSubmitStatusList(r);
                                const loanStatusGl = readLoanStatusRecord(r.loanStatus);
                                const financePartnerGl = r.financePartnerRaw || r.financePartner;
                                const resyncErrGl = loanResyncErrorsByRowKey[r.key];
                                const showSyncErrorsBtnGl = loanSyncHasAnyErrors(
                                    loanStatusGl,
                                    financePartnerGl,
                                    resyncErrGl,
                                );
                                const syncHooksGl = makeGoodLeapSyncHooks(r.key);
                                const canCancel = displayCancelApplicationButton(getCapabilityValue, r);
                                const hideUpload = hideUploadAndResync(r);
                                const showU = !hideUpload && shouldShowUpload(r, 'Utility');
                                const showSD = !hideUpload && shouldShowUpload(r, 'System Design');
                                const showDoc = !hideUpload && shouldShowUpload(r, 'Document Sent');

                                const { wrap, isBusy: isSyncBusyGl } = createLoanSyncBusyHandlers(
                                    r.key,
                                    actionBusyKey,
                                    setActionBusyKey,
                                );

                                /** Trinity `detail-cell-renderer`: `ProductType.includes('B')` → battery price, adder, manufacturer, model, count (not Sunnova-only for adder). */
                                const showBatteryDetailFieldsGl = pt.includes('B');
                                const items: React.ReactNode[] = [
                                    <div key="fp" className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Finance Partner</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.financePartner)}</span>
                                    </div>,
                                    <div key="epc" className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">{epcLabel}</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.totalEpcDisplay)}</span>
                                    </div>,
                                    showSystemSize ? (
                                        <div key="sys" className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">{systemSizeLabel}</span>
                                            <span className="obd-agreements-modal__detail-v">
                                                {formatSystemSizeKwDisplay(r.systemSizeRaw)}
                                            </span>
                                        </div>
                                    ) : null,
                                    showAnnualProduction ? (
                                        <div key="annual" className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Annual Production</span>
                                            <span className="obd-agreements-modal__detail-v">
                                                {formatAnnualKwhDisplay(r.annualProductionRaw)}
                                            </span>
                                        </div>
                                    ) : null,
                                    showRrrRoofDetailFields ? (
                                        <React.Fragment key="rr-roof">
                                            <AgreementRoofProductExpandFields row={r} />
                                        </React.Fragment>
                                    ) : null,
                                    showBatteryDetailFieldsGl ? (
                                        <React.Fragment key="battery-gl">
                                            <div className="obd-agreements-modal__detail-kv">
                                                <span className="obd-agreements-modal__detail-k">Battery Price Subtotal</span>
                                                <span className="obd-agreements-modal__detail-v">
                                                    {formatMoneyZero(r.batteryPriceSubTotalRaw)}
                                                </span>
                                            </div>
                                            {kindGl !== 'sunnova' ? (
                                                <div className="obd-agreements-modal__detail-kv">
                                                    <span className="obd-agreements-modal__detail-k">Adder Price Subtotal(Net)</span>
                                                    <span className="obd-agreements-modal__detail-v">
                                                        {formatMoneyZero(r.batteryAdderSubTotalRaw)}
                                                    </span>
                                                </div>
                                            ) : null}
                                            <div className="obd-agreements-modal__detail-kv">
                                                <span className="obd-agreements-modal__detail-k">Battery Manufacturer</span>
                                                <span className="obd-agreements-modal__detail-v">
                                                    {dash(r.batteryManufacturerNameRaw)}
                                                </span>
                                            </div>
                                            <div className="obd-agreements-modal__detail-kv">
                                                <span className="obd-agreements-modal__detail-k">Battery Model</span>
                                                <span className="obd-agreements-modal__detail-v">{dash(r.batteryModelNameRaw)}</span>
                                            </div>
                                            <div className="obd-agreements-modal__detail-kv">
                                                <span className="obd-agreements-modal__detail-k">Battery Count</span>
                                                <span className="obd-agreements-modal__detail-v">{dash(r.batteryQuantityRaw)}</span>
                                            </div>
                                        </React.Fragment>
                                    ) : null,
                                    showGrossPpw ? (
                                        <div key="ppw" className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Gross PPW</span>
                                            <span className="obd-agreements-modal__detail-v">{dash(r.desiredPpwNetDisplay)}</span>
                                        </div>
                                    ) : null,
                                    <div key="prod" className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Selected Product</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.productTypeCode || r.productType)}</span>
                                    </div>,
                                    hasLoanStatus ? (
                                        <GoodLeapLoanNumberExpand
                                            key="loan-number"
                                            row={r}
                                            opportunityId={opportunityId}
                                            onRefresh={load}
                                            enableGetCreditStatusView={caps.goodLeapGetCreditStatusView}
                                            pushNotice={pushAgreementNotice}
                                        />
                                    ) : null,
                                    ref ? (
                                        <div key="ref" className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Reference Number</span>
                                            <span className="obd-agreements-modal__detail-v">{dash(ref)}</span>
                                        </div>
                                    ) : null,
                                    showLoanAmounts ? (
                                        <div key="dep-gl" className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Deposit Amount</span>
                                            <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.depositAmountDisplay)}</span>
                                        </div>
                                    ) : null,
                                    showLoanAmounts ? (
                                        <div key="fin-gl" className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Financed Amount</span>
                                            <span className="obd-agreements-modal__detail-v">{dash(r.financedAmountDisplay)}</span>
                                        </div>
                                    ) : null,
                                    (() => {
                                        if (!hasLoanStatus) return null;
                                        const showInPerson = showGoodLeapAppButtons && canOpenGoodLeapInPerson(r, opportunityId);
                                        const showSendEmail =
                                            caps.goodLeapSendCreditEmailView &&
                                            showGoodLeapAppButtons &&
                                            !!opportunityId &&
                                            String(opportunityId).trim() !== '' &&
                                            !!r.contractIdRaw;
                                        const showAny = showInPerson || showSendEmail || canCancel;
                                        if (!showAny) return null;
                                        return (
                                            <div
                                                key="app"
                                                className="obd-agreements-modal__detail-kv obd-agreements-modal__detail-kv--expand-nested"
                                            >
                                                <span className="obd-agreements-modal__detail-k">Application</span>
                                                <span className="obd-agreements-modal__detail-v">
                                                    <Space wrap size="small">
                                                        {showInPerson ? (
                                                            <Button
                                                                type="primary"
                                                                size="small"
                                                                onClick={() => {
                                                                    if (kindGl === 'enfin') {
                                                                        const url = pickGoodLeapApplicationUrl(r.loanStatus);
                                                                        if (url) openEnfinApplicationIframe(url);
                                                                    } else {
                                                                        void openGoodLeapSubmitApplication(r);
                                                                    }
                                                                }}
                                                            >
                                                                In-person
                                                            </Button>
                                                        ) : null}
                                                        {showSendEmail ? (
                                                            <Button
                                                                type="primary"
                                                                size="small"
                                                                onClick={() => void resendGoodLeapApplicationEmail(r)}
                                                            >
                                                                Send email link
                                                            </Button>
                                                        ) : null}
                                                        {canCancel ? (
                                                            <Button
                                                                type="primary"
                                                                danger
                                                                size="small"
                                                                className="obd-agreements-modal__cancel-app-btn"
                                                                onClick={() => {
                                                                    if (kindGl === 'enfin') {
                                                                        if (appId) openEnfinCancelModal(r, appId);
                                                                        return;
                                                                    }
                                                                    showConfirmDialog({
                                                                        title: '',
                                                                        content: 'Are you sure you want to cancel this application?',
                                                                        okText: 'Yes',
                                                                        cancelText: 'No',
                                                                        onOk: async () => {
                                                                            if (!opportunityId || !appId) return;
                                                                            await contractService.goodLeapCancelLoanApplication(opportunityId as any, appId);
                                                                            await load();
                                                                        },
                                                                    });
                                                                }}
                                                            >
                                                                Cancel Application
                                                            </Button>
                                                        ) : null}
                                                    </Space>
                                                </span>
                                            </div>
                                        );
                                    })(),

                                    <ContractSignedDetailRow
                                        ppaProductStatus={ppaProductStatus}
                                        setSectionLoading={setLoading}
                                        key="ppa-signed"
                                        variant="gridCell"
                                        row={r}
                                        opportunityId={opportunityId}
                                        opportunityDetails={opportunityDetails}
                                        onUpdated={load}
                                        onConfirm={showConfirmDialog}
                                        ppaSignedCapabilityView={caps.enablePpaSignedView} pushNotice={pushAgreementNotice}
                                    />,
                                    hasLoanStatus && (status || submits.length) ? (
                                        <div
                                            key="loan-status"
                                            className={`obd-agreements-modal__detail-kv obd-agreements-modal__detail-kv--expand-nested${
                                                showLoanAmounts
                                                    ? ' obd-agreements-modal__detail-kv--goodleap-loan-status-full-row'
                                                    : ' obd-agreements-modal__detail-kv--goodleap-loan-status-after-ppa'
                                            }`}
                                        >
                                            <span className="obd-agreements-modal__detail-k">Loan Status</span>
                                            <span className="obd-agreements-modal__detail-v">
                                                {status ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <span>{status}</span>
                                                        {showSyncErrorsBtnGl ? (
                                                            <Button
                                                                size="small"
                                                                type="primary"
                                                                className="obd-agreements-modal__gl-timeline-btn"
                                                                onClick={() => openLoanSyncErrorsDialog(r)}
                                                            >
                                                                View errors
                                                            </Button>
                                                        ) : null}
                                                    </div>
                                                ) : null}
                                                {submits.length ? (
                                                    <ul className="obd-agreements-modal__loan-status-bullets">
                                                        {submits.map((s, i) => (
                                                            <li key={`${i}-${s}`}>
                                                                <span style={{ marginRight: 8 }}>{s}</span>
                                                                {i === 0 && appId && kindGl === 'goodLeap' && caps.goodLeapTimelineView ? (
                                                                    <Button
                                                                        size="small"
                                                                        type="primary"
                                                                        className="obd-agreements-modal__gl-timeline-btn"
                                                                        loading={actionBusyKey === `${r.key}:gl-timeline`}
                                                                        onClick={() =>
                                                                            void wrap('gl-timeline', async () => {
                                                                                const res = await contractService.goodLeapGetTimeline(appId);
                                                                                setGoodLeapTimelineDialog({
                                                                                    response: res,
                                                                                    applicationId: String(appId),
                                                                                    applicationLink: sunlightApplicationLink(r),
                                                                                });
                                                                            })
                                                                        }
                                                                    >
                                                                        Good Leap Timeline
                                                                    </Button>
                                                                ) : null}
                                                                {renderGoodLeapSubmitLineSyncIcons({
                                                                    statusText: s,
                                                                    row: r,
                                                                    appId,
                                                                    hideUpload,
                                                                    actionBusyKey,
                                                                    opportunityId,
                                                                    wrap,
                                                                    load,
                                                                    pushNotice: pushAgreementNotice,
                                                                    syncHooks: syncHooksGl,
                                                                })}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : null}
                                            </span>
                                        </div>
                                    ) : null,
                                ].filter(Boolean);
                                
                                return (
                                    <div className="obd-agreements-modal__detail-kv-flow">
                                        {items}
                                        {hasLoanStatus && goodLeapLoanIsActive(r) && appId && (showU || showSD || showDoc) ? (
                                            <div className="obd-agreements-modal__detail-kv-flow-span">
                                                <div className="obd-agreements-modal__detail-kv">
                                                    <span className="obd-agreements-modal__detail-k">Upload</span>
                                                    <span className="obd-agreements-modal__detail-v">
                                                        <Space wrap size="small">
                                                            {showU ? (
                                                                <Button
                                                                    type="primary"
                                                                    size="small"
                                                                    loading={isSyncBusyGl('Utility')}
                                                                    onClick={() =>
                                                                        void wrap(goodLeapSyncBusyTag('Utility'), async () => {
                                                                            const ok = await runGoodLeapLoanStageSync(
                                                                                'Utility',
                                                                                opportunityId as string | number,
                                                                                appId,
                                                                                financePartnerGl,
                                                                        pushAgreementNotice,
                                                                        r.quoteId,
                                                                        syncHooksGl,
                                                                    );
                                                                            if (ok) await load(); 
                                                                        })
                                                                    }
                                                                >
                                                                    Upload Utility
                                                                </Button>
                                                            ) : null}
                                                            {showSD ? (
                                                                <Button
                                                                    type="primary"
                                                                    size="small"
                                                                    loading={isSyncBusyGl('System Design')}
                                                                    onClick={() =>
                                                                        void wrap(goodLeapSyncBusyTag('System Design'), async () => {
                                                                            const ok = await runGoodLeapLoanStageSync(
                                                                                'System Design',
                                                                                opportunityId as string | number,
                                                                                appId,
                                                                                financePartnerGl,
                                                                        pushAgreementNotice,
                                                                        r.quoteId,
                                                                        syncHooksGl,
                                                                    );
                                                                            if (ok) await load();
                                                                        })
                                                                    }
                                                                >
                                                                    Upload System Design
                                                                </Button>
                                                            ) : null}
                                                            {showDoc ? (
                                                                <Button
                                                                    type="primary"
                                                                    size="small"
                                                                    loading={isSyncBusyGl('Document Sent')}
                                                                    onClick={() =>
                                                                        void wrap(goodLeapSyncBusyTag('Document Sent'), async () => {
                                                                            const ok = await runGoodLeapLoanStageSync(
                                                                                'Document Sent',
                                                                                opportunityId as string | number,
                                                                                appId,
                                                                                financePartnerGl,
                                                                        pushAgreementNotice,
                                                                        r.quoteId,
                                                                        syncHooksGl,
                                                                    );
                                                                            if (ok) await load();
                                                                        })
                                                                    }
                                                                >
                                                                    Send Documents
                                                                </Button>
                                                            ) : null}
                                                        </Space>
                                                    </span>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })()
                        ) : (
                        <div className="obd-agreements-modal__detail-kv-flow">
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Finance Partner</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.financePartner)}</span>
                                </div>
                                {isSunnova ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Terms &amp; Apr / Escalator</span>
                                        <span className="obd-agreements-modal__detail-v">{termAprDisplay(r)}</span>
                                    </div>
                                ) : null}
                                {isSunnova && r.systemIdRaw ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">
                                            {pt !== 'BO' ? 'Pricing Value' : 'Battery Contract Amount'}
                                        </span>
                                        <span className="obd-agreements-modal__detail-v">{pricingValueAngularDisplay(r)}</span>
                                    </div>
                                ) : null}
                                {isSunnova && r.systemIdRaw ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Contract Validation</span>
                                        <span className="obd-agreements-modal__detail-v">—</span>
                                    </div>
                                ) : null}
                                {isSunnova && r.systemIdRaw ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Recurring Payment Confirmation</span>
                                        <span className="obd-agreements-modal__detail-v">—</span>
                                    </div>
                                ) : null}
                                {showGrossPpw ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Gross PPW</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.desiredPpwNetDisplay)}</span>
                                    </div>
                                ) : null}
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">{epcLabel}</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.totalEpcDisplay)}</span>
                                </div>
                                {showNonEc ? (
                                    <>
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Non Energy Community - EPC</span>
                                            <span className="obd-agreements-modal__detail-v">{nonEcEpcDisplay}</span>
                                        </div>
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Non Energy Community - PPW</span>
                                            <span className="obd-agreements-modal__detail-v">{nonEcPpwDisplay}</span>
                                        </div>
                                    </>
                                ) : null}
                                {isSunnova && financeMethodRaw !== 'Prepaid PPA-EZ' ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Monthly Sunnova Payment</span>
                                        <span className="obd-agreements-modal__detail-v">{monthlyPaymentDisplay}</span>
                                    </div>
                                ) : null}
                                {isSunnova && financeMethodRaw === 'Prepaid PPA-EZ' ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Advance Payment Amount</span>
                                        <span className="obd-agreements-modal__detail-v">{monthlyPaymentDisplay}</span>
                                    </div>
                                ) : null}
                                {showSystemSize ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">{systemSizeLabel}</span>
                                        <span className="obd-agreements-modal__detail-v">{formatSystemSizeKwDisplay(r.systemSizeRaw)}</span>
                                    </div>
                                ) : null}
                                {kind === 'lightreach' && r.financePartnerContractIdRaw ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Contract ID</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.financePartnerContractIdRaw)}</span>
                                    </div>
                                ) : null}
                                <div className="obd-agreements-modal__detail-kv">
                                    <span className="obd-agreements-modal__detail-k">Selected Product</span>
                                    <span className="obd-agreements-modal__detail-v">{dash(r.productTypeCode || r.productType)}</span>
                                </div>
                                {isIgs &&
                                caps.igsCancelContractView &&
                                String(r.contractStatus || '').toLowerCase().includes('signed') ? (
                                    <div className="obd-agreements-modal__detail-kv obd-agreements-modal__detail-kv--expand-nested">
                                        <span className="obd-agreements-modal__detail-k">Application</span>
                                        <span className="obd-agreements-modal__detail-v">
                                            <Button
                                                type="primary"
                                                danger
                                                size="small"
                                                className="obd-agreements-modal__cancel-app-btn"
                                                onClick={() =>
                                                    showConfirmDialog({
                                                        title: '',
                                                        content:
                                                            'You have requested the current IGS Solar Contract to be Cancelled. Click Yes to Continue or Cancel to Cancel the request.',
                                                        okText: 'Yes',
                                                        cancelText: 'Cancel',
                                                        onOk: async () => {
                                                            const lead = r.leadIdRaw;
                                                            const cid = String(
                                                                r.contractIdRaw ?? r.opportunityContractIdRaw ?? '',
                                                            ).trim();
                                                            if (!lead || !cid) {
                                                                pushAgreementNotice(
                                                                    'error',
                                                                    'Missing lead id or contract id.',
                                                                );
                                                                return;
                                                            }
                                                            try {
                                                                const out = (await contractService.cancelIGSApplication(
                                                                    lead,
                                                                    cid,
                                                                )) as {
                                                                    IsSuccessful?: boolean;
                                                                    Message?: string;
                                                                };
                                                                const ok = out?.IsSuccessful !== false;
                                                                pushAgreementNotice(
                                                                    ok ? 'success' : 'error',
                                                                    String(out?.Message ?? '') ||
                                                                        (ok ? 'Request completed.' : 'Cancel request failed.'),
                                                                );
                                                                await load();
                                                            } catch (e: unknown) {
                                                                pushAgreementNotice(
                                                                    'error',
                                                                    contractApiErrorMessage(e) ?? 'Request failed.',
                                                                );
                                                            }
                                                        },
                                                    })
                                                }
                                            >
                                                Cancel Application
                                            </Button>
                                        </span>
                                    </div>
                                ) : null}
                                {showDownPayment ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Down Payment</span>
                                        <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.downPaymentRaw)}</span>
                                    </div>
                                ) : null}
                                {isBatteryish ? (
                                    <>
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Battery Price Subtotal</span>
                                            <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.batteryPriceSubTotalRaw)}</span>
                                        </div>
                                        {kind !== 'sunnova' ? (
                                            <div className="obd-agreements-modal__detail-kv">
                                                <span className="obd-agreements-modal__detail-k">Adder Price Subtotal(Net)</span>
                                                <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.batteryAdderSubTotalRaw)}</span>
                                            </div>
                                        ) : null}
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Battery Manufacturer</span>
                                            <span className="obd-agreements-modal__detail-v">{dash(r.batteryManufacturerNameRaw)}</span>
                                        </div>
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Battery Model</span>
                                            <span className="obd-agreements-modal__detail-v">{dash(r.batteryModelNameRaw)}</span>
                                        </div>
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Battery Count</span>
                                            <span className="obd-agreements-modal__detail-v">{dash(r.batteryQuantityRaw)}</span>
                                        </div>
                                    </>
                                ) : null}
                                {pt.includes('S') && isSunnova ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Estimated Production</span>
                                        <span className="obd-agreements-modal__detail-v">{estimatedProductionDisplay}</span>
                                    </div>
                                ) : null}
                                {pt !== 'RO' && isSunnova ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Year 1 Savings</span>
                                        <span className="obd-agreements-modal__detail-v">{year1SavingsDisplay}</span>
                                    </div>
                                ) : null}
                                {showLoanAmounts ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Deposit Amount</span>
                                        <span className="obd-agreements-modal__detail-v">{formatMoneyZero(r.depositAmountDisplay)}</span>
                                    </div>
                                ) : null}
                                {showAnnualProduction ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Annual Production</span>
                                        <span className="obd-agreements-modal__detail-v">{formatAnnualKwhDisplay(r.annualProductionRaw)}</span>
                                    </div>
                                ) : null}
                                {showRrrRoofDetailFields ? <AgreementRoofProductExpandFields row={r} /> : null}
                                {showQuoteExpiresIn ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Quote Expires In</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.quoteExpiresInDisplay)}</span>
                                    </div>
                                ) : null}
                                {showLoanAmounts ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Financed Amount</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(r.financedAmountDisplay)}</span>
                                    </div>
                                ) : null}
                                {isServiceFinance && (pt.includes('S') || pt.includes('R')) ? (
                                    <>
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Term and APR</span>
                                            <span className="obd-agreements-modal__detail-v">{termAprDisplay(r)}</span>
                                        </div>
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Total EPC (Net)</span>
                                            <span className="obd-agreements-modal__detail-v">{dash(r.totalEpcNetDisplay)}</span>
                                        </div>
                                    </>
                                ) : null}
                                {kind}
                                {kind === 'cash' ? (
                                    
                                    <ContractSignedDetailRow
                                        ppaProductStatus={ppaProductStatus}
                                        setSectionLoading={setLoading}
                                        variant="gridCell"
                                        row={r}
                                        opportunityId={opportunityId}
                                        opportunityDetails={opportunityDetails}
                                        onUpdated={load}
                                        onConfirm={showConfirmDialog}
                                        ppaSignedCapabilityView={caps.enablePpaSignedView}
                                        pushNotice={pushAgreementNotice}
                                    />
                                ) : null}
                        </div>
                        )
                    )
                        );
                    })()}
                    {(() => {
                        const kind = detectAgreementPartnerKind(r);
                        if (kind !== 'goodLeap' && kind !== 'sunlight' && kind !== 'lightreach') return null;
                        // GoodLeap details (loan #, ref, application, loan status, upload) are rendered above in row-major order.
                        if (kind === 'goodLeap') return null;
                        const hasLoanStatus = Object.keys(readLoanStatusRecord(r.loanStatus)).length > 0;
                        if ((kind === 'goodLeap' || kind === 'sunlight') && !hasLoanStatus) return null;
                        if (goodLeapRoofLoanMerged) return null;
                        const appId = loanApplicationId(r);
                        const ref = loanReferenceNumber(r);
                        const status = loanStatusText(r);
                        const submits = loanSubmitStatusList(r);
                        const projectId = kind === 'sunlight' ? sunlightProjectId(r) : null;
                        const credit = kind === 'sunlight' ? sunlightLoanCreditStatus(r) : null;
                        const loanStatusPartner = readLoanStatusRecord(r.loanStatus);
                        const financePartnerPartner = r.financePartnerRaw || r.financePartner;
                        const resyncErrPartner = loanResyncErrorsByRowKey[r.key];
                        const showSyncErrorsBtnPartner = loanSyncHasAnyErrors(
                            loanStatusPartner,
                            financePartnerPartner,
                            resyncErrPartner,
                        );
                        const syncHooksPartner = makeGoodLeapSyncHooks(r.key);
                        const { wrap: wrapPartner, isBusy: isSyncBusyPartner } = createLoanSyncBusyHandlers(
                            r.key,
                            actionBusyKey,
                            setActionBusyKey,
                        );
                        const hideUpload = hideUploadAndResync(r);
                        const showU = !hideUpload && shouldShowUpload(r, 'Utility');
                        const showSD = !hideUpload && shouldShowUpload(r, 'System Design');
                        const showDoc = !hideUpload && shouldShowUpload(r, 'Document Sent');
                        return (
                            <div className="obd-agreements-modal__detail-kv-flow" style={{ marginTop: 10 }}>
                                {kind === 'goodLeap' ? (
                                    <div className="obd-agreements-modal__detail-kv-flow-span">
                                        <GoodLeapLoanNumberExpand
                                            row={r}
                                            opportunityId={opportunityId}
                                            onRefresh={load}
                                            enableGetCreditStatusView={caps.goodLeapGetCreditStatusView}
                                            pushNotice={pushAgreementNotice}
                                        />
                                    </div>
                                ) : null}
                                {kind === 'goodLeap' && ref ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Reference Number</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(ref)}</span>
                                    </div>
                                ) : null}
                                {kind === 'sunlight' && ref ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Hash ID</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(ref)}</span>
                                    </div>
                                ) : null}
                                {kind === 'sunlight' && projectId ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Project ID</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(projectId)}</span>
                                    </div>
                                ) : null}
                                {kind === 'sunlight' && credit ? (
                                    <div className="obd-agreements-modal__detail-kv">
                                        <span className="obd-agreements-modal__detail-k">Credit Status</span>
                                        <span className="obd-agreements-modal__detail-v">{dash(credit)}</span>
                                    </div>
                                ) : null}
                                {(kind === 'goodLeap' || kind === 'sunlight') && appId && (showU || showSD || showDoc) ? (
                                    <div className="obd-agreements-modal__detail-kv-flow-span">
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Upload</span>
                                            <span className="obd-agreements-modal__detail-v">
                                                <Space wrap size="small">
                                                    {showU ? (
                                                        <Button
                                                            type="primary"
                                                            size="small"
                                                            loading={isSyncBusyPartner('Utility')}
                                                            onClick={() =>
                                                                void wrapPartner(goodLeapSyncBusyTag('Utility'), async () => {
                                                                    const ok = await runGoodLeapLoanStageSync(
                                                                        'Utility',
                                                                        opportunityId as string | number,
                                                                        appId,
                                                                        financePartnerPartner,
                                                                        pushAgreementNotice,
                                                                        r?.quoteId,
                                                                    );
                                                                    if (ok) await load();
                                                                })
                                                            }
                                                        >
                                                            Upload Utility
                                                        </Button>
                                                    ) : null}
                                                    {showSD ? (
                                                        <Button
                                                            type="primary"
                                                            size="small"
                                                            loading={isSyncBusyPartner('System Design')}
                                                            onClick={() =>
                                                                void wrapPartner(goodLeapSyncBusyTag('System Design'), async () => {
                                                                    const ok = await runGoodLeapLoanStageSync(
                                                                        'System Design',
                                                                        opportunityId as string | number,
                                                                        appId,
                                                                        financePartnerPartner,
                                                                        pushAgreementNotice,
                                                                        r?.quoteId,
                                                                    );
                                                                    if (ok) await load();
                                                                })
                                                            }
                                                        >
                                                            Upload System Design
                                                        </Button>
                                                    ) : null}
                                                    {showDoc ? (
                                                        <Button
                                                            type="primary"
                                                            size="small"
                                                            loading={isSyncBusyPartner('Document Sent')}
                                                            onClick={() =>
                                                                void wrapPartner(goodLeapSyncBusyTag('Document Sent'), async () => {
                                                                    if (kind === 'sunlight') {
                                                                        const syncLeadId = sunlightSyncLeadId(r);
                                                                        if (!syncLeadId) {
                                                                            pushAgreementNotice(
                                                                                'error',
                                                                                'Missing sync lead id for Sunlight.',
                                                                            );
                                                                            return;
                                                                        }
                                                                        await contractService.sunlightSendLoanDocument(
                                                                            syncLeadId,
                                                                            'true',
                                                                        );
                                                                        await load();
                                                                        return;
                                                                    }
                                                                    const ok = await runGoodLeapLoanStageSync(
                                                                        'Document Sent',
                                                                        opportunityId as string | number,
                                                                        appId,
                                                                        financePartnerPartner,
                                                                        pushAgreementNotice,
                                                                        r.quoteId,
                                                                    );
                                                                    if (ok) await load();
                                                                })
                                                            }
                                                        >
                                                            Send Documents
                                                        </Button>
                                                    ) : null}
                                                    {kind === 'goodLeap' && caps.goodLeapTimelineView ? (
                                                        <Button
                                                            size="small"
                                                            onClick={async () => {
                                                                const res = await contractService.goodLeapGetTimeline(appId);
                                                                setGoodLeapTimelineDialog({
                                                                    response: res,
                                                                    applicationId: String(appId),
                                                                    applicationLink: sunlightApplicationLink(r),
                                                                });
                                                            }}
                                                        >
                                                            Good Leap Timeline
                                                        </Button>
                                                    ) : null}
                                                </Space>
                                            </span>
                                        </div>
                                    </div>
                                ) : null}
                                <ContractSignedDetailRow
                                    ppaProductStatus={ppaProductStatus}
                                    setSectionLoading={setLoading}
                                    variant="gridCell"
                                    row={r}
                                    opportunityId={opportunityId}
                                    opportunityDetails={opportunityDetails}
                                    onUpdated={load}
                                    onConfirm={showConfirmDialog}
                                    ppaSignedCapabilityView={caps.enablePpaSignedView} pushNotice={pushAgreementNotice}
                                />
                                {status || submits.length ? (
                                    <div className="obd-agreements-modal__detail-kv-flow-span">
                                        <div className="obd-agreements-modal__detail-kv">
                                            <span className="obd-agreements-modal__detail-k">Loan Status</span>
                                            <span className="obd-agreements-modal__detail-v">
                                                {status ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <span>{status}</span>
                                                        {showSyncErrorsBtnPartner ? (
                                                            <Button
                                                                size="small"
                                                                type="primary"
                                                                className="obd-agreements-modal__gl-timeline-btn"
                                                                onClick={() => openLoanSyncErrorsDialog(r)}
                                                            >
                                                                View errors
                                                            </Button>
                                                        ) : null}
                                                    </div>
                                                ) : null}
                                                {submits.length ? (
                                                    <ul className="obd-agreements-modal__loan-status-bullets">
                                                        {submits.map((s, i) => (
                                                            <li key={`${i}-${s}`}>{s}</li>
                                                        ))}
                                                    </ul>
                                                ) : null}
                                            </span>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })()}
                    {(() => {
                        const pk = detectAgreementPartnerKind(r);
                        // Cash: PPA is inside each `detail-kv-flow` (dense grid); other partners below.
                        if (pk === 'goodLeap' || pk === 'sunlight' || pk === 'lightreach' || pk === 'cash') {
                            return null;
                        }
                        return (
                            <ContractSignedDetailRow
                                    ppaProductStatus={ppaProductStatus}
                                    setSectionLoading={setLoading}
                                row={r}
                                opportunityId={opportunityId}
                                opportunityDetails={opportunityDetails}
                                onUpdated={load}
                                onConfirm={showConfirmDialog}
                                ppaSignedCapabilityView={caps.enablePpaSignedView} pushNotice={pushAgreementNotice}
                            />
                        );
                    })()}
                    {/* Application actions are rendered inside the GoodLeap detail block above; avoid duplicate section. */}
                </div>
            );
            },
        }),
        [
            actionBusyKey,
            agreementsCapability,
            getCapabilityValue,
            isIgsEnergyCommunity,
            load,
            openGoodLeapSubmitApplication,
            openEnfinApplicationIframe,
            openEnfinCancelModal,
            opportunityId,
            opportunityDetails,
            resendGoodLeapApplicationEmail,
            showConfirmDialog,
            showInfoDialog,
            openLoanSyncErrorsDialog,
            makeGoodLeapSyncHooks,
            loanResyncErrorsByRowKey,
            pushAgreementNotice,
            ppaProductStatus,
        ],
    );

    let dialogShellWidth: number;
    if (dialogState != null) {
        dialogShellWidth = typeof dialogState.width === 'number' ? dialogState.width : 560;
        lastDialogShellWidthRef.current = dialogShellWidth;
    } else {
        dialogShellWidth = lastDialogShellWidthRef.current;
    }

    const body = (
        <>
            {noticeContextHolder}
            {!opportunityId ? (
                <Alert type="warning" message="No opportunity loaded." showIcon />
            ) : error ? (
                <Alert type="error" message={error} showIcon />
            ) : rows.length === 0 ? (
                <Alert type="info" message="No agreements found for this opportunity." showIcon />
            ) : (
                <>
                    <div className="obd-agreements-modal__partner-grid">
                        {agreementPartnerSections.map((section) => {
                            const partner = section.partner;
                            const gRows = section.rows;
                            const sectionKey = partner || 'unknown';
                            const sectionCollapsed = !!partnerSectionCollapsed[sectionKey];
                            const tablePartnerKind = detectAgreementPartnerKind(
                                gRows[0] ?? {
                                    financePartner: partner || '',
                                    financePartnerRaw: partner || '',
                                },
                            );
                            return (
                            <div key={sectionKey} className="obd-agreements-modal__partner-block">
                                <div className="obd-agreements-modal__partner-title">
                                    <Tag className="obd-agreements-modal__partner-tag obd-agreements-modal__partner-tag--inverse">
                                        {partnerKindAbbrev(
                                            detectAgreementPartnerKind(
                                                gRows[0] ?? {
                                                    financePartner: partner || '',
                                                    financePartnerRaw: partner || '',
                                                },
                                            ),
                                        )}
                                    </Tag>
                                    <span className="obd-agreements-modal__partner-title-text">
                                        {partnerSectionTitle(gRows[0]?.financePartner || partner || '')}
                                    </span>
                                    <button
                                        type="button"
                                        className="obd-agreements-modal__partner-collapse-btn"
                                        aria-expanded={!sectionCollapsed}
                                        aria-label={sectionCollapsed ? 'Expand section' : 'Collapse section'}
                                        onClick={() =>
                                            setPartnerSectionCollapsed((s) => ({
                                                ...s,
                                                [sectionKey]: !sectionCollapsed,
                                            }))
                                        }
                                    >
                                        <CaretDown
                                            size={18}
                                            weight="bold"
                                            className={`obd-agreements-modal__partner-collapse-icon${sectionCollapsed ? ' obd-agreements-modal__partner-collapse-icon--closed' : ''}`}
                                            aria-hidden
                                        />
                                    </button>
                                </div>
                                {sectionCollapsed ? null : (
                                <Table<AgreementRow>
                                    size="small"
                                    rowKey="key"
                                    columns={getAgreementGridColumns(tablePartnerKind, gRows)}
                                    dataSource={gRows}
                                    pagination={false}
                                    scroll={{
                                        x: agreementGridScrollMinX(tablePartnerKind),
                                    }}
                                    className="obd-agreements-modal__table obd-agreements-modal__table--partner obd-agreements-modal__table--agreements-grid"
                                    rowClassName={(r) =>
                                        [
                                            expandedRowKeys.includes(r.key) ? 'obd-agreements-modal__tr--expanded' : '',
                                            r.isReadyToSign ? 'obd-agreements-modal__tr--ready' : '',
                                            r.isLatestContractForProductType ? 'obd-agreements-modal__tr--latest' : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ') || ''
                                    }
                                    expandable={{
                                        ...agreementExpandable,
                                        expandedRowKeys,
                                        onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
                                    }}
                                />
                                )}
                            </div>
                            );
                        })}
                    </div>
                </>
            )}
            <DashboardModal
                open={!!dialogState}
                onClose={closeDialog}
                title={dialogState?.title ?? ''}
                width={dialogShellWidth}
                theme={theme}
                destroyOnClose={false}
                maskClosable={dialogState?.mode !== 'confirm'}
                zIndex={10080}
                footer={
                    dialogState ? (
                        <div className="obd-dashboard-modal__footer-actions">
                            {dialogState.mode === 'confirm' ? (
                                <Button onClick={closeDialog} disabled={dialogBusy}>
                                    {dialogState.cancelText ?? 'No'}
                                </Button>
                            ) : null}
                            <Button type="primary" onClick={() => void runDialogOk()} loading={dialogBusy}>
                                {dialogState.okText ?? 'OK'}
                            </Button>
                        </div>
                    ) : null
                }
            >
                <div>{dialogState?.content}</div>
            </DashboardModal>
            <DashboardModal
                open={!!goodLeapTimelineDialog}
                onClose={closeGoodLeapTimelineDialog}
                title="Good Leap Timeline"
                width={520}
                theme={theme}
                rootClassName="obd-gl-timeline-modal-root"
                destroyOnClose
                zIndex={10085}
                footer={
                    <div className="obd-dashboard-modal__footer-actions">
                        <Button type="primary" onClick={closeGoodLeapTimelineDialog}>
                            OK
                        </Button>
                    </div>
                }
            >
                <div>
                    {goodLeapTimelineDialog ? (
                        <GoodLeapTimelineModalContent
                            response={goodLeapTimelineDialog.response}
                            applicationId={goodLeapTimelineDialog.applicationId}
                            applicationLink={goodLeapTimelineDialog.applicationLink}
                        />
                    ) : null}
                </div>
            </DashboardModal>
            <DashboardModal
                title="Syncing with Salesforce"
                open={!!sfdcBundleUi}
                onClose={() => {
                    if (!sfdcBundleUi?.running) {
                        setSfdcBundleUi(null);
                        setFinanceSelectionRefreshTrigger(Date.now());
                        if (opportunityId) {
                            quoteServices.getSyncStatusList(String(opportunityId)).then(res => {
                                const syncList = res?.FinancePartnerSyncStatuses || (Array.isArray(res) ? res : []);
                                if (Array.isArray(syncList) && syncList.length > 0) {
                                    setFinancePartnerRawSyncStatuses(syncList);
                                }
                                const contractStatuses = res?.ContractCreationStatuses || (Array.isArray(res) ? res : []);
                                setContractCreationStatuses(Array.isArray(contractStatuses) ? contractStatuses : []);
                            }).catch(err => console.error("Failed to refresh sync status", err));
                        }
                    }
                }}
                footer={
                    sfdcBundleUi?.running ? null : (
                        <div className="obd-sync-modal__footer progress-modal-footer">
                            <button
                                type="button"
                                className="obd-sync-modal__btn obd-sync-modal__btn--primary"
                                onClick={() => {
                                    setSfdcBundleUi(null);
                                    setFinanceSelectionRefreshTrigger(Date.now());
                                    if (opportunityId) {
                                        quoteServices.getSyncStatusList(String(opportunityId)).then(res => {
                                            const syncList = res?.FinancePartnerSyncStatuses || (Array.isArray(res) ? res : []);
                                            if (Array.isArray(syncList) && syncList.length > 0) {
                                                setFinancePartnerRawSyncStatuses(syncList);
                                            }
                                            const contractStatuses = res?.ContractCreationStatuses || (Array.isArray(res) ? res : []);
                                            setContractCreationStatuses(Array.isArray(contractStatuses) ? contractStatuses : []);
                                        }).catch(err => console.error("Failed to refresh sync status", err));
                                    }
                                }}
                            >
                                Close
                            </button>
                        </div>
                    )
                }
                maskClosable={!sfdcBundleUi?.running}
                destroyOnClose
                width={600}
                theme={theme}
                zIndex={10070}
                className={`obd-sync-modal obd-agreements-modal obd-agreements-modal__sfdc-bundle-modal obd-theme-${theme}`}
                rootClassName={`obd-sync-modal-root obd-agreements-modal-root obd-theme-${theme}`}
            >
                {sfdcBundleUi ? (
                    <div className="obd-agreements-modal__sfdc-bundle-inner">
                        <div className="sync-modal-content__scrollable">
                            <div className="sync-modal-steps__container" aria-label="Salesforce sync steps">
                                {sfdcBundleUi.steps.map((s, index) => (
                                    <div
                                        key={s.id}
                                        className={`sync-modal-step__item sync-modal-step--${sfdcBundleStepProgressClass(s.status)}`}
                                    >
                                        <div className="sync-modal-step__visual">
                                            <div className="sync-modal-step__icon-wrapper">
                                                {getSfdcBundleStatusIcon(s.status)}
                                            </div>
                                            {index !== sfdcBundleUi.steps.length - 1 ? (
                                                <div
                                                    className={`sync-modal-step__line obd-agreements-modal__sfdc-step-line--${sfdcBundleStepProgressClass(s.status)}`}
                                                />
                                            ) : null}
                                        </div>
                                        <div className="sync-modal-step__text">
                                            <div className="sync-modal-step__label">{s.label}</div>
                                            {s.status === 'running' ? (
                                                <div className="sync-modal-step__status-text">Processing...</div>
                                            ) : null}
                                            {s.status === 'done' ? (
                                                <div className="sync-modal-step__status-text sync-modal-text--success">
                                                    Complete
                                                </div>
                                            ) : null}
                                            {s.status === 'error' ? (
                                                <div className="sync-modal-step__status-text sync-modal-text--error">
                                                    Failed
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {!sfdcBundleUi.running && sfdcBundleUi.errorDetails.length > 0 ? (
                            <div className="sync-modal-error__card obd-agreements-modal__sfdc-sync-error-card">
                                <span className="obd-agreements-modal__sfdc-error-details-icon" aria-hidden>
                                    <Warning size={20} weight="fill" />
                                </span>
                                <div className="sync-modal-error__content">
                                    <strong className="obd-agreements-modal__sfdc-error-details-title">Error details</strong>
                                    <div className="obd-agreements-modal__sfdc-api-errors">
                                        {uniqueSfdcErrorObjectNames(sfdcBundleUi.errorDetails).map((objectName) => (
                                            <div key={objectName} className="obd-agreements-modal__sfdc-api-error-group">
                                                <div className="sync-modal-step__label obd-agreements-modal__sfdc-api-error-object">
                                                    <span className="obd-agreements-modal__sfdc-api-error-object-text">
                                                        {objectName}
                                                    </span>
                                                    <span
                                                        className="obd-agreements-modal__sfdc-api-error-object-rule"
                                                        aria-hidden
                                                    />
                                                </div>
                                                {sfdcBundleUi.errorDetails
                                                    .filter((err) => err.objectName === objectName)
                                                    .map((err, idx) => (
                                                        <div
                                                            key={`${objectName}-${idx}-${err.name}`}
                                                            className="sync-modal-step__message obd-agreements-modal__sfdc-api-error-line"
                                                        >
                                                            <span className="obd-agreements-modal__sfdc-api-error-name">
                                                                {err.name}:{' '}
                                                            </span>
                                                            <span className="obd-agreements-modal__sfdc-api-error-msg">
                                                                {err.message ? err.message : 'null'}
                                                            </span>
                                                        </div>
                                                    ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </DashboardModal>
            <ServiceFinanceSubmitModal
                open={!!serviceFinanceModalRow}
                onClose={() => setServiceFinanceModalRow(null)}
                row={
                    serviceFinanceModalRow
                        ? {
                              quoteIdRaw: serviceFinanceModalRow.quoteIdRaw,
                              totalEpcDisplay: serviceFinanceModalRow.totalEpcDisplay,
                              termRaw: serviceFinanceModalRow.termRaw,
                              termAprRaw: serviceFinanceModalRow.termAprRaw,
                              productTypeCode: serviceFinanceModalRow.productTypeCode,
                              productType: serviceFinanceModalRow.productType,
                              financePartnerRaw: serviceFinanceModalRow.financePartnerRaw,
                              financePartner: serviceFinanceModalRow.financePartner,
                              financeMethod:
                                  serviceFinanceModalRow.financeMethod === '—'
                                      ? ''
                                      : serviceFinanceModalRow.financeMethod,
                              financePartnerContractIdRaw: serviceFinanceModalRow.financePartnerContractIdRaw,
                              opportunityContractIdRaw: serviceFinanceModalRow.opportunityContractIdRaw,
                              contractIdRaw: serviceFinanceModalRow.contractIdRaw,
                              primaryContactDisplay: serviceFinanceModalRow.primaryContactDisplay,
                              contractStatus: serviceFinanceModalRow.contractStatus,
                          }
                        : null
                }
                opportunityId={opportunityId}
                opportunityDetails={opportunityDetails}
                salesPersonEmail={salesPersonEmail}
                theme={theme}
                onCompleted={(appId) => {
                    setFinanceSelectionRefreshTrigger(Date.now());
                    const submitted = serviceFinanceModalRow;
                    if (submitted && appId) {
                        setRows((prev) =>
                            prev.map((r) =>
                                r.key === submitted.key ? { ...r, financePartnerContractIdRaw: appId } : r,
                            ),
                        );
                    }
                    if (submitted) {
                        void refreshServiceFinanceContractStatus(submitted);
                    } else {
                        void load();
                    }
                }}
            />
            <DashboardModal
                open={!!sunlightFlow}
                title={
                    sunlightFlow?.mode === 'credit'
                        ? 'Sunlight — loan application delivery'
                        : 'Sunlight — send loan documents'
                }
                onClose={() => setSunlightFlow(null)}
                theme={theme}
                destroyOnClose
                zIndex={10065}
                footer={
                    <button
                        type="button"
                        className="obd-sync-modal__btn obd-sync-modal__btn--primary"
                        onClick={() => void runSunlightDeliveryConfirm()}
                    >
                        Continue
                    </button>
                }
            >
                <p style={{ marginBottom: 12 }}>Choose delivery method (Trinity SelectDeliveryModel).</p>
                <Radio.Group value={sunlightDelivery} onChange={(e) => setSunlightDelivery(e.target.value)}>
                    <Radio value="inPerson">In-person</Radio>
                    <Radio value="email">Email</Radio>
                </Radio.Group>
            </DashboardModal>
            <SunlightIframeModal
                open={sunlightIframeOpen}
                title="Sunlight"
                url={sunlightIframeUrl}
                loadingText="Loading Sunlight…"
                onClose={() => {
                    setSunlightIframeOpen(false);
                    setSunlightIframeUrl(null);
                    void load();
                }}
            />
            <SunlightIframeModal
                open={goodLeapIframeOpen}
                title="Submit Application to Good Leap"
                url={goodLeapIframeUrl}
                loadingText="Loading Good Leap application…"
                matchAgreementsModal
                theme={theme}
                zIndex={10062}
                onClose={() => {
                    setGoodLeapIframeOpen(false);
                    setGoodLeapIframeUrl(null);
                    setFinanceSelectionRefreshTrigger(Date.now());
                    void load();
                }}
            />
            <SunlightIframeModal
                open={enfinIframeOpen}
                title="Submitted Application to Enfin"
                url={enfinIframeUrl}
                loadingText="Loading Enfin application…"
                matchAgreementsModal
                theme={theme}
                zIndex={10062}
                onClose={() => {
                    setEnfinIframeOpen(false);
                    setEnfinIframeUrl(null);
                    setFinanceSelectionRefreshTrigger(Date.now());
                    void load();
                }}
            />
            <DashboardModal
                open={!!enfinCancelModal}
                onClose={() => {
                    if (enfinCancelBusy) return;
                    setEnfinCancelModal(null);
                    setEnfinCancelReason('');
                    setEnfinCancelReasonError(false);
                }}
                title="Cancel Application"
                theme={theme}
                zIndex={10063}
                loading={enfinCancelBusy}
                loadingMessage="Cancelling application..."
                footer={
                    <>
                        <button
                            type="button"
                            className="obd-sync-modal__btn obd-sync-modal__btn--secondary"
                            disabled={enfinCancelBusy}
                            onClick={() => {
                                if (enfinCancelBusy) return;
                                setEnfinCancelModal(null);
                                setEnfinCancelReason('');
                                setEnfinCancelReasonError(false);
                            }}
                        >
                            No
                        </button>
                        <button
                            type="button"
                            className="obd-sync-modal__btn obd-sync-modal__btn--primary"
                            disabled={enfinCancelBusy}
                            aria-busy={enfinCancelBusy}
                            onClick={() => void runEnfinCancelApplication()}
                        >
                            Yes
                        </button>
                    </>
                }
            >
                <p style={{ marginBottom: 12 }}>Select a cancellation reason, then confirm.</p>
                <Select
                    style={{ width: '100%' }}
                    placeholder="Select cancellation reason"
                    value={enfinCancelReason || undefined}
                    options={ENFIN_CANCEL_REASONS.map((x) => ({ label: x.label, value: x.value }))}
                    disabled={enfinCancelBusy}
                    onChange={(value) => {
                        setEnfinCancelReason(String(value ?? ''));
                        setEnfinCancelReasonError(false);
                    }}
                    status={enfinCancelReasonError ? 'error' : undefined}
                />
                {enfinCancelReasonError ? (
                    <p style={{ color: 'var(--obd-danger, #cf1322)', marginTop: 8, marginBottom: 0 }}>
                        Cancellation reason is required.
                    </p>
                ) : null}
            </DashboardModal>
            <CreditCheckModal />
            <GoodLeapShareLoanModal
                open={!!goodLeapShareRow}
                row={goodLeapShareRow}
                opportunityId={opportunityId}
                opportunityDetails={opportunityDetails}
                statesFullName={statesFullName}
                theme={theme}
                onClose={closeGoodLeapShareModal}
                onCompleted={() => {
                    setFinanceSelectionRefreshTrigger(Date.now());
                    void load();
                }}
            />
            <SignFinancePartnerContractModal
                open={!!signFinancePartnerContractRow}
                row={signFinancePartnerContractRow}
                opportunityId={opportunityId}
                opportunityDetails={opportunityDetails}
                theme={theme}
                onClose={() => setSignFinancePartnerContractRow(null)}
                onCompleted={() => {
                    setAgreementsRefreshTrigger(Date.now());
                    void load();
                }}
                onContractSigningIframeClosed={refreshAfterFinancePartnerContractSigningIframe}
            />
            <ViewTrinityContractModal
                open={viewTrinityModalOpen && !!viewTrinityModalRow}
                onClose={closeViewTrinityModal}
                row={viewTrinityModalRow}
                mode={viewTrinityModalMode}
                theme={theme}
                salesforceId={salesforceId}
                opportunityId={opportunityId}
                opportunityDetails={opportunityDetails}
                onAfterAction={async () => {
                    await load();
                    setAgreementsRefreshTrigger(Date.now());
                }}
            />
            {quoteDetailsModal ? (
                <OBDQuoteDetailsModal
                    quoteId={quoteDetailsModal.quoteId}
                    partner={quoteDetailsModal.partner}
                    product={quoteDetailsModal.product}
                    monthly={0}
                    apr={0}
                    hoverEstimateID={quoteDetailsModal.hoverEstimateID}
                    mode="view"
                    onClose={() => setQuoteDetailsModal(null)}
                    opportunityId={opportunityId}
                    salesforceId={salesforceId}
                    opportunityDetails={opportunityDetails}
                    onAfterAction={load}
                />
            ) : null}
        </>
    );

    if (embedded) {
        return (
            <section className={`obd-agreements-embedded obd-agreements-modal obd-theme-${theme}`} aria-label="Agreements">
                <div className="obd-sync-modal__header obd-agreements-embedded__header">
                    <h3 className="obd-sync-modal__title">
                        <Article size={20} weight="duotone" className="obd-agreements-modal__title-icon" aria-hidden />
                        Agreements
                    </h3>
                </div>
                <div className="obd-sync-modal__content-body obd-agreements-modal__body">{body}</div>
            </section>
        );
    }

    return (
        <DashboardModal
            open={open}
            onClose={handleClose}
            footer={null}
            width="min(calc(100vw - 40px), 1920px)"
            title={
                <h3 className="obd-sync-modal__title">
                    <Article size={20} weight="duotone" className="obd-agreements-modal__title-icon" aria-hidden />
                    Agreements
                </h3>
            }
            theme={theme}
            destroyOnClose
            zIndex={10060}
            className={`obd-sync-modal obd-agreements-modal obd-theme-${theme}`}
            rootClassName={`obd-sync-modal-root obd-agreements-modal-root obd-theme-${theme}`}
            loading={loading}
            loadingMessage="Loading agreements..."
        >
            <div className={`obd-theme-${theme}`}>
                <div className="obd-sync-modal__content-body obd-agreements-modal__body">{body}</div>
            </div>
        </DashboardModal>
    );
};

export default AgreementsModal;
